import {
  withLegacyToleranceNames,
  answerToleranceMode, answerHalfWidths, answerSymmetry, bandAdmitsPlace, boundAtSigFigs,
  countSigFigs, lastSignificantPlace,
  tolerancesDiverge,
  UNCERTAINTY_TRIGGER_PREFIX,
  restateLinkFields,
  uncertaintyAsked,
  uncertaintyBand,
  uncertaintyDecimalsFor,
  probeValuesForRecalls,
  angleModeFor, addNumericalVersion, applyNumericalVersion, applyScenarioRow, scenarioInForce,
  markScenarioReviewed, syncScenarioBranches, type ScenarioLeaderVersion, type ScenarioRowTemplate, remapVersionsToScenarios, pickScenarioBranch, drawnScenarioId, resolveNumericalScenario, removeNumericalVersion, versionRollKey, writeNumericalVersion, writeScenarioRow, NUMERICAL_VERSION_KEYS, type DisplayVersion, type FieldVersion,
  targetedFiresIn, visibleTargeted, displayEvaluation,
  resolveTriggerValues,
  triggerFault,
  MATH_ERROR_CATEGORIES,
  collectMathErrorCandidates,
  bandAdmitsSigFigs,
  computeAcceptedRanges as computeAcceptedRangesShared,
  overlappingAnswerPairs,
  computeFormulaExtrema,
  bandFormulas,
  chipNamesIn,
  forEachRoll,
  analyseOverlapAcrossRolls,
  probeRoll,
  OVERLAP_ENUMERATION_MAX_COMBOS,
  evaluateNumerical,
  hasVariableChip,
  isValueDefaultIncorrectUnreachable,
  doesValueFeedbackOverlapAccepted,
  findOverlappingFeedbackPairs,
  isValueFeedbackFullySubsumed,
  valuesMatchWithinEpsilon,
  sanitizeTriggerValue, type ComparisonVerdict,
  unreviewedCount,
  hasRecallChip,
 type GradingMode ,
  type FeedbackState,
  type MathErrorCategory,
  type NumericalAnswer,
  type NumericalBlockData as BlocksNumericalBlockData,
  type ScenarioFieldBranch,
  type NumericalConditionType as ConditionType,
  type NumericalToleranceMode as ToleranceMode,
  type NumericalTriggerType as NumTriggerType,
  type NumericalUnitOption as UnitOption,
  type TriggerFault,
  type OverlapAnalysis, SET_ANSWER_TRIGGER, type DrivenFollower } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle, Check, ChevronDown, ChevronRight, CircleSlash, GripVertical, Hash, Minus, Percent, Plus, Ruler, Trash2, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useRecallFormulaValues, useRerollRecalls, useRolledWithRecalls } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { betweenInversionVerdict, type BlockIssue, findDuplicateGroups, type IssueCache, type IssueContext, issueFieldIds, IssuesPanel, memoizedByKey, triggerFaultMessage, triggerRangeInversionVerdict, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { CollapsedInputBlock, CollapsedBlockBar, collapsedIssueBar, collapsedModeBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { answerBarHasContent, AnswerBarText, activeIntervalIds, ConditionChipRow, joinChips, toTestBarChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipButtonClass, chipClass, ChipEvalIcon, chipToneClass, UnreachableChip } from './eval-chip';
import { FeedbackCard, ChipDivider,  TRIGGER_CHIP_TEXT_CLASS, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { evaluateFormulaRaw, type AngleMode } from './formula-eval';
import { FormulaField, FormulaWithValues, resolvedTriggerChip } from './formula-field';
import { stripHtmlForLabel } from './formula-plaintext';
import { GRADING_MODES } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { fmtFixed, fmtLocale } from './number-format';
import { OptionsSection } from './options-section';
import { RichTextEditor } from './rich-text-editor';
import { ScenarioLinkMenu } from './scenario-link-menu';
import { ScenarioRemapModal } from './scenario-remap-modal';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { ScenarioStimulus } from './scenario-stimulus';
import { TOLERANCE_HINT_CLASS, toleranceHint } from './tolerance-hint';
import { ToleranceRow, toleranceControlShape } from './tolerance-row';
import { TriggerKindSelect, triggerKindOf } from './trigger-kind';
import { UnitField } from './unit-field';
import { acceptedSpellings, getConversionFactor, isOffsetTempUnit, isValidUnit, unitSignature } from './unit-utils';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, normalizeStandardFeedback, type ExtraDefaultState, hasEmptyFeedbackContent, positiveTriggerForMode, retriggerPositiveSlot } from './use-input-block-feedback';
import { useRestoredData } from './use-restored-data';
import { useScenarioRowProjection } from './use-scenario-rows';
import { answerPositionsKey, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { ANSWER_COLUMN_CLASS, BlockQuestion, AnswerDisplayFrame, BlockPreviewProvider, hasRollableVariables, htmlHasVariableChips, PreviewHtml, rollVariableValues } from './variable-chip-preview';
import { VariableTestController } from './variable-test-controller';
import { renameVarInFormula, renameVarInHtml } from './variable-utils';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { useVersionInForce } from './version-in-force';
import { VersionSelector } from './version-selector';

import type { ConditionChip } from './condition-chips';
import type { ChipEvaluation } from './eval-chip';
import type { BlockUpdateOptions } from '@/lib/block-update';
import type { Block, TaskVariable } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import { BetweenAnswerRow } from '@/components/task/between-answer-row';
import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { NumberField } from '@/components/ui/number-field';
import { RadioGroup } from '@/components/ui/radio-group';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { useExtraBlockSetting } from '@/hooks/use-capabilities';
import { catchAllTitle, numericalHasFormatGate,
  computeBarEntries, formulasFromData, numericalCorrectChips, numericalHasAnswerText,
  neutralChips, numericalIncorrectChips, numericalRangeConfig } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { numericalTriggerSummary, numericalUnitTargetedTitle } from '@/lib/block-state-labels';
import { effectiveSetting, settingAvailable } from '@/lib/mode-gated-settings';


/** A tolerance hint, centred under its own field and OUT OF THE FLOW — see the row's `pb-5`.
 *  `w-max` so it never wraps to the field's width and re-grows the column it was taken out of. */
const HINT_POSITION = 'absolute left-1/2 top-full w-max -translate-x-1/2 pt-0.5 text-center';

/** The raw bound values a trigger carries, for `chipifyFormulaValues`. Reads the stored string
 * rather than a parsed number, because the whole point is to find the formula text as authored. */
function triggerValuesOf(trigger: string): string[] {
  const parts = trigger.replace(/^[ab]:/, '').split(':');
  return parts.slice(1);
}



// ─── Types ───────────────────────────────────────────────────────────────────
// ConditionType, ToleranceMode, NumTriggerType, and UnitOption are imported from
// @agavi/blocks above — identical shape to what evaluateNumerical consumes, no
// need to duplicate them locally. ExactAnswer/BetweenAnswer/threshold stay local
// with string values, though: they hold raw formula text before resolution
// ("{x}*5+2"), which is a genuinely different shape from the resolved numeric
// values evaluateNumerical operates on, not an accidental duplication.

interface ExactAnswer {
  id: string;
  value: string;
  toleranceValue: string;   // symmetric tolerance
  toleranceAbove: string;   // asymmetric: above correct value
  toleranceBelow: string;   // asymmetric: below correct value
  /** Both per answer since the tolerance move. Absent on answers authored before it, which fall
   *  back to the block's own pair — always read through `answerSymmetry`/`answerToleranceMode`,
   *  never directly. */
  toleranceSymmetry?: 'symmetric' | 'asymmetric';
  toleranceMode?: ToleranceMode;
  /** Manual Math Error override equation — only meaningful (and only ever shown in the
   * UI) when `value` itself is a bare number rather than an equation. */
  mathErrorEquation: string;
}

interface BetweenAnswer {
  id: string;
  low: string;
  high: string;
  mathErrorEquationLow: string;
  mathErrorEquationHigh: string;
}

/** Exported for the tests that exercise the pure derivations below; the app's own shape, which is
 *  the evaluator's plus the fields only the editor holds (raw formula text, display-only settings). */
export interface NumericalBlockData extends DrivenFollower {
  /** This block's question bank — see `versions.ts`. A version carries the accepted ANSWERS, the
   *  ranges, the error model and `conditionType`; the UNITS and the format gates stay shared,
   *  which is the split §3.2 is most emphatic about. */
  versions?: FieldVersion<{ states: FeedbackState[] }>[];
  /** The display block whose variant decides this block's answer — `docs/linked-scenarios.md`.
   *  Mutually exclusive with `versions` (§2.2): a block answers to ONE selector. */
  followsBlockId?: string;
  /** One answer setup per scenario — §5.1's value-range payload, carrying exactly the key set
   *  a version carries, because the split is a property of the block not of what selects it. */
  scenarioBranches?: ScenarioFieldBranch<{ states: FeedbackState[] }>[];
  question: string;
  conditionType: ConditionType;

  // 'exact' — one or more accepted values (OR logic)
  answers: ExactAnswer[];
  toleranceMode: ToleranceMode;
  toleranceSymmetry: 'symmetric' | 'asymmetric';

  // 'between' — one or more accepted ranges (OR logic)
  betweenAnswers: BetweenAnswer[];

  // 'lt' | 'lte' | 'gt' | 'gte' — single threshold
  threshold: string;

  // Settings (drawer)
  integerOnly: boolean;
  requireExponentialNotation: boolean;
  sigFigsRequired: boolean;
  sigFigsCount: number;

  /**
   * The student reports an UNCERTAINTY beside the value — `9.8 ± 0.3`, both numbers graded.
   *
   * Mutually exclusive with `sigFigsRequired`, and not as a conflict to resolve by precedence: a
   * sig-fig instruction exists only because, absent an uncertainty, nothing else fixes precision.
   * Once the student states one the value's precision is DERIVED, and since the correct uncertainty
   * is a formula over that student's own roll, the precision it implies rolls per student. A fixed
   * count marks a correct answer wrong on some draws. So the control RELOCATES to
   * `uncertaintySigFigs`, which reaches the value through the agreement rule.
   *
   * Also exclusive with `integerOnly`, which forbids the decimal every ordinary uncertainty needs.
   */
  uncertaintyRequired: boolean;
  /** Formula text, like every other answer field here — resolved for the evaluator. */
  uncertaintyValue: string;
  /** The manual Math Error equation for the uncertainty, used where its own field holds a bare
   *  number. The answer's per-answer `mathErrorEquation`, for the other half of the answer. */
  uncertaintyMathErrorEquation?: string;
  /** Tolerance on the uncertainty, applied BEFORE rounding. The same four fields an answer
   *  carries, read through the same `answerHalfWidths` / `answerToleranceMode`. */
  uncertaintyTolerance: string;
  uncertaintyToleranceAbove: string;
  uncertaintyToleranceBelow: string;
  uncertaintyToleranceMode?: ToleranceMode;
  uncertaintyToleranceSymmetry?: 'symmetric' | 'asymmetric';
  /** Figures the UNCERTAINTY carries. 1 is the usual convention, 2 the other defensible answer. */
  uncertaintySigFigs: number;
  mathErrorEnabled: boolean;
  mathErrorCategories: Record<MathErrorCategory, boolean>;

  // Units
  unitMode: 'none' | 'display' | 'withUnits';
  unitLabel: string;         // display label (shown when unitMode === 'display')
  canonicalUnit: string;     // mathjs-recognized unit (when unitMode === 'withUnits')
  /** Every other spelling of the canonical, computed on load. Shown as the "also accepted" line
   *  and accepted by the evaluator, which has no unit parser of its own. */
  canonicalSpellings?: string[];
  unitOptions: UnitOption[];  // derivative units library
  tempConvMode: 'absolute' | 'interval'; // only relevant when canonicalUnit is an offset temp scale
  angleMode: AngleMode;                  // the block's LEGACY mode, now only a seed for fields never set individually
  /** Per-field angle modes, keyed by `fieldId` — what the in-field toggle writes and what every
   *  evaluator reads back through `angleModeFor`. See `project_angle_mode_per_field`. */
  angleModes?: Record<string, AngleMode>;

  /**
   * Show the student how much PRECISION they need, never what the answer is.
   *
   * That distinction is why this is exact-only, and it is a deliberate limit rather than a gap
   * waiting to be filled in. A tolerance discloses precision: "±0.5" says how many digits to carry
   * and reveals nothing about the value. A range IS the answer -- "4.5 to 5.5" on a between answer,
   * or "≤ 5.5" on a threshold, hands the student the thing they were asked for.
   *
   * So the checkbox is disabled for every condition type but `exact`, and the label the teacher
   * reads says "tolerances" rather than "range" for the same reason.
   *
   * The FIELD NAME says otherwise, and so do the `acceptedRange*` locale keys the hint borrows.
   * Those keys are right where they are used elsewhere -- on the TEACHER's own chips, where showing
   * the range is exactly the point. Renaming this field would migrate every saved block for a name
   * only developers see, so the rule is written here instead.
   */
  showAcceptedRange: boolean;
  gradingMode: GradingMode;
}

interface NumericalBlockProps {
  block: Block;
  isPreview: boolean;
  /** Preview's single page-level Submit, as a monotonically increasing signal. Every other input
   * block already takes this; numerical's Preview was a disabled input and a dead button. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>, opts?: BlockUpdateOptions) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

;

/** The bare symbols need a spoken name, since "=" read aloud on its own says nothing about what it
 * means here. Same key names angle and compass use for the identical control. */
const CONDITION_ARIA_KEYS: Record<ConditionType, string> = {
  exact: 'conditionExact', lt: 'conditionLt', lte: 'conditionLte',
  gt: 'conditionGt', gte: 'conditionGte', between: 'conditionBetween',
};

const CONDITION_TYPES: { id: ConditionType; symbol: string }[] = [
  { id: 'exact',   symbol: '='   },
  { id: 'lt',      symbol: '<'   },
  { id: 'lte',     symbol: '≤'   },
  { id: 'gt',      symbol: '>'   },
  { id: 'gte',     symbol: '≥'   },
  { id: 'between', symbol: '↔'   },
];

const ONE_SIDED: ConditionType[] = ['lt', 'lte', 'gt', 'gte'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function numberError(val: string, t: (k: string) => string, opts: { allowNegative?: boolean; integerOnly?: boolean } = {}): string {
  if (!val) return '';
  const num = Number(val);
  if (!Number.isFinite(num)) return t('blocks.numerical.validationNumber');
  if (opts.allowNegative === false && num < 0) return t('blocks.numerical.validationNonNegative');
  if (opts.integerOnly && !Number.isInteger(num)) return t('blocks.numerical.validationInteger');
  return '';
}

function newExactAnswer(): ExactAnswer {
  return { id: crypto.randomUUID(), value: '', toleranceValue: '', toleranceAbove: '', toleranceBelow: '', mathErrorEquation: '' };
}

function newBetweenAnswer(): BetweenAnswer {
  return { id: crypto.randomUUID(), low: '', high: '', mathErrorEquationLow: '', mathErrorEquationHigh: '' };
}



/** True for both {variable} chip formulas and literal-operator equations like
 * "(5-0)*(0+4)"; false only for a bare number like "10". Decides whether Math Error
 * auto-detects a slot's own text as its equation, or needs a manual override input. */
const isEquationShaped = (s: string): boolean => s.trim() !== '' && !Number.isFinite(Number(s.trim()));

/** Joins items with commas and a final connector word (e.g. "1, 2, 3, or 4"). A single item is returned as-is. */


/**
 * Formula text living in a value TRIGGER rather than in the answer.
 *
 * `formulasFromData` reads the answer fields and nothing else, which is right for the accepted band
 * and wrong for the question "does this block have anything to roll". A teacher can put a variable
 * in a targeted feedback condition and nowhere else, and until now that block had no variable
 * testing at all: the panel it needed was gated on a predicate that could not see it.
 *
 * The two filters are belt-and-braces and known to be so — a mutation removing either survives the
 * suite, exactly as `resolveFeedbackTriggers`' own does. `triggerValuesOf` splits on `:` and drops
 * the head, so a trigger with no colon (`unit_<id>`, `incorrect`, `math_error_<cat>`) yields nothing
 * whatever we do. They stay because they say at the point of use which states have values at all.
 */
export function triggerFormulasOf(feedbackStates: FeedbackState[]): string[] {
  return feedbackStates
    .filter(s => s.type === 'targeted' && !s.trigger.startsWith('unit_'))
    .flatMap(s => triggerValuesOf(s.trigger))
    .filter(hasVariableChip);
}

/** " ± 5%" / " (+2% / −1%)" / "" — the tolerance part of an exact answer's symbolic
 * description, shown alongside the (unresolved) formula since there's no single value
 * to attach it to. Tolerance fields are assumed to be plain numbers, not formulas. */
/**
 * A literal answer's accepted band as text — "98 to 102", or just "100" when the tolerance is zero.
 *
 * The resolved reading the answer bar wants for anything that does not need a roll. Returns null
 * when the value or its tolerance will not parse, so the caller falls back to stating the answer as
 * authored rather than printing a band computed from NaN.
 */




interface MathErrorRowProps {
  /** The qualified path this row writes — `mathErrorCategories.<category>` — because the categories
   * are a plain object and the diff reports a dotted path for a scalar nested inside one. The bare
   * key would name the whole set and point at whichever row happened to render first. */
  fieldId?: string;
  title: string;
  content: string;
  evaluation: 'neutral' | 'incorrect';
  /** Whether the teacher has this category turned on at all — independent of whether it's
   * currently producing a wrong value (see `catching`). */
  enabled: boolean;
  /** Only meaningful when `enabled` — true if this equation/roll currently produces a
   * distinct wrong value for this category, false if there's structurally nothing to
   * catch right now (e.g. no trig function present). */
  catching: boolean;
  /** Advances the combined cycle Neutral → Incorrect → Deactivated → Neutral. The last
   * transition (out of Deactivated) both re-enables the category and resets evaluation to
   * Neutral, so the cycle always lands on a predictable state. */
  onAdvance: () => void;
  onContentChange: (html: string) => void;
  contentPlaceholder?: string;
  /** Registers this row's content editor into the shared, externally-rendered toolbar
   * (see feedback-card.tsx's identical `sharedToolbar` mechanics) instead of showing its
   * own toolbar on focus. */
  activeEditorRef: { current: Editor | null };
  setActiveEditor: (editor: Editor | null) => void;
}

// Condensed single-line row for a Math Error category. Teachers don't need to see the
// actual wrong-answer numbers, only whether a category is currently active, has nothing
// to catch right now, or has been turned off — so the disable control is folded into the
// same evaluation cycler (Neutral → Incorrect → Deactivated) instead of a separate button,
// and the header carries a plain text status next to the title instead of a chip. Expanding
// (the chevron) reveals only the message editor — no chips, no extra buttons there.
function MathErrorRow({
  fieldId, title, content, evaluation, enabled, catching,
  onAdvance, onContentChange, contentPlaceholder, activeEditorRef, setActiveEditor,
}: MathErrorRowProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const editorRef = useRef<Editor | null>(null);

  const cyclePos: 'neutral' | 'incorrect' | 'deactivated' = !enabled ? 'deactivated' : evaluation;
  const evalStateLabel =
    cyclePos === 'incorrect' ? t('blocks.feedbackCard.evalIncorrect') :
    cyclePos === 'deactivated' ? t('blocks.numerical.mathErrorStatusDeactivated') :
    t('blocks.feedbackCard.evalNeutral');
  const evalBtnAriaLabel = cyclePos === 'deactivated'
    ? t('blocks.numerical.mathErrorStatusDisabled')
    : t('blocks.feedbackCard.evalCycleAriaLabel', { state: evalStateLabel });
  const evalBtnClass = `flex w-9 shrink-0 items-center justify-center self-stretch min-h-9 rounded-md border transition-colors ${
    cyclePos === 'incorrect' ? 'border-ws-error-fg bg-ws-error-bg/40 text-ws-error-fg' :
    cyclePos === 'deactivated' ? 'border-ws-border text-ws-muted opacity-60 hover:opacity-100' :
    'border-ws-text bg-ws-btn text-ws-text hover:border-ws-primary hover:text-ws-primary'
  }`;

  const statusLabel: 'active' | 'inactive' | 'deactivated' = !enabled ? 'deactivated' : catching ? 'active' : 'inactive';

  return (
    <div data-field-id={fieldId} className='flex flex-col gap-2 rounded-xl border border-ws-border p-3 transition-colors'>
      <div className='flex items-center gap-2'>
        <button type='button' onClick={onAdvance} aria-label={evalBtnAriaLabel} className={evalBtnClass}>
          {cyclePos === 'incorrect' && <X aria-hidden='true' className='h-4 w-4' strokeWidth={3} />}
          {cyclePos === 'neutral' && <Minus aria-hidden='true' className='h-4 w-4' strokeWidth={2} />}
          {cyclePos === 'deactivated' && <CircleSlash aria-hidden='true' className='h-4 w-4' strokeWidth={2} />}
        </button>
        <button
          type='button'
          onClick={() => setCollapsed(c => !c)}
          aria-expanded={!collapsed}
          className='flex min-w-0 flex-1 items-center gap-2 text-left'
        >
          <span className='min-w-0 shrink truncate text-sm text-ws-text'>{title}</span>
          {statusLabel === 'active' && (
            <span className={chipClass('incorrect', { width: 'auto' })}>
              <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />
              {t('blocks.numerical.mathErrorStatusActive')}
            </span>
          )}
          {statusLabel === 'inactive' && (
            // The shared chip, not bare italics: this is the same "can never fire" that every
            // other state in the app renders as a dashed chip, and saying it two ways makes it
            // read as two different conditions.
            <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
          )}
          {statusLabel === 'deactivated' && (
            <span className='shrink-0 text-xs italic text-ws-muted'>{t('blocks.numerical.mathErrorStatusDeactivated')}</span>
          )}
          <span className='min-w-0 flex-1' />
          {collapsed
            ? <ChevronRight aria-hidden='true' className='h-4 w-4 shrink-0 text-ws-muted' />
            : <ChevronDown aria-hidden='true' className='h-4 w-4 shrink-0 text-ws-muted' />}
        </button>
      </div>
      {!collapsed && (
        <div onClick={() => editorRef.current?.commands.focus()}>
          <RichTextEditor
            content={content}
            onChange={onContentChange}
            placeholder={contentPlaceholder}
            editorClassName='min-h-[60px]'
            hideToolbar
            onEditorReady={(e) => { editorRef.current = e; }}
            onEditorFocus={() => {
              if (editorRef.current) { activeEditorRef.current = editorRef.current; setActiveEditor(editorRef.current); }
            }}
          />
        </div>
      )}
    </div>
  );
}


// Collapsed-view input row. Sits inside BlockPreviewProvider so the question chips
// above it get resolved. Decorative only (plain spans, not real inputs) — looks like
// a live input so the teacher can see what the block will look like, but clicking it
// just bubbles up to the card's own click-to-edit handler, same as MCQ/keyword's
// non-interactive option/answer previews.
/**
 * The "±0.5" hint under the answer box, shown when the teacher enables showAcceptedRange.
 * Only meaningful for exact-condition answers with a non-zero tolerance, which is why the
 * option is disabled for every other condition type.
 *
 * A plain function rather than part of a component because the hint has to appear on the
 * STUDENT surface (NumericalTestInput, which serves Preview, the selected card and the phone
 * Test tab) as well as the teacher's inert summary card. It used to live inside
 * NumericalAnswerSummary alone — a component that renders only when the block is UNSELECTED —
 * so an option reading "show the accepted range to students" showed it to nobody but the
 * teacher, and only while they were looking away from the block.
 *
 * Tolerance fields are plain numbers, not FormulaFields (only value/low/high/threshold take
 * equations), so parseFloat is the whole resolution story here.
 */
function toleranceHintFor(
  d: NumericalBlockData,
  language: string,
  t: (k: string, o?: Record<string, unknown>) => string,
  /** WHICH number this hint is for. It used to be implicitly the answer's and render under the
   *  whole row, so with an error box on screen it appeared to qualify both numbers and the unit
   *  while describing one of them. */
  scope: 'value' | 'uncertainty' = 'value',
): React.ReactNode | null {
  return (() => {
    // See `effectiveSetting`: a mode that takes the setting away must also take its effect
    // away. Optional printed "Tolerance: ±0.5" directly beside "All Answers Accepted".
    if (!effectiveSetting('showAcceptedRange', d.gradingMode, d.showAcceptedRange)) return null;
    if (d.conditionType !== 'exact') return null;
    const i18n = { language };
    if (scope === 'uncertainty') {
      /* AT ONE FIGURE THERE IS NO BAND to describe, and that is the lattice rule rather than an
         omission: the next admissible uncertainty is a 20% to 100% step away, so the editor does
         not offer a tolerance there and `uncertaintyBand` collapses to a point. Absent, not
         "exact" — the teacher chose nothing to report. */
      if (!uncertaintyAsked(d) || (d.uncertaintySigFigs ?? 1) < 2) return null;
      return toleranceHint(
        {
          symmetry: d.uncertaintyToleranceSymmetry ?? 'symmetric',
          toleranceMode: d.uncertaintyToleranceMode,
          toleranceValue: d.uncertaintyTolerance,
          toleranceAbove: d.uncertaintyToleranceAbove,
          toleranceBelow: d.uncertaintyToleranceBelow,
        },
        { fmt: (n: number) => fmtLocale(n, i18n.language), t },
      );
    }
    const { answers } = d;
    if (answers.length === 0) return null;
    // Multiple OR-answers: only show hint if all share the same tolerance config
    const first = answers[0]!;
    if (answers.length > 1) {
      const allSame = answers.every(a =>
        a.toleranceValue === first.toleranceValue &&
        a.toleranceAbove === first.toleranceAbove &&
        a.toleranceBelow === first.toleranceBelow,
      );
      if (!allSame) return null;
    }
    // The decision table lives in `toleranceHint`, shared with every other block that has a
    // tolerance. What stays here is the part that is Numerical's own: whether the option is on,
    // whether the condition type is eligible, and whether several OR-answers agree enough to
    // describe as one.
    return toleranceHint(
      {
        /* PER ANSWER, read through the shared helpers rather than off the block. Mixing the
           block's mode with an answer's values is the defect already fixed once in the grader
           this round: a teacher who set absolute on the answer was shown a percentage band. */
        symmetry: answerSymmetry(first, d),
        toleranceMode: answerToleranceMode(first, d, 'absolute'),
        toleranceValue: first.toleranceValue,
        toleranceAbove: first.toleranceAbove,
        toleranceBelow: first.toleranceBelow,
      },
      // No unit: Numerical's own unit box sits right beside the input, so repeating it here would
      // say the same thing twice on one line.
      { fmt: (n: number) => fmtLocale(n, i18n.language), t },
    );
  })();
}

/**
 * The DISPLAY state — the unselected card. Per the block convention it is "a static rendition that
 * looks like the active state", so it renders the active state itself, made inert, rather than a
 * parallel mockup that has to be kept in sync by hand.
 *
 * It stays BLANK. Numerical is one of the blocks whose answer cannot be written into the card (a
 * tolerance band or a threshold is not a number you can put in a box), so the answer is carried by
 * the status bar instead — see the always-on bar this block shares with Slider, Scale and the three
 * sensors.
 */
function NumericalAnswerSummary({ d }: { d: NumericalBlockData }) {
  const { t } = useTranslation();
  return <NumericalTestInput d={d} value='' unit='' uncertainty='' onValue={noop} onUnit={noop} onUncertainty={noop} t={t} inert />;
}

const noop = () => {};

// Pure function that returns the answer bar content for the collapsed numerical block.
// Called by CollapsedInputBlock's answerBarSlot render prop, receiving the current
// mini-preview rolled values (null = preview off).

// ─── Standardized issue reporting ──────────────────────────────────────────────
// Numerical was the inverted outlier of the whole block set: it had per-card feedback warnings
// (valueFeedbackWarning) but NO issue bar, so its problems were the only ones invisible unless the
// offending card happened to be open. This adds the shared frame every other audited block already
// renders through.
//
// SCOPE: aggregates only what this block ALREADY detects and shows somewhere — setup-incomplete,
// blank feedback, and the three feedback-overlap conditions. Deliberately introduces no NEW checks;
// those belong to Numerical's own deep audit, or the bulk pass stops being mechanical and turns
// into eleven small design decisions wearing one commit message.
function buildNumericalIssues(args: {
  t: (k: string, opts?: Record<string, unknown>) => string;
  question: string;
  setupIncomplete: boolean;
  hasMissingFeedbackText: boolean;
  /** How many Math Error override equations do not produce the answer they are attached to. */
  mathErrorEquationsWrong: number;
  /** A targeted trigger that can never fire. These were previously validated ONLY inline, next to the
   * field, so a malformed trigger reached none of the three issue surfaces — unlike GPS/Compass/Angle,
   * where the same mistake is a real error the bar and the canvas card both report. */
  triggerMissingValue: boolean;
  /** A trigger holding a formula that cannot be resolved — typed, but not a number. */
  triggerUnresolvable: boolean;
  triggerRangeIncomplete: boolean;
  triggerRangeInverted: boolean;
  anySubsumed: boolean;
  anyOverlapsCorrect: boolean;
  /** A trigger that collides with Correct for SOME rolls and not others. Only reachable with a
   * variable on one side of the comparison, which is exactly why it needs saying: the teacher is
   * looking at one roll, and the collision may not be in it. */
  anyMayOverlapCorrect: boolean;
  anyOverlapEachOther: boolean;
  /** A between pair whose low sits above its high — accepts nothing at all. Previously reported
   * ONLY inline, so it reached none of the three issue surfaces. */
  betweenVerdict: ComparisonVerdict;
  trigRangeVerdict: ComparisonVerdict;
  /** The significant-figure requirement and the accepted band admit no common number, so no
   * submission can ever be marked correct. */
  sigFigsUnsatisfiable: boolean;
  uncertaintyFiguresUnsatisfiable: boolean;
  valuePlaceUnsatisfiable: boolean;
  authoredAnswerFigures: { id: string; figs: number } | null;
  authoredUncertaintyFigures: { figs: number } | null;
  authoredAnswerPlace: { id: string } | null;
  answerOverlap: boolean;
  /** How many figures were asked for — named in the message, since "significant figures" alone
   * leaves the teacher counting. */
  sigFigsCount: number;
  /** An answer/tolerance/threshold field left EMPTY. An absence, so it waits for the completion
   * boundary. Consolidated into one issue on purpose: the status bar names the AREA to look in,
   * and one row per field would flood the panel with what is really "the answer isn't written
   * yet". */
  answerFieldBlank: boolean;
  /** An answer/tolerance/threshold field holding something that is not a usable number — a
   * malformed literal, or a formula that cannot resolve. Authored and wrong, so it surfaces
   * immediately: more typing in that field is exactly what needs to happen. */
  answerFieldMalformed: boolean;
  /** Units are switched on but not named yet (no canonical unit, or no display label). */
  unitSetupBlank: boolean;
  /** A canonical unit was typed and does not parse. Tagged separately from `unitSetupBlank`
   * because the two are opposite kinds: staying quiet about a typo until the block closes is how
   * one reached publication. */
  unitSetupInvalid: boolean;
  /** Graded, and nothing at all has been entered for the answer — not a partial setup, an absent
   * one. The same condition MCQ, Keyword and Scale each report as `no-correct-option`. Numerical
   * used to render it as an amber, uncategorised bar on the card and nowhere else, so the one
   * block whose answer is hardest to eyeball was also the one that called an unanswerable block a
   * warning while three others called it an error. */
  noCorrectAnswers: boolean;
  /**
   * Unit OPTIONS that misbehave, as `{ labels, ids }` so the issue can point at the rows.
   *
   * One issue per kind rather than per row: with `VISIBLE_ISSUE_CAP` at 3, a teacher with three bad
   * options would push every other problem in the block behind the "+N more" disclosure.
   *
   * The message does not name the offenders either — "one or more" is enough, because `ids` rings
   * the actual rows. Naming them in prose as well would be the same fact twice, and the prose is
   * the copy that goes stale the moment a label is edited.
   */
  badUnitOptions: {
    duplicate: { labels: string[]; ids: string[] };
    unrecognized: { labels: string[]; ids: string[] };
  };
  /** Scenario rows copied from the block's own setup and not yet confirmed for that scenario
   *  — §4.4. See the push below for why the in-box strip is not enough on its own. */
  unreviewedScenarios: number;
  /** The exact answers disagree about how their tolerances should be read. Precomputed, like
   *  every other verdict here — this builder never sees the raw fields. */
  mixedToleranceTypes: boolean;
}): BlockIssue[] {
  const { t, question, setupIncomplete, hasMissingFeedbackText, mathErrorEquationsWrong, noCorrectAnswers,
          triggerMissingValue, triggerUnresolvable, triggerRangeIncomplete, triggerRangeInverted,
          anySubsumed, anyOverlapsCorrect, anyMayOverlapCorrect, anyOverlapEachOther,
          betweenVerdict, trigRangeVerdict, sigFigsUnsatisfiable, uncertaintyFiguresUnsatisfiable,
          valuePlaceUnsatisfiable, authoredAnswerFigures, authoredUncertaintyFigures,
          authoredAnswerPlace,
          answerOverlap, sigFigsCount, answerFieldBlank, answerFieldMalformed,
          unitSetupBlank, unitSetupInvalid, badUnitOptions, unreviewedScenarios,
          mixedToleranceTypes } = args;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const categoryFeedback = t('blocks.areas.feedback');

  if (!question.replace(/<\/?p>/g, '').trim()) {
    issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  }
  // Three verdicts: a row that is ALWAYS backwards accepts nothing ever (a defect); one that is
  // backwards on SOME rolls accepts nothing on those rolls (a warning, and it says so); and a
  // variable space too wide to sweep is reported as unverified rather than passed over silently.
  if (betweenVerdict === 'always') {
    issues.push({ key: 'between-inverted', severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.conditions.betweenInverted') });
  } else if (betweenVerdict === 'sometimes') {
    issues.push({ key: 'between-may-invert', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.conditions.betweenMayInvert') });
  } else if (betweenVerdict === 'unknown') {
    issues.push({ key: 'answers-unverifiable', severity: 'warning', kind: 'unverified', category: categoryAnswerSetup,
      detail: t('blocks.answers.issueTooWideToVerify') });
  }
  /*
   * ANSWERS THAT READ THEIR TOLERANCES DIFFERENTLY — said out loud, never forbidden.
   *
   * Mixing is deliberately allowed: OR-logic answers are independent acceptance windows, so
   * there is no cross-answer invariant for a mixed setting to violate, and the one question
   * that NEEDS it — accepted answers spanning zero, where a percentage collapses on the small
   * one — was unauthorable while these lived on the block.
   *
   * It is still worth a warning, because a column of bands that mean different things is the
   * real cost of the move. A warning, following the standing rule that a capability is costed
   * rather than removed: the teacher is told, and decides.
   */
  if (mixedToleranceTypes) {
    issues.push({ key: 'tolerance-types-differ', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.tolerance.issueTypesDiffer') });
  }

  /*
   * UNREVIEWED ROWS, counted — Dropdown has had this since the feature shipped and Numerical
   * did not, which is the whole of the bug.
   *
   * The strip inside the answer-setup box speaks only for the row you are on, so a teacher who
   * never pages to scenario 5 never learns that scenario 5 was never decided. On Numerical the
   * box is also the busiest one in the product, and the strip sat under an answer bar that is
   * always there: the one signal competed with furniture and lost.
   *
   * An INCOMPLETION, not a defect — every row has a working answer key; what is missing is
   * somebody's confirmation that the copy is right for that scenario. A warning, because the
   * block grades perfectly well in the state; `kind` is what makes it count at publication.
   */
  if (unreviewedScenarios > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedScenarios }),
    });
  }
  if (sigFigsUnsatisfiable && !authoredAnswerFigures) {
    // An ERROR and a DEFECT: both settings are present, they contradict each other, and the
    // contradiction is complete — not "this might grade oddly" but "nothing can be correct".
    issues.push({ key: 'sigfig-unsatisfiable', severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueSigFigsUnsatisfiable', { count: sigFigsCount }) });
  }
  /* Self-consistency of what was AUTHORED, ahead of the reachability checks below: a key that
     cannot be submitted is a simpler problem than a band that admits nothing, and saying both
     would send the teacher after the second when the first is the cause. */
  if (authoredAnswerFigures) {
    issues.push({ key: 'authored-answer-figures', fields: [authoredAnswerFigures.id],
      severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueAuthoredAnswerFigures',
        { count: authoredAnswerFigures.figs, want: sigFigsCount }) });
  }
  if (authoredUncertaintyFigures) {
    issues.push({ key: 'authored-uncertainty-figures', fields: ['uncertaintyValue'],
      severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueAuthoredUncertaintyFigures',
        { count: authoredUncertaintyFigures.figs }) });
  }
  if (authoredAnswerPlace) {
    issues.push({ key: 'authored-answer-place', fields: [authoredAnswerPlace.id],
      severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueAuthoredAnswerPlace') });
  }
  /* STEPS ASIDE FOR THE AUTHORING ERRORS ABOVE. A band that admits nothing is the CONSEQUENCE of
     a key written to the wrong precision, so reporting both sends the teacher after the symptom
     alongside the cause. Fix the key and these re-evaluate on their own. */
  if (valuePlaceUnsatisfiable && !authoredAnswerPlace && !authoredUncertaintyFigures) {
    issues.push({ key: 'value-place-unsatisfiable', severity: 'error', kind: 'defect',
      category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueValuePlaceUnsatisfiable') });
  }
  if (uncertaintyFiguresUnsatisfiable && !authoredUncertaintyFigures) {
    // Same severity and kind as the value's: both settings present, contradicting completely.
    issues.push({ key: 'uncertainty-figures-unsatisfiable', severity: 'error', kind: 'defect',
      category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueUncertaintyFiguresUnsatisfiable') });
  }
  if (noCorrectAnswers) {
    issues.push({ key: 'no-correct-answers', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup,
      detail: t('blocks.answers.noCorrectAnswers') });
  }
  if (answerOverlap) {
    // A WARNING: overlapping answers still grade, they just grade more narrowly than they read.
    issues.push({ key: 'answer-overlap', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.answers.issueAnswerOverlap') });
  }
  if (answerFieldMalformed) {
    issues.push({ key: 'answer-field-invalid', severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueAnswerFieldInvalid') });
  }
  if (answerFieldBlank) {
    issues.push({ key: 'answer-field-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueAnswerFieldBlank') });
  }
  if (unitSetupInvalid) {
    issues.push({ key: 'unit-setup-invalid', severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueUnitSetupInvalid') });
  }
  if (unitSetupBlank) {
    issues.push({ key: 'unit-setup-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueUnitSetupBlank') });
  }
  // Both change GRADING, which is why they are errors rather than inline-only hints. A duplicate is
  // dead configuration — `unitOptions.find(...)` returns the first match, so later copies are
  // unreachable and the first silently decides all of them. An unrecognized option is worse than
  // dead: it is stored with role `wrong`, so a student who types that exact string is marked
  // incorrect by a rule the teacher most likely typed by accident. Neither is visible once the
  // editor is closed, which is what an issue fixes — the same reach an unrecognized CANONICAL unit
  // has had all along, and the asymmetry between the two was never deliberate.
  if (badUnitOptions.duplicate.labels.length > 0) {
    issues.push({
      key: 'unit-option-duplicate',
      fields: badUnitOptions.duplicate.ids,
      severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueUnitOptionDuplicate'),
    });
  }
  if (badUnitOptions.unrecognized.labels.length > 0) {
    issues.push({
      key: 'unit-option-unrecognized',
      fields: badUnitOptions.unrecognized.ids,
      severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.numerical.issueUnitOptionUnrecognized'),
    });
  }

  if (setupIncomplete) {
    // Steps aside for any specific answer-setup check — "Numerical setup incomplete" alongside
    // "Min is greater than max" is one problem stated twice, at two levels of precision. Slider,
    // compass, angle and gps already do this; numerical was the outlier.
    issues.push({ key: 'setup-incomplete', severity: 'error', kind: 'incompletion',
      supersededBy: ['between-inverted', 'answer-field-invalid', 'answer-field-blank', 'unit-setup-invalid', 'unit-setup-blank', 'no-correct-answers'],
      category: categoryAnswerSetup, detail: t('blocks.numerical.barSetupIncomplete') });
  }
  // Errors, not warnings: a trigger with no usable number can never fire, so the state silently does
  // nothing while looking authored. Not grace-gated — adding a targeted state is an explicit action.
  if (trigRangeVerdict === 'always' || trigRangeVerdict === 'sometimes') {
    issues.push({ key: 'trigger-range-may-invert', severity: 'warning', kind: 'defect', category: categoryFeedback,
      detail: t('blocks.conditions.betweenMayInvert') });
  }
  if (triggerRangeInverted) {
    issues.push({ key: 'trigger-range-inverted', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.numerical.validationRangeInverted') });
  }
  if (mathErrorEquationsWrong > 0) {
    // A DEFECT, not an incompletion: the equation is present and says something, and what it says
    // contradicts the answer beside it. Math Error mutates whatever it is given, so this one
    // silently diagnoses mistakes against a number the student was never asked for.
    issues.push({
      key: 'math-error-equation', severity: 'error', kind: 'defect', category: categoryFeedback,
      detail: t('blocks.numerical.validationMathErrorEquation'),
    });
  }
  if (triggerUnresolvable) {
    // Split from "no value set" for the same reason the answer fields are: a blank trigger is
    // unfinished, while `{missing}+1` is a formula the teacher wrote that does not come out as a
    // number. Saying "no value set" about a field with something in it sends them looking for an
    // empty box they will not find.
    issues.push({ key: 'trigger-unresolvable', severity: 'error', kind: 'defect', category: categoryFeedback,
      detail: t('blocks.numerical.validationTriggerUnresolvable') });
  }
  if (triggerMissingValue) {
    issues.push({ key: 'trigger-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.numerical.validationTriggerIncomplete') });
  }
  if (triggerRangeIncomplete) {
    issues.push({ key: 'trigger-range-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.numerical.validationRangeIncomplete') });
  }
  if (hasMissingFeedbackText) {
    issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });
  }
  // One issue per KIND, not per offending state — matches the MCQ/Dropdown chip-collision collapse,
  // so three overlapping ranges read as one problem to fix rather than three.
  if (anySubsumed) {
    issues.push({ key: 'feedback-subsumed', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.numerical.feedbackSubsumedWarning') });
  }
  if (anyOverlapsCorrect) {
    issues.push({ key: 'feedback-overlaps-correct', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.numerical.feedbackOverlapsCorrectWarning') });
  }
  // Superseded by the certain version of itself: a trigger that always collides is reported as
  // always, and a second, hedged line about the same block would read as a second problem. This is
  // for the collision a teacher cannot see, because the roll in front of them does not contain it.
  if (anyMayOverlapCorrect) {
    issues.push({ key: 'feedback-may-overlap-correct', severity: 'warning', kind: 'defect',
      supersededBy: ['feedback-overlaps-correct', 'feedback-subsumed'],
      category: categoryFeedback, detail: t('blocks.numerical.feedbackMayOverlapCorrectWarning') });
  }
  if (anyOverlapEachOther) {
    issues.push({ key: 'feedback-overlap', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.numerical.feedbackOverlapWarning') });
  }
  return issues;
}

function numericalCollapsedBar(
  d: NumericalBlockData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  issues: BlockIssue[],
): React.ReactNode {
  // Highest-severity issue first, exactly as the other nine blocks do. Numerical used to run its
  // own priority ladder here — a private ordering over four hand-built bars, none of which reached
  // the issues list — which is why an unanswerable block read as an uncategorised amber "No
  // Correct Answers" on the card while the editor panel said something else entirely. The ladder
  // is gone; severity ordering is the shared rule, and every condition it used to own is now a
  // real issue (see numericalIssues).
  const issueBar = collapsedIssueBar(issues);
  if (issueBar) return issueBar;

  if (d.gradingMode !== 'graded') return collapsedModeBar(d.gradingMode, t, 'numerical', undefined, numericalSurveyBarKey(d));

  /*
   * The collapsed card RENDERS the Correct chips — the fifth and last surface to stop deriving its
   * own. It had `formulas`, a pre-filtered list from `formulasFromData`, printed with no connector
   * between the entries: two answers appeared as two bare equations with nothing saying they were
   * alternatives, while the editor beside it and compass/angle's cards all said "or".
   *
   * Roll `null`: a card is a DISPLAY surface, so a variable shows as its equation, not one roll.
   */
  const cardChips = numericalCorrectChips(d, null, t, locale);
  if (answerBarHasContent(cardChips, numericalHasAnswerText(d))) {
    return (
      <CollapsedBlockBar tone='success' align='start'>
        <div className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
          <AnswerBarText chips={cardChips}
              noValidAnswerLabel={numericalHasAnswerText(d) ? t('blocks.answers.noValidAnswer') : undefined} />
          {/* NO UNIT HERE. The chips carry it — `symbolicAnswerLabels` appends it to every
              label and `previewsForUnit` puts it in every resolved preview — so a second one
              printed the bar as `{river}+{kayak} m/sm/s`. This span predates the value-range
              chip unification, which moved the unit into the chips and left it behind. */}
        </div>
      </CollapsedBlockBar>
    );
  }

  const barEntries = computeBarEntries(d, t, locale, {});
  if (barEntries === null || barEntries.entries.length === 0) return null;
  const { entries: barList } = barEntries;

  return (
    <CollapsedBlockBar tone='success' align='start'>
      <span className='min-w-0'>{barList.map(e => e.preview).join(', ')}</span>
    </CollapsedBlockBar>
  );
}

// ─── Data migration ───────────────────────────────────────────────────────────

function normalizeMathErrorCategories(raw: unknown): Record<MathErrorCategory, boolean> {
  const r = (raw ?? {}) as Partial<Record<MathErrorCategory, boolean>>;
  const out = {} as Record<MathErrorCategory, boolean>;
  for (const cat of MATH_ERROR_CATEGORIES) out[cat] = r[cat] ?? true;
  return out;
}

/** Numerical's nine fixed states beyond the universal three, in the order the panel renders them
 * (Math Error, Unit Feedback, Value Feedback, Sig Figs) — `normalizeFeedback` re-derives every
 * extra's position from this declaration order on load. Module-level so the editor's own seeding
 * and the card's `buildNormalizedNumericalData` cannot drift into two different lists. */
function numericalExtraDefaults(t: (key: string, opts?: Record<string, unknown>) => string): ExtraDefaultState[] {
  return [
    {
      trigger: 'incomplete_answer',
      title: t('blocks.feedbackDefaults.incompleteAnswerTitle'),
      content: `<p>${t('blocks.feedbackDefaults.incompleteAnswerContent')}</p>`,
      evaluation: 'neutral',
    },
    {
      trigger: 'math_error_sign_flip',
      title: t('blocks.numerical.mathErrorSignFlipFeedback'),
      content: `<p>${t('blocks.numerical.mathErrorSignFlipDefault')}</p>`,
      evaluation: 'neutral',
    },
    {
      trigger: 'math_error_dropped_parens',
      title: t('blocks.numerical.mathErrorDroppedParensFeedback'),
      content: `<p>${t('blocks.numerical.mathErrorDroppedParensDefault')}</p>`,
      evaluation: 'neutral',
    },
    {
      trigger: 'math_error_exponent_misread',
      title: t('blocks.numerical.mathErrorExponentMisreadFeedback'),
      content: `<p>${t('blocks.numerical.mathErrorExponentMisreadDefault')}</p>`,
      evaluation: 'neutral',
    },
    {
      trigger: 'math_error_swapped_operands',
      title: t('blocks.numerical.mathErrorSwappedOperandsFeedback'),
      content: `<p>${t('blocks.numerical.mathErrorSwappedOperandsDefault')}</p>`,
      evaluation: 'neutral',
    },
    {
      trigger: 'math_error_ignored_precedence',
      title: t('blocks.numerical.mathErrorIgnoredPrecedenceFeedback'),
      content: `<p>${t('blocks.numerical.mathErrorIgnoredPrecedenceDefault')}</p>`,
      evaluation: 'neutral',
    },
    {
      trigger: 'math_error_wrong_angle_mode',
      title: t('blocks.numerical.mathErrorWrongAngleModeFeedback'),
      content: `<p>${t('blocks.numerical.mathErrorWrongAngleModeDefault')}</p>`,
      evaluation: 'neutral',
    },
    {
      trigger: 'math_error_log_ln_confusion',
      title: t('blocks.numerical.mathErrorLogLnFeedback'),
      content: `<p>${t('blocks.numerical.mathErrorLogLnDefault')}</p>`,
      evaluation: 'neutral',
    },
    {
      /*
       * Present whatever the mode; the panel and the chips gate it on `withUnits`, the same way
       * `incomplete_answer` is carried and gated. Seeded incorrect with the other two answer-form
       * faults: a unit the question does not accept is a wrong answer, not a note about one.
       */
      trigger: 'unit_error',
      title: t('blocks.numerical.unitErrorFeedback'),
      content: `<p>${t('blocks.numerical.unitErrorDefault')}</p>`,
      evaluation: 'incorrect',
    },
    // Sig figs sits last, after every math-error category — matches the panel's own
    // section order (Math Error, Unit Feedback, Value Feedback, Sig Figs) and normalizeFeedback
    // re-derives every extra's position from this declaration order on every load, so this
    // also fixes the order for blocks saved before this reordering.
    // Ahead of sig figs, matching the order the evaluator checks them in: a non-normalized number
    // has an ambiguous sig-fig count, so the notation fault is the one that gets reported.
    /*
     * SEEDED INCORRECT, with the three unit/format faults, and for the reason the evaluator
     * already states one branch down: where the VALUE is accepted but the form is not, the
     * format fault IS the verdict — "a teacher who required the notation meant the answer to
     * carry it". Seeding neutral made that branch hand back a neutral verdict for an answer
     * that failed a requirement the teacher had deliberately switched on, so the evaluator and
     * the seed disagreed. Both are opt-in: a block that asks for neither never sees these.
     *
     * Unconditional, NOT mode-aware. Participation neutralises them at the card
     * (`displayEvaluation`) and at the fire (`participationOutcome`), so the stored value is the
     * Has Correct setup and the mode decides what is in force — the same shape as
     * `MODE_GATED_SETTINGS`. A mode-aware seed would instead make a block first authored in
     * Participation come back to Has Correct defaulting to neutral, which is the wrong default
     * in the only mode that reads it.
     */
    {
      trigger: 'notation_error',
      title: t('blocks.numerical.notationErrorFeedback'),
      content: `<p>${t('blocks.numerical.notationErrorDefault')}</p>`,
      evaluation: 'incorrect',
    },
    {
      trigger: 'sigfig_error',
      title: t('blocks.numerical.sigfigErrorFeedback'),
      content: `<p>${t('blocks.numerical.sigfigErrorDefault')}</p>`,
      evaluation: 'incorrect',
    },
    /* THE ERROR BOX, APPENDED — `normalizeFeedback` re-derives every extra's position from this
       order, so anything inserted above would move the cards of every block already saved. Which
       is also why the three that used to sit here could be removed: they were last.

       TWO CARDS, not five and not twelve. A general `uncertainty_error` duplicated Default
       Incorrect, which is the universal catch-all and is used as one here; the fraction detector
       was the only arithmetic in the grader joining the two submitted numbers, and a derived
       relation between the answer and the uncertainty is the thing this block does not have; and
       the seven math-error categories are shared with the answer's, because the toggles that
       enable them were always block-level and a duplicate set of cards said otherwise. */
    {
      trigger: 'uncertainty_sigfig_error',
      title: t('blocks.numerical.uncertaintySigfigFeedback'),
      content: `<p>${t('blocks.numerical.uncertaintySigfigDefault')}</p>`,
      evaluation: 'incorrect',
    },
    {
      trigger: 'agreement_error',
      title: t('blocks.numerical.agreementErrorFeedback'),
      content: `<p>${t('blocks.numerical.agreementErrorDefault')}</p>`,
      evaluation: 'incorrect',
    },
  ];
}


/** The default feedback set for a block read straight from `block.data` — the editor seeds these
 * into its own state, but nothing reaches the saved block until the teacher changes something, so
 * without this a freshly added block's test legend has no states to show while the feedback panel
 * beside it lists several. States rather than a data object, because the local (authoring-time,
 * string-based) NumericalBlockData deliberately does not carry feedback: this block's two
 * non-editing render paths read it off `block.data` separately. */
/** Exported for the seeding test: what a block's default states START as, which for
 *  `unit_error` now depends on the mode. */
export function normalizedNumericalStates(
  raw: unknown,
  gradingMode: NumericalBlockData['gradingMode'],
  t: (key: string, opts?: Record<string, unknown>) => string,
): FeedbackState[] {
  return normalizeStandardFeedback(raw, t, gradingMode, { extraDefaults: numericalExtraDefaults(t) }).states;
}

/**
 * A bank's versions, with each one's ANSWERS backfilled exactly as the block's own are.
 *
 * Shared by both `initData` branches because `initData branch parity` requires them to produce
 * the same key set — a legacy block and a modern one must be indistinguishable downstream, and a
 * key present in only one of them is the drift that invariant exists to catch.
 *
 * The backfills themselves matter for the same reason they do on the block: a stored answer row
 * can predate `mathErrorEquation`, and an editor field reading it would be undefined.
 */
function parseVersions(raw: unknown): NumericalBlockData['versions'] {
  return (raw as NumericalBlockData['versions'])?.map(v => {
    const vAnswers = v.answers as ExactAnswer[] | undefined;
    const vBetween = v.betweenAnswers as BetweenAnswer[] | undefined;
    return {
      ...v,
      answers: vAnswers?.length
        ? vAnswers.map(a => ({ ...a, mathErrorEquation: a.mathErrorEquation ?? '' }))
        : vAnswers,
      betweenAnswers: vBetween?.length
        ? vBetween.map(a => ({ ...a, mathErrorEquationLow: a.mathErrorEquationLow ?? '', mathErrorEquationHigh: a.mathErrorEquationHigh ?? '' }))
        : vBetween,
    };
  });
}

export function initData(rawInput: Record<string, unknown>): NumericalBlockData {
  /* Blocks saved before the rename say `errorValue`; read under the current names. A missed
     one would not crash — `?? ''` turns it into an empty tolerance, and an empty tolerance
     grades EXACT, so every authored band would quietly become a point. */
  const raw = withLegacyToleranceNames(rawInput);
  /**
   * Migrate old flat format (correctValue, toleranceMode, etc.) to new model.
   *
   * This branch must return the SAME FIELD SET as the modern one below, not merely a valid
   * `NumericalBlockData`. `useRestoredData` compares `Object.keys` of what `derive` returns
   * against the editor's own state and throws when they differ, and the two branches are the one
   * place they can differ: a legacy block seeds its editor state HERE, then its first edit writes
   * `conditionType` and every later `derive` takes the modern branch instead. So a field the
   * modern branch sets and this one omits is not a missing default -- it is a crash on the first
   * keystroke in any block authored before that field existed, and only in those blocks.
   *
   * `canonicalSpellings` was exactly that. Optional on the interface, so its absence here was not
   * a type error, and every test used modern fixtures. `initData-branch-parity` in
   * `numerical-legacy-migration.test.ts` now compares the two key sets directly.
   */
  if (!raw.conditionType) {
    const unitLabel = (raw.unitLabel as string) ?? '';
    return {
    versions: parseVersions(raw.versions),
    // Both branches must name these, like every other field: `initData-branch-parity` asserts
    // the two key sets match, and a scenario link that existed in one and not the other would
    // unlink a legacy block on its first keystroke.
    followsBlockId: raw.followsBlockId as NumericalBlockData['followsBlockId'],
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as NumericalBlockData['drivenBy'],
    scenarioBranches: raw.scenarioBranches as NumericalBlockData['scenarioBranches'],

      question:      (raw.question as string) ?? '',
      conditionType: 'exact',
      answers: [{
        id:         'primary',
        value:      (raw.correctValue as string) ?? '',
        toleranceValue: (raw.toleranceValue as string) ?? '',
        toleranceAbove: (raw.toleranceAbove as string) ?? '',
        toleranceBelow: (raw.toleranceBelow as string) ?? '',
        mathErrorEquation: '',
      }],
      toleranceMode:     (['absolute', 'percentage'].includes(raw.toleranceMode as string) ? raw.toleranceMode as ToleranceMode : 'percentage'),
      toleranceSymmetry: (['symmetric', 'asymmetric'].includes(raw.toleranceSymmetry as string) ? raw.toleranceSymmetry as 'symmetric' | 'asymmetric' : 'symmetric'),
      betweenAnswers: [],
      threshold:     '',
      integerOnly:   false,
      uncertaintyRequired: false,
      uncertaintyValue:    '',
      uncertaintyMathErrorEquation: undefined,
      uncertaintyTolerance:    '',
      uncertaintyToleranceAbove: '',
      uncertaintyToleranceBelow: '',
      /* PERCENTAGE, the same default the answer's own tolerance takes — two tolerance rows in one
         panel disagreeing about their default is a trap, and this one was absolute only by
         omission. Symmetry stays EXPLICIT `undefined`: the key has to exist in both branches or
         `initData branch parity` reports the two shapes as different, which is what it is for. */
      uncertaintyToleranceMode: 'percentage',
      uncertaintyToleranceSymmetry: undefined,
      uncertaintySigFigs:  1,
      requireExponentialNotation: false,
      sigFigsRequired: false,
      sigFigsCount:  3,
      mathErrorEnabled: false,
      mathErrorCategories: normalizeMathErrorCategories(undefined),
      unitMode:      unitLabel ? 'display' : 'none',
      unitLabel,
      canonicalUnit: '',
      // Through the same helper the modern branch uses, so the key is present either way -- it
      // resolves to `undefined` for a blank canonical, and a KEY that is absent is what breaks
      // the compare, not a value that is undefined.
      ...unitIdentityFor(''),
      unitOptions:   [],
      tempConvMode:  'absolute',
      angleMode:     'degrees',
      // Present and empty, not absent: `initData`'s two branches must agree on the FIELD SET,
      // which `numerical-legacy-migration` asserts — a key in one and not the other is how a
      // brand-new block and an edited one start behaving differently.
      angleModes:    undefined,
      showAcceptedRange: (raw.showAcceptedRange as boolean) ?? false,
      gradingMode:   (['graded', 'survey', 'optional'].includes(raw.gradingMode as string) ? raw.gradingMode as NumericalBlockData['gradingMode'] : 'graded'),
    };
  }

  const conditionType = (raw.conditionType as ConditionType) ?? 'exact';
  const rawAnswers = raw.answers as ExactAnswer[] | undefined;
  const rawBetween = raw.betweenAnswers as BetweenAnswer[] | undefined;

  const parsed = {
    versions:        parseVersions(raw.versions),
    // Both branches must name these, like every other field: `initData-branch-parity` asserts
    // the two key sets match, and a scenario link that existed in one and not the other would
    // unlink a legacy block on its first keystroke.
    followsBlockId: raw.followsBlockId as NumericalBlockData['followsBlockId'],
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as NumericalBlockData['drivenBy'],
    scenarioBranches: raw.scenarioBranches as NumericalBlockData['scenarioBranches'],
    question:        (raw.question as string) ?? '',
    conditionType,
    answers:         rawAnswers?.length
      ? rawAnswers.map(a => ({ ...a, mathErrorEquation: a.mathErrorEquation ?? '' }))
      : [newExactAnswer()],
    toleranceMode:       (['absolute', 'percentage'].includes(raw.toleranceMode as string) ? raw.toleranceMode as ToleranceMode : 'percentage'),
    toleranceSymmetry:   (['symmetric', 'asymmetric'].includes(raw.toleranceSymmetry as string) ? raw.toleranceSymmetry as 'symmetric' | 'asymmetric' : 'symmetric'),
    betweenAnswers:  rawBetween?.length
      ? rawBetween.map(a => ({ ...a, mathErrorEquationLow: a.mathErrorEquationLow ?? '', mathErrorEquationHigh: a.mathErrorEquationHigh ?? '' }))
      : [newBetweenAnswer()],
    threshold:       (raw.threshold as string) ?? '',
    integerOnly:     (raw.integerOnly as boolean) ?? false,
    uncertaintyRequired: (raw.uncertaintyRequired as boolean) ?? false,
    uncertaintyValue:    (raw.uncertaintyValue as string) ?? '',
    uncertaintyMathErrorEquation: (raw.uncertaintyMathErrorEquation as string) || undefined,
    uncertaintyTolerance:    (raw.uncertaintyTolerance as string) ?? '',
    uncertaintyToleranceAbove: (raw.uncertaintyToleranceAbove as string) ?? '',
    uncertaintyToleranceBelow: (raw.uncertaintyToleranceBelow as string) ?? '',
    /* Falls back the way `toleranceMode` does a few lines up, and to the same value. Symmetry is
       CARRIED rather than defaulted — `undefined` there is "symmetric", the meaning the answer's
       own has when absent, and a literal would repeat the mistake three builders already made. */
    uncertaintyToleranceMode: (['absolute', 'percentage'].includes(raw.uncertaintyToleranceMode as string)
      ? raw.uncertaintyToleranceMode as ToleranceMode : 'percentage'),
    uncertaintyToleranceSymmetry: (['symmetric', 'asymmetric'].includes(raw.uncertaintyToleranceSymmetry as string)
      ? raw.uncertaintyToleranceSymmetry as 'symmetric' | 'asymmetric' : undefined),
    // Absent on every block authored before the error box, where 1 is the convention anyway.
    /* Clamped on READ as well as in the stepper, so a block saved before the cap came down, or
       one edited by another route, still lands inside the convention. */
    uncertaintySigFigs:  Math.min(2, Math.max(1, (raw.uncertaintySigFigs as number) ?? 1)),
    requireExponentialNotation: (raw.requireExponentialNotation as boolean) ?? false,
    sigFigsRequired: (raw.sigFigsRequired as boolean) ?? false,
    sigFigsCount:    (raw.sigFigsCount as number) ?? 3,
    mathErrorEnabled: (raw.mathErrorEnabled as boolean) ?? false,
    mathErrorCategories: normalizeMathErrorCategories(raw.mathErrorCategories),
    unitMode:        (raw.unitMode === 'required' ? 'withUnits'
      : (['none', 'display', 'withUnits'].includes(raw.unitMode as string) ? raw.unitMode as NumericalBlockData['unitMode'] : 'none')),
    unitLabel:       (raw.unitLabel as string) ?? '',
    canonicalUnit:   (raw.canonicalUnit as string) ?? '',
    unitOptions:     Array.isArray(raw.unitOptions)
      ? (raw.unitOptions as (Omit<UnitOption, 'conversionOffset'> & { conversionOffset?: number })[]).map(o => ({ ...o, conversionOffset: o.conversionOffset ?? 0 }))
      : Array.isArray(raw.unitForms)
        // Legacy `unitForms` migration. Positional id, not a minted one — this runs during render,
        // so a random id gave the migrated unit a new identity every pass.
        ? (raw.unitForms as string[]).filter(Boolean).map((label: string, idx: number) => ({
            id: `unit-${idx}`, label, role: 'correct' as const, conversionFactor: 1, conversionOffset: 0,
          }))
        : [],
    tempConvMode:    (['absolute', 'interval'].includes(raw.tempConvMode as string) ? raw.tempConvMode as NumericalBlockData['tempConvMode'] : 'absolute'),
    angleMode:       (['degrees', 'radians'].includes(raw.angleMode as string) ? raw.angleMode as AngleMode : 'degrees'),
    angleModes: (raw.angleModes as Record<string, AngleMode> | undefined),
    showAcceptedRange: (raw.showAcceptedRange as boolean) ?? false,
    gradingMode:     (['graded', 'survey', 'optional'].includes(raw.gradingMode as string) ? raw.gradingMode as NumericalBlockData['gradingMode'] : 'graded'),
  };
  // Computed on load, so a block authored before either existed gains them the first time it is
  // read — no migration, and matching on the bare label covers the gap until then.
  return {
    ...parsed,
    ...unitIdentityFor(parsed.canonicalUnit),
    unitOptions: withUnitIdentity(parsed.unitOptions),
  };
}

// ─── Accepted-answers preview computation ────────────────────────────────────

/**
 * Whether any answer field has been typed into at all, for whichever condition type is active.
 *
 * Deliberately narrower than `hasConfiguredAnswer` (which needs the answer to RESOLVE): a formula
 * whose variables have not been rolled yet is an authored answer that simply cannot be previewed,
 * and calling that "unreachable" is the flicker-with-the-dice failure the range checks already
 * avoid. Nothing typed anywhere is a different, unambiguous state, and it is the one a freshly
 * added block is in.
 */

function computeAcceptedPreviews(
  fields: NumericalBlockData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  rolledValues: Record<string, string> = {},
  omitUnit = false,
): string[] | null {
  // Resolve a value that may be a plain number string or an expression like "{x}*5+2"
  // or "100*pi" — evaluateFormulaRaw handles both, with or without variables to roll.
  const resolve = (val: string, fieldId: string): number => {
    if (!val) return NaN;
    // The FIELD's mode. `fields.angleMode` is the block's legacy seed, consulted only when this
    // particular field has never been set — see `angleModeFor`.
    return evaluateFormulaRaw(val, rolledValues, angleModeFor(fields, fieldId));
  };
  // Display mode contributes NOTHING here. `unitLabel` is chrome drawn beside the input box; the
  // student never types it and nothing grades it, so putting it on an answer preview claims the
  // answer is "5 m/s" when the answer is 5. `withUnits` is the opposite — there the unit is
  // submitted and graded — so that branch stays.
  const unitSuffix = omitUnit ? ''
    : fields.unitMode === 'withUnits' && fields.canonicalUnit ? ` ${fields.canonicalUnit}`
    : '';
  const fmt = (n: number) => fmtLocale(n, locale, fields.sigFigsRequired ? fields.sigFigsCount : undefined, fields.requireExponentialNotation);

  /** A bound shown at the question's own precision, rounded INWARD — see `boundAtSigFigs`. */
  const bound = (n: number, dir: 'up' | 'down') =>
    fmt(fields.sigFigsRequired ? boundAtSigFigs(n, fields.sigFigsCount, dir) : n);

  // Collapse when both bounds format identically (e.g. 9.9–10.1 at 1 sig fig → both "10").
  const fmtRange = (lo: number, hi: number) => {
    const flo = bound(lo, 'up'), fhi = bound(hi, 'down');
    return flo === fhi
      ? `${flo}${unitSuffix}`
      : t('blocks.answers.acceptedRange', { low: flo, high: fhi, unit: unitSuffix });
  };

  switch (fields.conditionType) {
    case 'exact': {
      const out: string[] = [];
      for (const ans of fields.answers) {
        const v = resolve(ans.value, `${ans.id}.value`);
        if (!Number.isFinite(v)) return null;
        /* PER ANSWER, through the readers. This function was the last place still asking the
           BLOCK for its symmetry and mode, which is why the collapsed card went on drawing a
           percentage band after the grader had been corrected — the one surface a teacher sees
           without opening anything. */
        const { below, above } = answerHalfWidths(
          { toleranceValue: parseFloat(ans.toleranceValue || '0'),
            toleranceAbove: parseFloat(ans.toleranceAbove || '0'),
            toleranceBelow: parseFloat(ans.toleranceBelow || '0'),
            toleranceSymmetry: ans.toleranceSymmetry },
          fields);
        if (!Number.isFinite(above) || !Number.isFinite(below) || above < 0 || below < 0) return null;
        if (above === 0 && below === 0) { out.push(`${fmt(v)}${unitSuffix}`); continue; }
        const pct = answerToleranceMode(ans, fields, 'absolute') === 'percentage';
        const lo = pct ? v * (1 - below / 100) : v - below;
        const hi = pct ? v * (1 + above / 100) : v + above;
        out.push(fmtRange(lo, hi));
      }
      return out.length ? out : null;
    }
    case 'between': {
      const out: string[] = [];
      for (const ans of fields.betweenAnswers) {
        const lo = resolve(ans.low, ans.id), hi = resolve(ans.high, ans.id);
        if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
        out.push(fmtRange(lo, hi));
      }
      return out.length ? out : null;
    }
    case 'lt':  { const v = resolve(fields.threshold, 'threshold'); return Number.isFinite(v) ? [t('blocks.numerical.acceptedPreviewLt',  { value: bound(v, 'down'), unit: unitSuffix })] : null; }
    case 'lte': { const v = resolve(fields.threshold, 'threshold'); return Number.isFinite(v) ? [t('blocks.numerical.acceptedPreviewLte', { value: bound(v, 'down'), unit: unitSuffix })] : null; }
    case 'gt':  { const v = resolve(fields.threshold, 'threshold'); return Number.isFinite(v) ? [t('blocks.numerical.acceptedPreviewGt',  { value: bound(v, 'up'), unit: unitSuffix })] : null; }
    case 'gte': { const v = resolve(fields.threshold, 'threshold'); return Number.isFinite(v) ? [t('blocks.numerical.acceptedPreviewGte', { value: bound(v, 'up'), unit: unitSuffix })] : null; }
  }
}

// ─── Live test ───────────────────────────────────────────────────────────────
//
// `evaluateNumerical` was written in full and had ZERO callers: numerical was the only input block
// with no test surface at all, so the most intricate evaluator in the app had never once run outside
// its unit tests. Everything below is the wiring the other ten blocks already had.

/** The canonical display order for numerical's feedback states, shared by the editor panel and the
 * test-bar legend so the two can never disagree about what this block can produce. Extracted from
 * `rebuildStates`, which is where the order was originally decided; units sort before values inside
 * the targeted group, matching the panel's own section order. */
function numericalStateDisplayOrder(states: FeedbackState[]): FeedbackState[] {
  const defaults = states.filter(s => s.type === 'default');
  const targeteds = states.filter(s => s.type === 'targeted');
  const byTrigger = (trigger: string) => defaults.filter(s => s.trigger === trigger);
  const positive = defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response');
  const named = new Set(['unanswered', 'incomplete_answer', 'correct', 'any_answer', 'any_response', 'incorrect', 'unit_error', 'notation_error', 'sigfig_error']);
  return [
    ...byTrigger('unanswered'),
    ...byTrigger('incomplete_answer'),
    /**
     * Correct sits here, not down beside Default Incorrect, because that is where the panel puts
     * it: the answer first, then the sections that qualify it. The legend's row order comes from
     * this list, so the two surfaces read the same way down the page.
     *
     * OUTSIDE GRADED it moves to the end instead. There is no answer to lead with — the positive
     * slot is the CATCH-ALL there, and a catch-all belongs after the specific states it is the
     * remainder of. The legend groups by track before category, so leaving it here put "Other
     * Responses" above the value list it was supposedly other THAN.
     */
    ...(positive.some(p => p.trigger === 'correct') ? positive : []),
    // Specific units first, the catch-all after them — the order the evaluator resolves them in,
    // and the order the panel lays them out, where "Any Other Unit" sits below the Add button.
    ...targeteds.filter(s => s.trigger.startsWith('unit_')),
    ...byTrigger('unit_error'),
    // Format before value, because format is now reported BESIDE a wrong value rather than only
    // instead of a right one — and because these two are fixed, always-the-same states where the
    // value list is open-ended and ends in Default Incorrect. Everything determinate, then the
    // authored list, then the catch-all.
    ...byTrigger('notation_error'),
    ...byTrigger('sigfig_error'),
    // Math Error sits with the value it diagnoses, immediately above the value list: it is a
    // reading of WHY a number came out wrong, so it belongs beside the number, not up with the
    // structural checks. Everything unnamed lands here, which today is the seven categories.
    ...defaults.filter(s => !named.has(s.trigger)),
    ...targeteds.filter(s => !s.trigger.startsWith('unit_')),
    ...byTrigger('incorrect'),
    // The participation catch-all, last — see `positive` above.
    ...positive.filter(p => p.trigger !== 'correct'),
  ];
}

/**
 * The order the feedback panel's sections read in, named rather than positional.
 *
 * The array they come from is built out of conditional spreads, so the physical order in the source
 * is both hard to read and hard to change safely. Naming it puts the decision in one line:
 * everything DETERMINATE first — the units, then the two format states, which are single fixed
 * cards — and the open-ended authored value list after them, since it is the one that runs into
 * Default Incorrect at the foot of the panel.
 */
/** The label treatment the Answer / Uncertainty / Significant Figures / Unit rows share, so the
 *  labelled rows in this column start at the same x and read as one set. */
const NUMERICAL_ROW_LABEL = 'shrink-0 text-sm font-medium text-ws-text';

/* The order the GRADER runs in: units, then form, then the two values. `uncertainty-feedback`
   trails `value-feedback` because the two value checks are parallel and nothing else decides it —
   so it follows the Answer Setup panel above, which reads answer-first. */
const TARGETED_SECTION_ORDER = ['unit-feedback', 'notation', 'sigfig', 'math-error',
  'value-feedback', 'uncertainty-feedback'];
function orderTargetedSections<T extends { key: string }>(sections: T[]): T[] {
  return [...sections].sort((a, b) =>
    TARGETED_SECTION_ORDER.indexOf(a.key) - TARGETED_SECTION_ORDER.indexOf(b.key));
}

/**
 * Which triggers overlap another trigger, keyed by their RAW text.
 *
 * Resolved before comparing, because `398+2`, `399+1` and `400` are one condition written three
 * ways and a raw-text comparison sees three unrelated strings. Two that resolve to the SAME
 * condition overlap totally, which the pair finder cannot see once they have collapsed to one key
 * — so identical keys are reported directly rather than searched for.
 *
 * Returns raw triggers because that is what the cards downstream are identified by; only the
 * comparison happens in resolved terms.
 */
export function overlappingTriggerSetOf(
  triggers: string[], resolveTrigger: (trigger: string) => string,
): Set<string> {
  const byResolved = new Map<string, string[]>();
  for (const trigger of triggers) {
    const key = resolveTrigger(trigger);
    byResolved.set(key, [...(byResolved.get(key) ?? []), trigger]);
  }
  const overlapping = new Set(findOverlappingFeedbackPairs([...byResolved.keys()]).flat());
  const out = new Set<string>();
  for (const [resolved, raws] of byResolved) {
    if (overlapping.has(resolved) || raws.length > 1) for (const r of raws) out.add(r);
  }
  return out;
}

/**
 * Resolve a numerical trigger's values, and put a COMPUTED range the right way round.
 *
 * A teacher who types `range:200:100` made a mistake they can see and fix, and it stays an error. A
 * teacher who writes `range:{a}:{b}` has made none: which of the two comes out larger depends on
 * the roll, they cannot express an ordering constraint, and there is nothing for them to correct.
 * So a range with a computed bound is treated as INVERTIBLE — the pair names two edges, and this
 * puts the smaller first.
 *
 * The test is "was anything computed here", which `resolveTriggerValues` already answers by leaving
 * plain numbers exactly as written and rewriting only what it resolved. That is the same
 * distinction, for free.
 *
 * Numerical's own, NOT folded into the shared resolver, and the reason is compass and angle: on a
 * circle `357°` to `3°` is a perfectly ordinary range that runs through north, and "putting it the
 * right way round" would silently turn a 6° window into a 354° one. Those two pass `allowWrap` to
 * `triggerFault` instead. Inversion only means what it means on a line.
 */
export function numericalResolveTrigger(trigger: string, resolve: (value: string) => number): string {
  const resolved = resolveTriggerValues(trigger, resolve);
  if (resolved === trigger) return resolved;          // nothing computed: literals stay as authored
  const parts = resolved.split(':');
  if (parts[0] !== 'range') return resolved;
  const lo = parseFloat(parts[1] ?? ''), hi = parseFloat(parts[2] ?? '');
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo <= hi) return resolved;
  return `range:${parts[2]}:${parts[1]}`;
}

/**
 * Numerical's reading of `triggerFault`, which is the shared detector with one difference.
 *
 * The trigger is normalised FIRST. A range with a computed bound is invertible here — the teacher
 * cannot control which of `{a}` and `{b}` comes out larger — so putting it the right way round
 * before asking means `range-inverted` can only ever be reported for a range someone typed
 * backwards, which is the case they can act on. Compass and angle pass `allowWrap` instead, because
 * on a circle an inverted range is not a mistake at all.
 */
export function numericalTriggerFault(
  rawTrigger: string,
  resolveAtProbe: (value: string) => number,
  declared: Set<string>,
  probeable: Set<string>,
): TriggerFault | null {
  return triggerFault(numericalResolveTrigger(rawTrigger, resolveAtProbe),
    { resolve: resolveAtProbe, declared, probeable });
}

/** One Math Error slot: an answer value, and the equation it was arrived at by. */
interface MathErrorSlotBase {
  key: string;
  /** True when the answer field itself holds the equation, so no override is needed. */
  isEquationShaped: boolean;
  rawValue: string;
  override: string;
  storedValue: number;
  /** WHICH NUMBER this equation produces, and so which number its mutations are compared against.
   *  Not cosmetic: the two pools must never merge — see `mathErrorEquationFieldsOf`. */
  scope: 'value' | 'uncertainty';
}

/**
 * The answers Math Error works from, derived once for every surface that asks.
 *
 * The panel builds these to draw its override fields, the legend needs them to know which
 * categories can fire, and the issue bar needs them to know whether an override is wrong. Three
 * readings of one thing, so it is computed in one place.
 */
export function mathErrorSlotsOf(d: NumericalBlockData, resolve: (s: string, fieldId: string) => number): MathErrorSlotBase[] {
  if (!d.mathErrorEnabled) return [];
  if (d.conditionType === 'exact') {
    return [
      ...d.answers.map(a => ({
        key: a.id, isEquationShaped: isEquationShaped(a.value), rawValue: a.value,
        override: a.mathErrorEquation ?? '', storedValue: resolve(a.value, `${a.id}.value`),
        scope: 'value' as const,
      })),
      /* THE ERROR BOX'S OWN SLOT. Its `storedValue` is the UNCERTAINTY, so `mathErrorMismatches`
         checks the override produces the ± rather than the value — the same guard, pointed at the
         number this equation is actually for. Exact-only, which costs nothing: an error box
         forces `exact` and a single answer, so there are never more than two slots. */
      ...(uncertaintyAsked(d) ? [{
        key: 'uncertainty',
        isEquationShaped: isEquationShaped(d.uncertaintyValue ?? ''),
        rawValue: d.uncertaintyValue ?? '',
        override: d.uncertaintyMathErrorEquation ?? '',
        storedValue: resolve(d.uncertaintyValue ?? '', 'uncertaintyValue'),
        scope: 'uncertainty' as const,
      }] : []),
    ];
  }
  if (d.conditionType === 'between') {
    return d.betweenAnswers.flatMap(a => [
      { key: `${a.id}-low`, isEquationShaped: isEquationShaped(a.low), rawValue: a.low,
        override: a.mathErrorEquationLow ?? '', storedValue: resolve(a.low, a.id),
        scope: 'value' as const },
      { key: `${a.id}-high`, isEquationShaped: isEquationShaped(a.high), rawValue: a.high,
        override: a.mathErrorEquationHigh ?? '', storedValue: resolve(a.high, a.id),
        scope: 'value' as const },
    ]);
  }
  return [];
}

/** The uncertainty's equation: its own field where that holds one, the override where it does not.
 *  Null when there is nothing to mutate, which is what `numericalEvaluatorData` omits on. */
export function uncertaintyMathErrorEquationOf(slots: MathErrorSlotBase[]): string | null {
  const slot = slots.find(x => x.scope === 'uncertainty');
  if (!slot) return null;
  const eq = (slot.isEquationShaped ? slot.rawValue : slot.override).trim();
  return eq === '' ? null : eq;
}

/** The equations actually fed to the mutation engine: the answer itself where it is one, the
 *  teacher's override where it is not. */
/**
 * Each math-error equation WITH the field it came from, so its angle mode can be looked up.
 *
 * Two different fields feed this list: an equation-shaped answer is the answer's own field, an
 * override is the override's. They can be in different modes, so the pairing has to survive the
 * filter — which is why this returns pairs and `mathErrorEquationsOf` is derived from it rather
 * than the two deriving the same list separately. They did, in two places, and one of them was
 * the one still reading the block-wide mode.
 */
export function mathErrorEquationFieldsOf(slots: MathErrorSlotBase[]): { equation: string; fieldId: string }[] {
  return slots
    /* VALUE SCOPE ONLY. Both callers feed `collectMathErrorCandidates`, whose candidates are
       compared against the student's VALUE. The uncertainty's equation reaches the grader by its
       own route and is compared against their ±; letting it in here would merge the two pools and
       fire a propagation's mutation on a value. The filter is here rather than at the call sites
       because here there is exactly one of it. */
    .filter(s => s.scope === 'value')
    .map(s => ({
      equation: s.isEquationShaped ? s.rawValue : s.override.trim(),
      fieldId: s.isEquationShaped ? `${s.key}.value` : `mathError.${s.key}`,
    }))
    .filter(x => x.equation !== '');
}

export function mathErrorEquationsOf(slots: MathErrorSlotBase[]): string[] {
  return mathErrorEquationFieldsOf(slots).map(x => x.equation);
}

/**
 * The overrides that do not produce the answer they are attached to.
 *
 * A silent one is worse than none: Math Error mutates the equation it is given, so an override that
 * evaluates to something else generates its mutations from the wrong expression and diagnoses
 * mistakes the student never made.
 */
export function mathErrorMismatches(slots: MathErrorSlotBase[], resolve: (s: string, fieldId: string) => number): MathErrorSlotBase[] {
  return slots.filter(s => {
    const typed = s.override.trim();
    if (!typed) return false;
    const v = resolve(typed, `mathError.${s.key}`);
    return !Number.isFinite(v) || !valuesMatchWithinEpsilon(v, s.storedValue);
  });
}

/**
 * A trigger with its ARITHMETIC worked out, and its variables left alone.
 *
 * `398+2` is not a pending value — there is nothing to wait for and no scenario that changes it —
 * so a chip reading `= 398+2` asks the reader to do the sum before they can see when the state
 * fires. It also hid a real problem: three triggers written `398+2`, `399+1` and `400` are one
 * condition three times, and only resolving them makes that visible at a glance.
 *
 * Variables are NOT resolved here, and that is the locked three-mode rule: a display surface shows
 * `{test}` as a chip until a test scenario is picked. Returning NaN for anything holding a chip
 * leaves that part exactly as written, which is what `resolveTriggerValues` does with a value it
 * cannot turn into a number.
 */
export function triggerWithArithmeticResolved(trigger: string, angleMode: AngleMode): string {
  return resolveTriggerValues(trigger, val =>
    hasVariableChip(val) ? NaN : shownPrecision(evaluateFormulaRaw(val, {}, angleMode)));
}

/**
 * Six significant figures, the default `fmtLocale` prints every other number in this block at.
 *
 * `resolveTriggerValues` stringifies with `String(n)` — right for the evaluator, which wants the
 * whole double, and wrong for a chip: `1/3` arrived as sixteen digits and `1/{test}` at test=400 as
 * 0.0025000000000000005.
 *
 * ROUNDED, not formatted. The result goes back into a trigger string, and a locale's thousands
 * separator inside one would not survive being parsed back out.
 */
function shownPrecision(n: number): number {
  return Number.isFinite(n) ? parseFloat(n.toPrecision(6)) : n;
}

/**
 * The chip for a value trigger: the number it fires on, and — for a LITERAL equation — the
 * arithmetic that produced it.
 *
 * `= 400 (398+2)` rather than `= 398+2`. The value is what the state actually tests, so it leads;
 * the parenthetical earns its place because three triggers written `398+2`, `399+1` and `400` are
 * one condition three times, and without it a teacher looking at three chips all reading `= 400`
 * cannot tell which is which. Only shown where the two differ — a trigger typed as a plain number
 * has no second thing to say.
 *
 * A VARIABLE gets no parenthetical, and the asymmetry is deliberate. The two cases look alike and
 * are not: `398+2` is one fixed condition dressed up, so the dressing has to be shown to tell it
 * apart from its neighbours, while `{test}/log(2)` produces a different number every roll and its
 * text is already stated — unrolled, as chips — on the very same panel whenever no scenario is
 * picked. Repeating it beside the resolved value adds nothing there and crowds a row that is
 * already dense.
 *
 * So there are exactly two readings, and which one shows is the scenario:
 *   - no scenario (or dismissed): the condition as authored, variables as chips;
 *   - Min / Max / Random: the resolved number, alone.
 */
export function numericalTriggerChipContent(
  trigger: string, angleMode: AngleMode, scenario: Record<string, string>,
  t: (k: string, o?: Record<string, unknown>) => string,
): React.ReactNode {
  return resolvedTriggerChip({
    trigger, scenario,
    summaryOf: resolved => numericalTriggerSummary(resolved, t),
    evaluate: (expr, values) => evaluateFormulaRaw(expr, values, angleMode),
    valuesOf: triggerValuesOf,
    hasChip: hasVariableChip,
    // Numerical's own resolver, because a range with a COMPUTED bound is invertible here — see
    // `numericalResolveTrigger`. Every other block passes the plain one.
    resolveTrigger: numericalResolveTrigger,
  });
}

/**
 * Can this state fire at all, given how the block is configured?
 *
 * ONE predicate, because this list of conditions existed twice: as a chain of `break`s in the test
 * legend's loop and as a scatter of JSX gates in the feedback panel. Three of this file's bugs were
 * one of those two copies falling behind the other — a `cm` chip in the legend for a unit flagged
 * correct, a deactivated Math Error category still shown as a live rule, a whole UNITS row after
 * units were switched off. Every one of them was a state the panel had already learned to hide.
 *
 * A state that fails this is not deleted, only hidden: every one of these settings is a toggle a
 * teacher may toggle back, and the authored feedback should be waiting when they do.
 */
export function numericalStateIsLive(st: FeedbackState, d: NumericalBlockData): boolean {
  if (st.type === 'targeted') {
    /**
     * Set Answer exists only OUTSIDE graded — see `targetedFiresIn`. In graded the accepted band
     * IS `correct`, so the preset would be a second name for a card the block already has.
     */
    if (st.trigger === SET_ANSWER_TRIGGER) return d.gradingMode === 'survey';
    /*
     * A unit trigger is about the UNIT, not the value.
     *
     * It read as a correctness concept — "you used the wrong unit presupposes a right one" — and
     * was graded-only on that basis. It is not: a unit option's role is authored directly, so
     * "this question takes joules, not calories" needs no correct number, and
     * `evaluateNumerical` fires these in participation now alongside `unit_error`. Dead in
     * OPTIONAL, which is inert, and dead with units switched off.
     */
    if (st.trigger.startsWith('unit_')) {
      return d.gradingMode !== 'optional' && d.unitMode === 'withUnits'
        && !isUnreachableUnitTargeted(st.trigger, d);
    }
    /* A TRAP ON A BOX THAT IS NO LONGER THERE. Switching the uncertainty off leaves its targeted
       states in the data — deliberately, so switching back restores them — and a bare `return
       true` kept drawing chips and cards for conditions nothing could ever test. Graded only, for
       the reason the section is: `evaluateNumerical` returns at the survey branch long before the
       uncertainty is judged. */
    if (st.trigger.startsWith(UNCERTAINTY_TRIGGER_PREFIX)) {
      return d.gradingMode === 'graded' && uncertaintyAsked(d);
    }
    return true;
  }
  const graded = d.gradingMode === 'graded';
  switch (st.trigger) {
    /**
     * Both of these are dead in OPTIONAL, and not by judgement — `evaluateNumerical`'s optional
     * branch returns ABOVE the blank check and the partial check, so neither can fire. Leaving a
     * numerical blank there is a complete submission, the same reading every other block gives
     * it; the editor was drawing two cards nothing could reach.
     */
    case 'unanswered':        return d.gradingMode !== 'optional';
    /* `evaluateNumerical` fires this where the submission is PART of an answer, which needs more
       than one input to be part of. Units gave the second one; the error box gives another, and a
       blank value beside a filled uncertainty is as partial as a value with no unit. Gated on
       either, because with neither an empty answer really is just Unanswered. */
    case 'incomplete_answer':
      return d.gradingMode !== 'optional' && (d.unitMode === 'withUnits' || uncertaintyAsked(d));
    case 'any_answer':        return d.gradingMode === 'survey';
    case 'any_response':      return d.gradingMode === 'optional';
    case 'correct':
    case 'incorrect':         return graded;
    /*
     * FORM, not correctness — so these live outside graded too.
     *
     * Whether the string is in scientific notation, how many significant figures it carries and
     * whether the unit is one the question takes are all answerable with no correct answer at
     * all. They were `graded &&` because `evaluateNumerical` returned at its survey branch long
     * before reaching them; it resolves the unit and reads the format for both modes now.
     *
     * The requirement is the teacher's opt-in and it belongs to the QUESTION — "to 4 sig figs,
     * in joules" — rather than to the grading, so switching the verdict off does not switch the
     * ask off. A teacher who wants neither turns the requirement itself off, which kills these
     * in every mode through the same flags below.
     */
    case 'unit_error':        return d.gradingMode !== 'optional' && d.unitMode === 'withUnits' && !!d.canonicalUnit;
    case 'notation_error':    return d.gradingMode !== 'optional' && d.requireExponentialNotation;
    case 'sigfig_error':      return d.gradingMode !== 'optional' && d.sigFigsRequired;
    /*
     * THE ERROR BOX. Integers-only takes it off the question entirely, so these go with it —
     * `evaluateNumerical` reads the same pair and would fire none of them.
     *
     * Agreement and the rounding fault are FORM, true of the two strings alone, so they live
     * outside graded exactly as sig figs and notation do. The two that need a correct answer —
     * the general fault and the fraction — are graded-only, same line Math Error sits on.
     */
    case 'uncertainty_sigfig_error':
    case 'agreement_error':
      return d.gradingMode !== 'optional' && uncertaintyAsked(d);
    default:
      if (st.trigger.startsWith('math_error_')) {
        const cat = st.trigger.slice('math_error_'.length) as MathErrorCategory;
        // Graded, like every case above it. `evaluateNumerical` returns at the survey branch
        // long before the math-error block, so outside graded these cannot fire and the editor
        // was drawing a card per enabled category that nothing could reach.
        return graded && d.mathErrorEnabled && !!d.mathErrorCategories[cat];
      }
      return true;
  }
}

/**
 * THREE BOXES IN THE ANSWER ROW — value, uncertainty and unit — which is the layout's hard case.
 *
 * Measured at 513px. The row's container query already knows that (`@max-[33rem]`), and the cap
 * it sits inside does not: `AnswerDisplayFrame`'s standard column is `md:max-w-md`, 448px. 448 is
 * always under 528, so on the one surface wrapped by that frame the query was permanently true
 * and the horizontal layout was unreachable — three boxes stacked, with `pb-5`'s reserved hint
 * line overlapping the box beneath, at every window size.
 *
 * So the predicate is shared rather than spelled out twice: the row uses it to pick its
 * breakpoint, and the frame uses it to decide whether the row is allowed the width that
 * breakpoint is asking for. Two reads of one fact that must not drift apart.
 */
function answerRowNeedsWidth(
  d: Pick<NumericalBlockData, 'uncertaintyRequired' | 'integerOnly' | 'conditionType' | 'unitMode'>,
): boolean {
  return uncertaintyAsked(d) && d.unitMode === 'withUnits';
}

/**
 * Can this unit-targeted state ever fire?
 *
 * Two ways it cannot. Its unit is marked CORRECT, so evaluation converts and proceeds and never
 * reaches the targeted lookup; or its unit is gone, leaving a trigger pointing at nothing. Neither
 * is deleted when it happens — a role is a cycle a teacher may cycle back — so both linger.
 *
 * ONE predicate for both surfaces. The panel already hid these and the test bar did not, so a
 * legend advertised "Selected unit: cm" for a unit flagged correct, and "Selected unit: ?" for one
 * that had been deleted: feedback promised for answers that can never reach it.
 */
export function isUnreachableUnitTargeted(
  trigger: string, d: Pick<NumericalBlockData, 'unitMode' | 'unitOptions'>,
): boolean {
  if (!trigger.startsWith('unit_')) return false;
  // UNITS SWITCHED OFF is the broadest way for one of these to die, and the quietest: the whole
  // library is kept rather than cleared, so the option is still there with its role intact and
  // nothing looks wrong. Evaluation gates its entire unit branch on `withUnits`, so no unit_X can
  // fire — and the panel already hid the section while the legend went on advertising it.
  if (d.unitMode !== 'withUnits') return true;
  const unit = d.unitOptions.find(u => u.id === trigger.slice('unit_'.length));
  return !unit || unit.role === 'correct';
}

/** Authoring shape → evaluator shape. The editor holds raw formula text ("{x}*5+2"); the evaluator
 * wants resolved numbers, so every value goes through `resolve`. Same bridge Slider's own
 * `evaluatorDataFrom` provides, for the same reason. */
export function numericalEvaluatorData(
  d: NumericalBlockData,
  feedbackStates: FeedbackState[],
  resolve: (s: string, fieldId: string) => number,
): BlocksNumericalBlockData {
  return {
    question: d.question,
    conditionType: d.conditionType,
    answers: d.answers.map(a => ({
      id: a.id,
      value: resolve(a.value, `${a.id}.value`),
      toleranceValue: parseFloat(a.toleranceValue || '0'),
      toleranceAbove: parseFloat(a.toleranceAbove || '0'),
      toleranceBelow: parseFloat(a.toleranceBelow || '0'),
      /* CARRIED, NOT DEFAULTED. `undefined` means "fall back to the block", so dropping these
         forces every answer onto that fallback — and the block-level pair has not been written
         since tolerances moved per answer, leaving it on the `percentage` the parser defaults to.
         A teacher switching one answer to absolute then had the toggle change some chips and
         nothing else. See `answerToleranceMode` / `answerSymmetry`. */
      toleranceSymmetry: a.toleranceSymmetry,
      toleranceMode: a.toleranceMode,
      // Only Math Error's candidate generation reads this, and only when the slot was authored as
      // an equation — a bare number has nothing equation-shaped to hand it.
      ...(hasVariableChip(a.value) ? { mathErrorEquation: a.value } : {}),
    })),
    toleranceMode: d.toleranceMode,
    toleranceSymmetry: d.toleranceSymmetry,
    betweenAnswers: d.betweenAnswers.map(a => ({ id: a.id, low: resolve(a.low, a.id), high: resolve(a.high, a.id) })),
    threshold: resolve(d.threshold, 'threshold'),
    sigFigsRequired: d.sigFigsRequired,
    sigFigsCount: d.sigFigsCount,
    /* FORWARDED, and it was not. `evaluateNumerical` reads this to decide whether an uncertainty
       is being asked for at all — integers-only forbids the decimal every ordinary one needs — so
       without it a block that greys the error box away in the editor still graded against it. */
    integerOnly: d.integerOnly,
    uncertaintyRequired: d.uncertaintyRequired,
    uncertaintyValue: d.uncertaintyValue ? resolve(d.uncertaintyValue, 'uncertaintyValue') : undefined,
    uncertaintyTolerance: parseFloat(d.uncertaintyTolerance || '0'),
    uncertaintyToleranceAbove: parseFloat(d.uncertaintyToleranceAbove || '0'),
    uncertaintyToleranceBelow: parseFloat(d.uncertaintyToleranceBelow || '0'),
    uncertaintyToleranceMode: d.uncertaintyToleranceMode,
    uncertaintyToleranceSymmetry: d.uncertaintyToleranceSymmetry,
    uncertaintySigFigs: d.uncertaintySigFigs,
    /* Math Error mutates the EXPRESSION, so it needs the text — and only when there is something
       equation-shaped to hand it, exactly as an answer's own override works.

       `isEquationShaped`, which is the test the ANSWER uses, and not `hasVariableChip`, which was
       the test here and is narrower. `sqrt(0.1^2+0.05^2)` is an expression with mutations worth
       generating and no chip in it, so the whole tier was silently off for every uncertainty key
       written as arithmetic over literals. The comment claimed parity with the answer while the
       code did something else. */
    /* Its own field where that holds an expression, the teacher's override where it holds a bare
       number — the same two ways the answer gets its equation, now through the same slot list. */
    ...(() => {
      const eq = uncertaintyMathErrorEquationOf(mathErrorSlotsOf(d, resolve));
      return eq ? { uncertaintyEquation: eq } : {};
    })(),
    requireExponentialNotation: d.requireExponentialNotation,
    mathErrorEnabled: d.mathErrorEnabled,
    mathErrorCategories: d.mathErrorCategories,
    angleMode: d.angleMode,
    // The per-field map travels WITH the data: Math Error mutates each answer equation, and
    // each answer is a field with its own mode.
    angleModes: d.angleModes,
    unitMode: d.unitMode,
    canonicalUnit: d.canonicalUnit,
    // Without this the evaluator has only the canonical's bare string to match — and says nothing,
    // because that fallback exists so a block saved before spellings still grades. A dropped list
    // is therefore invisible rather than loud, which is why the field is a REQUIRED property on
    // NumericalBlockData: omitting it here has to be a type error.
    canonicalSpellings: d.canonicalSpellings,
    unitOptions: d.unitOptions,
    gradingMode: d.gradingMode,
    // `numericalResolveTrigger`, NOT the shared `resolveFeedbackTriggers`. Both do the same
    // formula resolution — a trigger can hold `{x}*5` and the evaluator reads it with parseFloat,
    // so that step has to happen somewhere — but only the numerical one also puts a COMPUTED range
    // the right way round. Using the shared version here meant the editor normalised an inverted
    // `range:{a}:{b}` for its analysis and its chips while the grader went on reading it backwards
    // and accepting nothing: the panel showing a healthy condition the student could never trigger.
    feedback: {
      states: feedbackStates.map(s =>
        s.type === 'targeted'
          ? { ...s, trigger: numericalResolveTrigger(s.trigger, v => resolve(v, `feedback.${s.id}.trigger`)) }
          : s),
    },
  };
}

interface NumericalTestData {
  testResult: FeedbackState[] | null;
  testChips: TestBarChip[];
  notices: BlockIssue[];
}

const numericalTestDataCache: IssueCache<NumericalTestData> = new WeakMap();
/**
 * A SECOND cache, for the draft.
 *
 * Preview's chips describe the SUBMITTED answer -- that is what the student has been told about.
 * Behind the Scenes wants "what fires if they press Submit now", and the two part company the
 * moment someone edits after submitting. Keyed on the three boxes, so it costs one derivation
 * per distinct answer tried rather than one per keystroke.
 */
const numericalDraftChipsCache: IssueCache<NumericalTestData> = new WeakMap();

/** The two evaluation tracks numerical grades on. `TestBarChip.track` exists for exactly this case
 * — its own doc comment names it — and this is its first consumer: a unit chip and a value chip in
 * one flat row would imply an ordering between them that the evaluator does not have. */
/**
 * One row per named group in the legend, matching the feedback panel's own sections.
 *
 * A track is what splits the legend into rows, so a group that the panel gives a heading to needs
 * a track of its own to get one here. Rows appear in the order their first chip is pushed, which
 * is `numericalStateDisplayOrder` — so the legend and the panel read top-to-bottom the same way,
 * and neither has to be kept in step with the other by hand.
 */
/** The survey bar's sentence, narrowed when the setup intercepts answers — see
 *  `collapsedModeBar`'s own note. */
function numericalSurveyBarKey(d: NumericalBlockData): string | undefined {
  return numericalHasFormatGate(d) ? 'barSurveyFormatted' : undefined;
}

const TRACK_VALUE = 'value';
const TRACK_UNIT = 'unit';
/** The error box's targeted traps. Its own row because the panel gives it its own heading — see
 *  the legend's note above: tracks and panel sections are the same list seen twice. */
const TRACK_UNCERTAINTY = 'uncertainty';
/** Math Error's own row. One shared set of cards fed by two equations belongs to neither number. */
const TRACK_MATH_ERROR = 'math-error';
// ONE row for both format states, because each can only ever contribute a single chip and two
// rows of one chip is two lines of legend for one idea. The panel keeps them apart — there a
// section is a place to WRITE, and they are written separately — but the legend is read, and a
// reader wants the shape of the answer in as few lines as the block can honestly give.
const TRACK_FORMAT = 'format';
// Correct and Default Incorrect get rows of their own rather than sharing the value track, so each
// lands where the panel puts it: the answer near the top, the catch-all at the very bottom. Sharing
// a track pulled Default Incorrect up beside Correct, three rows above the sections it follows.
const TRACK_CORRECT = 'correct';
const TRACK_INCORRECT = 'incorrect';

function computeNumericalTestData(
  d: NumericalBlockData,
  feedbackStates: FeedbackState[],
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  submitted: NumericalAnswer | null,
  rolledValues: Record<string, string>,
  issues: BlockIssue[],
): NumericalTestData {
  const resolve = (val: string, fieldId: string): number =>
    (val ? evaluateFormulaRaw(val, rolledValues, angleModeFor(d, fieldId)) : NaN);
  /**
   * The submitted number, keyed by the unit it was submitted IN — the shape `activeIntervalIds`
   * matches bands against, so the legend lights the one band a submission actually landed in.
   *
   * Keyed by unit because a block with alternative units publishes one band per unit for the same
   * answer; comparing a bare number against all of them would light the wrong one. A block without
   * units keys on '', which is what its bands carry.
   */
  const submittedByUnit: Record<string, number | null> = (() => {
    if (!submitted) return {};
    const n = parseFloat(submitted.value);
    if (!Number.isFinite(n)) return {};
    const key = d.unitMode === 'withUnits' ? (submitted.unit?.trim() || '') : '';
    return { [key]: n };
  })();
  // Which Math Error categories can actually fire, against this surface's own roll — the same
  // question the panel answers for its scenario, asked here for the legend's.
  const mathErrorFields = mathErrorEquationFieldsOf(mathErrorSlotsOf(d, resolve));
  const mathErrorCandidates = d.mathErrorEnabled
    ? collectMathErrorCandidates(
        mathErrorFields.map(x => x.equation),
        i => angleModeFor(d, mathErrorFields[i]?.fieldId ?? ''),
        rolledValues,
        numericalRangeConfig(d, rolledValues))
    : null;
  const testResult = submitted
    ? evaluateNumerical(submitted, numericalEvaluatorData(d, feedbackStates, resolve), rolledValues)
    : null;
  const firedIds = new Set((testResult ?? []).map(s => s.id));
  const chips: TestBarChip[] = [];

  // The legend is a permanent setup overview, so every state this block can produce gets a chip
  // whether or not it fired — dimmed until it does. Order comes from the shared display order, so
  // the legend reads the same way the feedback panel does.
  // Value triggers only: unit triggers are graded-only and already dead outside it.
  //
  // Never in OPTIONAL, which fires no targeted state at all — see `catchAllChips`, which carries
  // the same guard for the blocks that go through it. Without this the stored targets, inert as
  // they are, flipped Any Response to neutral on the bar while the panel kept its tick.
  const anyValueTargeted = d.gradingMode !== 'optional' && feedbackStates.some(
    x => x.type === 'targeted' && !x.trigger.startsWith('unit_') && numericalStateIsLive(x, d));
  /*
   * ANYTHING ELSE that can intercept a submission, which is what the catch-all's own name turns
   * on — "Any Answer" is only the whole story while nothing else can fire.
   *
   * Targeted states were the only thing counted, which was the whole story until the format
   * gates started firing outside graded. With scientific notation required, a block said "Any
   * Answer" while rejecting `3` in front of the teacher.
   *
   * It takes no new wording. "Other" already means "not caught above", so it absorbs one gate or
   * five without growing — which is why it beats naming them ("any answer, as long as it is in
   * scientific notation, to 4 significant figures, in joules") and why it composes with the
   * targeted case rather than competing with it. The row label is already right: `anyTargeted`
   * in `groupChipsByTrack` is computed across every track, and these chips are `targeted`.
   */
  const anyOtherCatch = anyValueTargeted || numericalHasFormatGate(d);
  for (const st of numericalStateDisplayOrder(feedbackStates)) {
    // The ONE liveness rule, shared with the feedback panel. Each `case` below used to repeat its
    // own half of it, which is how the legend came to advertise three different states the panel
    // had already hidden.
    if (!numericalStateIsLive(st, d)) continue;
    const active = firedIds.has(st.id);
    // Neutral outside graded: the stored evaluation mirrors a role the mode does not consult.
    const base = { id: st.id, evaluation: displayEvaluation(d.gradingMode, st.evaluation), active };

    if (st.type === 'targeted') {
      /*
       * Targeted states skip the can-fire filter above — it switches on the DEFAULT triggers — so
       * the one mode-wide rule has to be applied here instead. Optional is inert and fires none of
       * them, which left the legend listing rows nothing could reach while the panel beside it had
       * already dropped the cards.
       */
      if (!targetedFiresIn(st.trigger, d.gradingMode)) continue;
      /*
       * SET ANSWER names no value of its own — it IS the accepted band — so the generic value
       * derivation had nothing to parse and printed the bar's operator with an empty operand:
       * "= ?". The same shape as Tilt's "<?°", and the same fix.
       *
       * Fanned out through `numericalCorrectChips`, which is the call the panel's own preset card
       * makes, so the bar and the card state one answer in one wording. `neutralChips` because a
       * preset is a target rather than a verdict, exactly as the panel has it.
       */
      if (st.trigger === SET_ANSWER_TRIGGER) {
        const presetChips = neutralChips(numericalCorrectChips(d, rolledValues, t, locale));
        const row = toTestBarChips(presetChips, {
          id: st.id, category: 'targeted', active, track: TRACK_VALUE,
          activeSourceIds: activeIntervalIds(presetChips, submittedByUnit),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        });
        // The row names itself from its first chip, as the fanned-out correct row does.
        if (row[0]) row[0] = { ...row[0], categoryLabel: t('blocks.numerical.testBarRowValue') };
        chips.push(...row);
        continue;
      }
      /* THREE WAYS. A bare `isUnit` ternary sent the error box's traps to the value row, under a
         heading naming the other number. */
      const isUnit = st.trigger.startsWith('unit_');
      const isUnc  = st.trigger.startsWith(UNCERTAINTY_TRIGGER_PREFIX);
      /* The bare trigger, because `numericalTriggerChipContent` parses one and
         `uncertainty_exact:0.38` is not — the same unwrapping the panel's editor does. */
      const chipTrigger = isUnc ? st.trigger.slice(UNCERTAINTY_TRIGGER_PREFIX.length) : st.trigger;
      chips.push({
        ...base,
        category: 'targeted',
        track: isUnit ? TRACK_UNIT : isUnc ? TRACK_UNCERTAINTY : TRACK_VALUE,
        categoryLabel: t(isUnit ? 'blocks.numerical.testBarRowUnits'
          : isUnc ? 'blocks.numerical.testBarRowUncertainty'
          : 'blocks.numerical.testBarRowValue'),
        // Resolved, like every legend chip: this is the testing panel, and its whole job is showing
        // what the variables came out as. A unit trigger names a unit, so it has nothing to resolve.
        condition: isUnit
          ? numericalUnitTargetedTitle(st.trigger, d.unitOptions, t)
          : numericalTriggerChipContent(chipTrigger, angleModeFor(d, `feedback.${st.id}.trigger`), rolledValues, t),
      });
      continue;
    }

    switch (st.trigger) {
      case 'unanswered':
        chips.push({ ...base, category: 'unanswered', condition: t('blocks.feedbackDefaults.noAnswerTitle') });
        break;
      case 'incomplete_answer':
        // Only reachable with a unit box beside the value: `evaluateNumerical` fires this on
        // `requiresUnit && hasValue !== hasUnit`, so with one input there is no partial state to be
        // in — empty is Unanswered. The feedback panel already gates its card the same way; the
        // legend was the one surface advertising a pathway the evaluator cannot take.
        chips.push({ ...base, category: 'incomplete', condition: t('blocks.feedbackDefaults.incompleteAnswerTitle') });
        break;
      // Renamed and de-ticked once a value is targeted, like the panel's own card — the row
      // label said "Other Responses" while the chip beside it still said "Any Response".
      /**
       * On the VALUE track, which is what actually orders this row.
       *
       * The legend groups by TRACK before category, so an untracked catch-all rendered as its own
       * group above the value lane — "Other Answers" sitting above the very list it is other
       * THAN. Putting it in the lane lets `CATEGORY_ORDER` do its job and the targeted values
       * come first. Graded is untouched: its catch-all is `incorrect`, which stays untracked.
       */
      case 'any_answer':
        chips.push({ ...base, category: 'any_answer', track: TRACK_VALUE,
          categoryLabel: undefined,
          evaluation: anyValueTargeted ? 'neutral' : base.evaluation,
          condition: catchAllTitle(d.gradingMode, anyOtherCatch, t) ?? t('blocks.feedbackDefaults.anyAnswerTitle') });
        break;
      case 'any_response':
        chips.push({ ...base, category: 'any_response', track: TRACK_VALUE,
          categoryLabel: undefined,
          evaluation: anyValueTargeted ? 'neutral' : base.evaluation,
          condition: catchAllTitle(d.gradingMode, anyOtherCatch, t) ?? t('blocks.feedbackDefaults.anyResponseTitle') });
        break;
      case 'correct': {
        // The condition comes from `numericalCorrectChips`, the same call the feedback panel makes.
        // The bar used to join the previews into one string with its own preview builder; now it
        // fans out exactly as the panel does, with "or" spelled between the accepted values, so a
        // block with alternative units cannot read one way here and another there.
        const correctChips = numericalCorrectChips(d, rolledValues, t, locale);
        chips.push(...toTestBarChips(correctChips, {
          id: st.id, category: 'correct', active, track: TRACK_CORRECT,
          // Only the band the submission landed in — this row used to light in full, so a teacher
          // with two accepted answers could not see which one they had just matched.
          activeSourceIds: activeIntervalIds(correctChips, submittedByUnit),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
        break;
      }
      case 'incorrect': {
        // Was a flat "Any other value". True, but it described the state differently from the
        // feedback panel, which has always named the gaps either side of the accepted band. Same
        // call now, so the two cannot disagree.
        const incorrectChips = numericalIncorrectChips(d, feedbackStates, rolledValues, t, locale);
        chips.push(...toTestBarChips(incorrectChips, {
          activeSourceIds: activeIntervalIds(incorrectChips, submittedByUnit),
          id: st.id, category: 'incorrect', active, track: TRACK_INCORRECT,
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
        break;
      }
      case 'unit_error':
        // Only where a unit can be wrong at all. The catch-all beneath the per-unit cards: any
        // unit this question does not accept, listed or not, which is most of them.
        chips.push({
          ...base, category: 'targeted', track: TRACK_UNIT,
          categoryLabel: t('blocks.numerical.testBarRowUnits'),
          condition: t('blocks.numerical.feedbackTriggerAnyOtherUnit'),
        });
        break;
      case 'notation_error':
        chips.push({
          ...base, category: 'targeted', track: TRACK_FORMAT,
          categoryLabel: t('blocks.numerical.testBarRowFormat'),
          // The PANEL's string, not a second one of its own. This chip shipped as "scientific
          // notation", which is not just a different wording from the card's "Not in Scientific
          // Notation" -- it reads as though the state fires for an answer that IS in scientific
          // notation, the exact opposite of when it fires.
          condition: t('blocks.numerical.feedbackTriggerNotation'),
        });
        break;
      /* THE ERROR BOX'S FORM FAULTS, which fired and lit nothing. Every other default state has a
         case here; these two were added to the evaluator and the panel and missed in the legend,
         so a teacher testing a mis-written pair saw the verdict change with no chip to explain it.
         Their own row, matching the panel's Answer Form Feedback section. */
      case 'uncertainty_sigfig_error':
        chips.push({
          ...base, category: 'targeted', track: TRACK_FORMAT,
          categoryLabel: t('blocks.numerical.testBarRowAnswerForm'),
          condition: t('blocks.numerical.feedbackTriggerUncertaintyFigures',
            { count: d.uncertaintySigFigs ?? 1 }),
        });
        break;
      case 'agreement_error':
        chips.push({
          ...base, category: 'targeted', track: TRACK_FORMAT,
          categoryLabel: t('blocks.numerical.testBarRowAnswerForm'),
          condition: t('blocks.numerical.feedbackTriggerAgreement'),
        });
        break;
      case 'sigfig_error':
        chips.push({
          ...base, category: 'targeted', track: TRACK_FORMAT,
          categoryLabel: t('blocks.numerical.testBarRowFormat'),
          // The PANEL's string, with the count the bar used to carry on its own. One key,
          // pluralised, so "Not 1 Significant Figure" reads correctly at the stepper's minimum.
          condition: t('blocks.numerical.feedbackTriggerSigFig', { count: d.sigFigsCount }),
        });
        break;
      default:
        // Math Error categories — only reachable while the feature is on, and each names the
        // mistake it diagnoses rather than a value, so the stored title is already the right chip.
        if (st.trigger.startsWith('math_error_')) {
          const cat = st.trigger.slice('math_error_'.length) as MathErrorCategory;
          // A DEACTIVATED category is not a state this block can produce, so it has no chip at all.
          // It used to keep one, frozen at whatever evaluation it carried when it was switched off,
          // which read as a live rule set to Incorrect.
          chips.push({
            ...base, category: 'targeted', track: TRACK_MATH_ERROR,
            // Enabled but with nothing to catch — no mutation of the teacher's equation lands on
            // this category — reads the same here as it does in the panel beside it.
            reachable: (mathErrorCandidates?.[cat]?.length ?? 0) > 0,
            // Named, the way FITB names its dead blank. A bare "Unreachable" among six live chips
            // says something is wrong and not which of them, which is the half that matters.
            unreachableLabel: `${st.title ?? st.trigger}: ${t('blocks.feedbackPanel.unreachable')}`,
            /* ITS OWN ROW. These used to be labelled like the value chips they sat with, which
               was right while each number had its own set of cards. One shared card fed by both
               equations cannot sit in a row named for one of them — the row would be saying where
               the mistake was, which is the one thing a shared card does not know. */
            categoryLabel: t('blocks.numerical.testBarRowMathError'),
            condition: st.title ?? st.trigger,
          });
        }
        break;
    }
  }

  return { testResult, testChips: chips, notices: issues };
}

/**
 * Every issue this block can report, from data alone.
 *
 * ONE derivation, called by the editor's warning bar and by the card's test-bar notices. The flags
 * `buildNumericalIssues` takes used to be assembled inline at the editor's single call site, which
 * is why the card had no issues panel at all: there was no way to compute them without the
 * editor's local state. Everything here is derivable from persisted data, so both surfaces can ask
 * the same question and get the same answer — the property `IssuesPanel` exists to guarantee and
 * that Slider's card once broke by recomputing its own conditions.
 *
 * It takes no ROLL, and that is load-bearing rather than tidy. It used to take one, and the two
 * surfaces passed different rolls — so the same block could report an overlap on the card and not
 * in the editor, or report one on Monday and not on Tuesday, because the editor seeds its roll at
 * random on mount. Validation asks two kinds of question and neither wants a live roll: "is this
 * well formed", which resolves against a deterministic `probeRoll` (any member of the domain
 * answers it), and "do these collide", which quantifies over the whole domain. A sample answers
 * neither.
 */
/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * The feedback states come off the raw block because `NumericalBlockData` genuinely has no
 * `feedback`: this block keeps them outside its own data type, so the editor hands them in from
 * React state and everyone else reads the stored copy.
 */
export function numericalBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  const feedbackStates = (block.data as { feedback?: { states?: FeedbackState[] } } | undefined)
    ?.feedback?.states ?? [];
  return numericalIssues(initData(block.data), { ...ctx, feedbackStates });
}

export function numericalIssues(d: NumericalBlockData, ctx: IssueContext): BlockIssue[] {
  const { t, variables, locale } = ctx;
  /* `NumericalBlockData` genuinely has no `feedback` — this block keeps its states outside its own
     data type — so there is nothing to fall back TO. A caller that wants the feedback checks has to
     supply them; the board reads them off the raw stored block. Empty means those checks pass, which
     is the same thing they do for a block with no feedback authored. */
  const feedbackStates: FeedbackState[] = ctx.feedbackStates ?? [];
  // One assignment out of the domain, the same one every time. Enough to answer whether a formula
  // is arithmetic at all; never enough to answer what it comes out as, which is why nothing below
  // compares it against anything.
  /*
   * ...AND A STAND-IN FOR EVERY RECALLED ANSWER THE BLOCK QUOTES.
   *
   * Scanned off the serialised data rather than field by field, because a recall can sit in an
   * answer, a range bound, a threshold or a feedback trigger, and a probe missing one makes
   * that field look like it is not arithmetic. It reported `2*[ara_...]` first as "not a usable
   * number" and then, once the field check was fixed, as "Numerical setup incomplete" — the
   * same gap surfacing twice in the block's own words.
   */
  const probe = { ...probeRoll(variables), ...probeValuesForRecalls(JSON.stringify(d)) };
  const acceptedRanges = computeAcceptedRangesShared(numericalRangeConfig(d, probe));
  const rangesResolvedValidly = acceptedRanges.every(r =>
    (Number.isFinite(r.lo) || r.lo === -Infinity) && (Number.isFinite(r.hi) || r.hi === Infinity)
  );

  const targeted = feedbackStates.filter(s => s.type === 'targeted');
  const valueFeedbackStates = targeted.filter(s =>
    !s.trigger.startsWith('unit_') && !s.trigger.startsWith(UNCERTAINTY_TRIGGER_PREFIX));
  /**
   * A trigger with its formulas worked out.
   *
   * EVERY comparison below reads this rather than the raw text, because a trigger may hold a
   * formula and `parseFloat('398+2')` is NaN. Reading the raw text meant a formula trigger was
   * invisible to all of it: not reported as overlapping the correct answer, not reported as
   * overlapping another trigger, and separately reported as having no value at all. Three wrong
   * answers from one missing step.
   */
  /*
   * Each trigger in ITS OWN mode. The analysis below compares triggers against one another, so
   * resolving them all in one mode would have it comparing numbers that do not mean the same
   * thing — a radian trigger read as degrees overlaps all the wrong neighbours.
   */
  const modeOfTrigger = new Map(valueFeedbackStates.map(s =>
    [s.trigger, angleModeFor(d, `feedback.${s.id}.trigger`)] as const));
  const resolvedTrigger = (trigger: string) =>
    numericalResolveTrigger(trigger, (val: string) =>
      evaluateFormulaRaw(val, probe, modeOfTrigger.get(trigger) ?? angleModeFor(d, '')));
  const overlappingTriggerSet = overlappingTriggerSetOf(
    valueFeedbackStates.map(s => s.trigger), resolvedTrigger);

  /**
   * A significant-figure requirement no answer can satisfy.
   *
   * The band and the precision are each fine alone and contradict each other together: with an
   * answer of 106 and two figures required, 110 is the right rounding and falls outside the band,
   * while 106 itself carries three figures and the format fault becomes the verdict. Nobody is ever
   * marked correct, and every other way to break this block already reports itself.
   *
   * Quantified when the band moves, on the same rule as everything else here: a formula answer is
   * only accused when NO roll admits an answer, because one unlucky roll is not a setup problem.
   */
  /*
   * Two OR'd answers that accept some of the same values.
   *
   * "10 ± 5, or 12 ± 5" reads as two alternatives and is really one band with a seam: a submitted 11
   * satisfies both, and which one the evaluator reports is an accident of order. Compass and angle
   * have flagged this since they were written; numerical and slider — same multi-answer shape, same
   * mistake available — flagged nothing, because the check lived inside those two files instead of
   * in the package. It is `overlappingAnswerPairs` now, and this is one of its four callers.
   *
   * Judged on the PROBE roll. Two formula answers that overlap for one student and not another is a
   * question about the whole domain, and a warning this soft does not earn a sweep.
   */
  const answerOverlap = (() => {
    if (d.conditionType !== 'exact' && d.conditionType !== 'between') return false;
    const src = d.conditionType === 'exact' ? d.answers : d.betweenAnswers;
    if (src.length < 2) return false;
    const cfg = numericalRangeConfig(d, probe);
    const regions = src.map((a, i) => ({
      id: a.id,
      intervals: computeAcceptedRangesShared(d.conditionType === 'exact'
        ? { ...cfg, answers: [cfg.answers[i]!], betweenAnswers: [] }
        : { ...cfg, answers: [], betweenAnswers: [cfg.betweenAnswers[i]!] })
        .filter(r => Number.isFinite(r.lo) && Number.isFinite(r.hi)),
    }));
    return overlappingAnswerPairs(regions).length > 0;
  })();

  const sigFigsUnsatisfiable = (() => {
    if (!d.sigFigsRequired || d.gradingMode !== 'graded') return false;
    const admits = (vals: Record<string, string>) => {
      const ranges = computeAcceptedRangesShared(numericalRangeConfig(d, vals));
      // Nothing configured, or a band that will not resolve: a different issue owns that, and
      // stacking this on top would send the teacher after the wrong thing.
      if (ranges.length === 0) return true;
      if (!ranges.every(r => (Number.isFinite(r.lo) || r.lo === -Infinity)
        && (Number.isFinite(r.hi) || r.hi === Infinity))) return true;
      return ranges.some(r => bandAdmitsSigFigs(r.lo, r.hi, d.sigFigsCount));
    };
    const names = chipNamesIn(bandFormulas(d));
    if (names.length === 0) return !admits(probe);
    let everAdmits = false;
    const swept = forEachRoll(names, variables, vals => {
      if (admits(vals)) { everAdmits = true; return 'stop'; }
      return undefined;
    }, OVERLAP_ENUMERATION_MAX_COMBOS);
    return swept && !everAdmits;
  })();

  /**
   * THE AUTHORED ANSWER MUST BE WRITTEN THE WAY THE STUDENT IS ASKED TO WRITE IT.
   *
   * A teacher who sets three significant figures and then types `9.8134` has authored an answer
   * no student may submit, and the editor used to say nothing — the mismatch surfaced only as
   * confusion about why the panel and the grader disagreed. Holding the authored value to the
   * same count makes the answer key literally the expected paper.
   *
   * Only for a LITERAL answer. A formula resolves per student and its figures are a property of
   * the roll, not of what was typed.
   */
  const authoredAnswerFigures = (() => {
    if (!d.sigFigsRequired || d.conditionType !== 'exact' || d.gradingMode !== 'graded') return null;
    for (const a of d.answers) {
      const raw = a.value.trim();
      if (!raw || hasVariableChip(raw)) continue;
      const figs = countSigFigs(raw);
      if (figs > 0 && figs !== d.sigFigsCount) return { id: `${a.id}.value`, figs };
    }
    return null;
  })();

  /** The same rule on the error box, against its own count. */
  const authoredUncertaintyFigures = (() => {
    if (!uncertaintyAsked(d) || d.gradingMode !== 'graded') return null;
    const raw = d.uncertaintyValue.trim();
    if (!raw || hasVariableChip(raw)) return null;
    const want = Math.max(1, d.uncertaintySigFigs ?? 1);
    const figs = countSigFigs(raw);
    return figs > 0 && figs !== want ? { figs } : null;
  })();

  /**
   * AND THE ANSWER SITS WHERE THE UNCERTAINTY PUTS IT.
   *
   * Nobody authors the answer's precision — the uncertainty does. `0.3` says tenths, so an answer
   * of `4.27` is one no student may write. This is the rule that makes the two fields read as one
   * decision rather than two independent numbers.
   */
  const authoredAnswerPlace = (() => {
    if (!uncertaintyAsked(d) || d.gradingMode !== 'graded') return null;
    const unc = d.uncertaintyValue.trim();
    if (!unc || hasVariableChip(unc)) return null;
    const want = lastSignificantPlace(unc);
    if (want === null) return null;
    for (const a of d.answers) {
      const raw = a.value.trim();
      if (!raw || hasVariableChip(raw)) continue;
      const got = lastSignificantPlace(raw);
      if (got !== null && got !== want) return { id: `${a.id}.value` };
    }
    return null;
  })();

  /**
   * THE BAND IS TOO TIGHT FOR THE PRECISION THE UNCERTAINTY DEMANDS.
   *
   * With an error box the value's precision is not a count the teacher set — it is the place the
   * student's own uncertainty dictates, and the teacher's tolerance has to be wide enough to
   * contain a number written there. `9.8134 ± 0.005` is [9.8084, 9.8184] and a tenths answer has
   * only `9.8` to offer, which is outside. Nobody can be right, and nothing in the editor said so
   * until this.
   *
   * The place comes from the uncertainty KEY, which is a formula, so this sweeps the variable
   * values the same way the other two guards do — the required precision genuinely differs
   * between students, and a block answerable on one draw and not the next is still broken.
   */
  const valuePlaceUnsatisfiable = (() => {
    if (!uncertaintyAsked(d) || d.gradingMode !== 'graded') return false;
    const figs = Math.max(1, d.uncertaintySigFigs ?? 1);
    const admits = (vals: Record<string, string>) => {
      const key = evaluateFormulaRaw(d.uncertaintyValue, vals, angleModeFor(d, 'uncertaintyValue'));
      if (!Number.isFinite(key) || key <= 0) return true;
      // The place the student's uncertainty will sit at, once written to the figures asked for.
      const place = Math.floor(Math.log10(Math.abs(key))) - figs + 1;
      const ranges = computeAcceptedRangesShared(numericalRangeConfig(d, vals));
      if (ranges.length === 0) return true;
      if (!ranges.every(r => (Number.isFinite(r.lo) || r.lo === -Infinity)
        && (Number.isFinite(r.hi) || r.hi === Infinity))) return true;
      return ranges.some(r => bandAdmitsPlace(r.lo, r.hi, place));
    };
    const names = chipNamesIn([d.uncertaintyValue, ...bandFormulas(d)].filter(Boolean));
    if (names.length === 0) return !admits(probe);
    let everAdmits = false;
    const swept = forEachRoll(names, variables, vals => {
      if (admits(vals)) { everAdmits = true; return 'stop'; }
      return undefined;
    }, OVERLAP_ENUMERATION_MAX_COMBOS);
    return swept && !everAdmits;
  })();

  /**
   * THE SAME CONTRADICTION, ON THE ERROR BOX.
   *
   * `0.28` with no tolerance and one required figure admits nothing: the band accepts only 0.28,
   * the format accepts only 0.2 and 0.3. Exactly the trap the value is already guarded against,
   * so it is the same function — the uncertainty is graded as a number with a band and a format,
   * and that is what makes the guard transfer unchanged.
   *
   * Graded-only and opt-in, like the value's: participation has no band to contradict.
   */
  const uncertaintyFiguresUnsatisfiable = (() => {
    if (!uncertaintyAsked(d) || d.gradingMode !== 'graded') return false;
    const figs = Math.max(1, d.uncertaintySigFigs ?? 1);
    const admits = (vals: Record<string, string>) => {
      const key = evaluateFormulaRaw(d.uncertaintyValue, vals, angleModeFor(d, 'uncertaintyValue'));
      // An unresolved or non-positive key is a different problem with its own report; piling this
      // on top sends the teacher after the wrong one.
      if (!Number.isFinite(key) || key <= 0) return true;
      const band = uncertaintyBand(key, {
        uncertaintyTolerance: parseFloat(d.uncertaintyTolerance || '0'),
        uncertaintyToleranceAbove: parseFloat(d.uncertaintyToleranceAbove || '0'),
        uncertaintyToleranceBelow: parseFloat(d.uncertaintyToleranceBelow || '0'),
        uncertaintyToleranceMode: d.uncertaintyToleranceMode,
        uncertaintyToleranceSymmetry: d.uncertaintyToleranceSymmetry,
      });
      return bandAdmitsSigFigs(band.lo, band.hi, figs);
    };
    const names = chipNamesIn([d.uncertaintyValue].filter(Boolean));
    if (names.length === 0) return !admits(probe);
    let everAdmits = false;
    const swept = forEachRoll(names, variables, vals => {
      if (admits(vals)) { everAdmits = true; return 'stop'; }
      return undefined;
    }, OVERLAP_ENUMERATION_MAX_COMBOS);
    return swept && !everAdmits;
  })();

  // Trigger-versus-Correct is the one question here that needs the whole domain rather than one
  // point of it, so it gets its own sweep.
  const overlap = analyseTriggerOverlap(d, valueFeedbackStates.map(s => ({ id: s.id, trigger: s.trigger })), variables);

  // `probeable` is everything the probe can actually supply a value for. It differs from `declared`
  // for a variable whose range is not set yet, and that difference decides whether an unresolved
  // formula is an accusation or a shrug — see `triggerValueIsDead`.
  const declaredNames = new Set(variables.map(v => v.name));
  const probeableNames = new Set(Object.keys(probe));
  const faults = valueFeedbackStates
    .map(st => numericalTriggerFault(st.trigger,
      val => evaluateFormulaRaw(val, probe, angleModeFor(d, `feedback.${st.id}.trigger`)),
      declaredNames, probeableNames))
    .filter((f): f is TriggerFault => f !== null);

  // A field that has not been filled in is an ABSENCE; a field holding something unusable is a
  // CONTRADICTION. They are reported as two different issues because `kind` decides whether a
  // teacher is interrupted mid-typing, and one key covering both meant a blank box shouted.
  const fieldBlank = (val: string): boolean => val.trim() === '';
  const fieldMalformed = (val: string, fieldId: string): boolean => {
    if (val.trim() === '') return false;
    if (Number.isFinite(Number(val))) return !!numberError(val, t, { integerOnly: d.integerOnly });
    // `probe` already carries a stand-in for every recall the block quotes — see its own note.
    return !Number.isFinite(evaluateFormulaRaw(val, probe, angleModeFor(d, fieldId)));
  };
  const toleranceMalformed = (val: string): boolean =>
    !!numberError(val, t, { allowNegative: false, integerOnly: d.integerOnly });

  /** Each answer field's text WITH its id, so a malformed check can read that field's mode. */
  const answerFields: { value: string; fieldId: string }[] =
    d.conditionType === 'exact' ? d.answers.map(a => ({ value: a.value, fieldId: `${a.id}.value` }))
      : d.conditionType === 'between'
        ? d.betweenAnswers.flatMap(a => [
          { value: a.low, fieldId: a.id }, { value: a.high, fieldId: a.id }])
        : [{ value: d.threshold, fieldId: 'threshold' }];
  // Graded only, with the answer fields themselves: outside graded they are not on screen, so an
  // error about one would point at a field the teacher cannot see, let alone fill.
  const answerFieldBlank = d.gradingMode === 'graded' && answerFields.some(f => fieldBlank(f.value));
  const answerFieldMalformed = d.gradingMode === 'graded' && answerFields.some(f => fieldMalformed(f.value, f.fieldId))
    || (d.conditionType === 'exact' && d.answers.some(a =>
      toleranceMalformed(a.toleranceValue) || toleranceMalformed(a.toleranceAbove) || toleranceMalformed(a.toleranceBelow)));

  /*
   * Quantified over every roll, not sampled at one.
   *
   * This evaluated both bounds at `probe` — each variable's MINIMUM — and reported yes/no from
   * that. The probe roll is close to the least likely one to be inverted, so a pair of bounds that
   * cross on most rolls was reported clean; and a pair that happened to cross at the minimum was
   * reported as an outright defect even when almost every other roll was fine.
   */
  const betweenRows = d.conditionType === 'between'
    ? d.betweenAnswers
      .filter(a => !fieldBlank(a.low) && !fieldBlank(a.high)
        && !fieldMalformed(a.low, a.id) && !fieldMalformed(a.high, a.id))
      // Each row in ITS OWN mode: a between-answer is a field, and two of them in one block can
      // disagree. The third argument is only the fallback for a row that names none.
      .map(a => ({ ...a, angleMode: angleModeFor(d, a.id) }))
    : [];
  const betweenVerdict = betweenInversionVerdict(betweenRows, variables, angleModeFor(d, ''));
  /*
   * Targeted RANGE triggers, quantified.
   *
   * `numericalTriggerFault` deliberately normalises a computed range before asking, so it reports
   * only an inversion a teacher typed as literals — which means a range trigger with equation
   * bounds was never checked. A warning rather than an error even when it always inverts: an
   * inverted feedback band just means that state never fires and the answer falls through to
   * Default Incorrect, still graded, only less specific.
   */
  const trigRangeVerdict = triggerRangeInversionVerdict(
    valueFeedbackStates.map(st => st.trigger), variables, angleModeFor(d, ''),
    i => angleModeFor(d, `feedback.${valueFeedbackStates[i]?.id}.trigger`));

  // Same split again: units switched on but not yet named is an absence; a unit that was named and
  // does not parse is a contradiction, and staying silent about it until the block closes is how a
  // typo survived to publication.
  /*
   * The CANONICAL unit is read wherever it is OFFERED, which is everywhere but Optional.
   *
   * This said "graded-only" and gated both errors on it, on the reasoning that the editor stops
   * offering the field outside graded so a warning would point at nothing. That reasoning was
   * true when only graded had units and is now false: Participation offers the field and the
   * evaluator reads it, because a unit is part of what the QUESTION asks ("give it in joules")
   * rather than part of the grading — the same footing as sig figs and scientific notation, and
   * the reason `numericalHasFormatGate` counts it.
   *
   * What the stale gate cost: a teacher who picked "with units" in Participation and left the
   * canonical blank, or typed something that does not parse, got no warning at all while EVERY
   * answer a student could give came back a Unit Error. Nothing can match a unit that was never
   * named, so the block silently rejected the lot.
   *
   * Optional is genuinely exempt: the field is not drawn there and the evaluator returns at the
   * optional branch before any unit is looked at.
   *
   * The DISPLAY label is different again: it is drawn beside the student's box in every mode, so
   * a blank one is worth saying regardless.
   */
  const unitsInForce = d.gradingMode !== 'optional' && d.unitMode === 'withUnits';
  const unitSetupBlank = (unitsInForce && !d.canonicalUnit)
    || (d.unitMode === 'display' && !d.unitLabel);
  const unitSetupInvalid = unitsInForce && !!d.canonicalUnit && !isValidUnit(d.canonicalUnit);

  // The card's own bar used to branch on this directly, which is how the two derivations drifted:
  // `null` means nothing is renderable AND nothing has been entered (an absent answer), while an
  // empty `entries` array means nothing is renderable but something HAS been entered (a partial
  // one). Both now feed the issues list rather than a private ladder, so the card, the editor
  // banner and the test-panel diagnostics cannot disagree about which of the two it is.
  const barEntries = d.gradingMode === 'graded' ? computeBarEntries(d, t, locale, probe) : null;
  const noCorrectAnswers = d.gradingMode === 'graded' && barEntries === null;
  const barSetupPartial = barEntries !== null && barEntries.entries.length === 0;

  // Trimmed, so `kg` and `kg ` count as the one unit they would both grade as. A blank row is
  // neither: it is unreachable and harmless, and nagging about a row the teacher has only just
  // added would fire on the Add button itself.
  const canonLabel = d.canonicalUnit.trim();
  // Only the OPTION is named, never the canonical field. Unlike two options, which have no
  // precedence between them, here one is the definition and the other is a copy of it — the copy is
  // what the teacher should remove.
  const canonCopies = canonLabel ? d.unitOptions.filter(o => o.label.trim() === canonLabel) : [];
  const dupGroups = findDuplicateGroups(d.unitOptions, o => o.label.trim());
  // Only when the alternatives are actually on screen. The whole options section is gated on a
  // canonical unit that is present AND valid, so with the canonical blank or unrecognized a teacher
  // is told about rows they cannot see, let alone fix — and the canonical's own error, which they
  // CAN act on, is what unblocks them. Reporting both at once buries the actionable one.
  const alternativesVisible = !!canonLabel && isValidUnit(canonLabel);
  const unrecognized = alternativesVisible
    ? d.unitOptions.filter(o => o.label.trim() && !isValidUnit(o.label.trim()))
    : [];

  return buildNumericalIssues({
    t,
    noCorrectAnswers,
    unreviewedScenarios: unreviewedCount(d.scenarioBranches),
    mixedToleranceTypes: d.conditionType === 'exact' && tolerancesDiverge(d.answers, d),
    badUnitOptions: {
      duplicate: {
        labels: [...dupGroups.map(g => g[0]!.label.trim()), ...(canonCopies.length ? [canonLabel] : [])],
        ids: [...new Set([...dupGroups.flatMap(g => g.map(o => o.id)), ...canonCopies.map(o => o.id)])],
      },
      unrecognized: {
        labels: unrecognized.map(o => o.label.trim()),
        ids: unrecognized.map(o => o.id),
      },
    },
    question: d.question,
    setupIncomplete: d.gradingMode === 'graded' && (!rangesResolvedValidly || barSetupPartial),
    // Unreachable states excluded, for the same reason the panel and the legend hide them: a card
    // that can never fire cannot be missing anything. A `cm` targeted state left behind when `cm`
    // was flagged correct raised "Some feedback is missing text" against a panel where every
    // visible card had text — a warning with no findable source, which is worse than no warning.
    // GRADED ONLY, because the fault is unfixable anywhere else: the toggle that owns math errors
    // is disabled outside Has Correct and its feedback section is not drawn, so a stored
    // `mathErrorEnabled` (deliberately kept, so switching back restores the setup) would raise a
    // missing-override fault against a control the teacher can no longer reach. Same shape as the
    // band warnings — never reason about a mechanism in a mode where it is not in force.
    mathErrorEquationsWrong: d.gradingMode !== 'graded' ? 0 : (() => {
      const r = (val: string, fieldId: string): number =>
        evaluateFormulaRaw(val, probe, angleModeFor(d, fieldId));
      return mathErrorMismatches(mathErrorSlotsOf(d, r), r).length;
    })(),
    hasMissingFeedbackText: hasEmptyFeedbackContent(
      feedbackStates.filter(s => !isUnreachableUnitTargeted(s.trigger, d))),
    answerFieldBlank,
    answerFieldMalformed,
    unitSetupBlank,
    unitSetupInvalid,
    betweenVerdict,
    trigRangeVerdict,
    sigFigsUnsatisfiable,
    uncertaintyFiguresUnsatisfiable,
    valuePlaceUnsatisfiable,
    authoredAnswerFigures,
    authoredUncertaintyFigures,
    authoredAnswerPlace,
    answerOverlap,
    sigFigsCount: d.sigFigsCount,
    // Counted from the per-state faults, so the bar and the card cannot disagree about whether a
    // condition is broken. Every one of these reads the RESOLVED trigger: a trigger may hold a
    // formula, and `parseFloat('{test}+1')` is NaN, so an equation-valued condition used to be
    // reported as having no value set while working perfectly.
    triggerMissingValue:     faults.includes('no-value'),
    triggerUnresolvable:     faults.includes('unresolvable'),
    triggerRangeIncomplete:  faults.includes('range-incomplete'),
    // Only an AUTHORED inversion, and that falls out of where the check happens rather than from a
    // special case: `numericalTriggerFault` normalises a COMPUTED range before asking, so by the
    // time `triggerFault` looks, the only backwards range left is one a teacher typed.
    triggerRangeInverted:    faults.includes('range-inverted'),
    anySubsumed: overlap.any.alwaysSubsumed,
    anyOverlapsCorrect: overlap.any.alwaysOverlaps,
    anyMayOverlapCorrect: overlap.any.mayCollide,
    anyOverlapEachOther: overlappingTriggerSet.size > 0,
  });
}

/**
 * This block's fired feedback — see `FiredFeedback`, which every block now shares.
 *
 * CLAMPED TO THE STANDARD ANSWER COLUMN, here rather than at the three call sites, so all three
 * surfaces stay the same width as every other block's feedback. It matters because the answer row
 * above may be `wide`: a three-box row needs 513px (see `answerRowNeedsWidth`), and letting the
 * prose underneath inherit that made it the widest feedback in the app. The clamp is `max-width`
 * only, so on a phone, where the surface is already narrower, it does nothing.
 */
function renderNumericalFiredFeedback(testResult: FeedbackState[] | null): React.ReactNode {
  return <div className={ANSWER_COLUMN_CLASS}><FiredFeedback states={testResult} /></div>;
}

/** The answerable surface, shared by the card, the phone Test tab and Preview so all three grade
 * the same thing. The unit box appears only for `withUnits` — that is the one mode where the unit
 * is part of the answer rather than decoration, and `evaluateNumerical` checks it before it ever
 * compares a value. */
function NumericalTestInput({ d, value, unit, uncertainty, onValue, onUnit, onUncertainty, t, inert = false }: {
  d: NumericalBlockData;
  value: string;
  unit: string;
  uncertainty: string;
  onValue: (v: string) => void;
  onUnit: (v: string) => void;
  onUncertainty: (v: string) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  /**
   * The unselected card's display state: the same markup, made non-interactive.
   *
   * It is the SAME component rather than a hand-built mockup beside it because the two are
   * required to look identical, and the mockup had already drifted three ways — a 128px box
   * against a 160px field, left-aligned against centred, and a transparent box against the
   * field's own `bg-ws-btn`. `inert` (not `disabled`) because TextField carries
   * `disabled:opacity-40`, which would grey the display state and reintroduce the difference
   * this is removing.
   */
  inert?: boolean;
}) {
  const { i18n } = useTranslation();
  const toleranceHint = toleranceHintFor(d, i18n.language, t);
  const uncertaintyToleranceHint = toleranceHintFor(d, i18n.language, t, 'uncertainty');
  const anyToleranceHint = !!toleranceHint || !!uncertaintyToleranceHint;
  return (
    <div
      /* `@container`: the row below stacks on the BLOCK's width, not the viewport's. */
      className={`@container flex flex-col items-center gap-1${inert ? ' pointer-events-none' : ''}`}
      {...(inert ? { inert: '', 'aria-hidden': true } : {})}
    >
      {/* Stacked when there is no room for both, side by side when there is.
          The two fields are `w-40` each plus a gap -- 328px, which overflows the card below
          about 360px of width, pushing the unit box off the edge.

          A CONTAINER query, not a viewport one. This was `max-sm:flex-col`, which asks how wide
          the WINDOW is — and the window is not what holds these fields. The canvas is the
          viewport minus a 256px library and a 384px editor panel, so at a 1024px window the card
          is about 384px wide and `sm` still reports "plenty of room". The fields then overflowed
          on a perfectly ordinary laptop, which is where this was reported from. `@container` asks
          the element that actually constrains them.

          Still CSS rather than the `isPhone` hook, for the original reason: this one component
          renders on the canvas card, the phone Test tab AND Preview, and the hook would have to
          be threaded through all three. */}
      {/*
        * THE BREAKPOINT COUNTS THE FIELDS, because it was measured against two of them.
        *
        * `@max-sm` (384px) came from the value and the unit needing 328px. A third box takes the
        * row to 513px, so between 384 and 513 the query said "plenty of room" and the unit hung
        * 33px outside the card — measured in-browser at a 446px container, which is an ordinary
        * laptop with both panels open. The same class of mistake the `@container` switch was
        * itself introduced to fix, one field later.
        *
        * Both spellings are literal so the JIT can see them.
        */}
      {/* `pb-5` RESERVES the hint line once, for the row, because the hints below are positioned
          out of the flow. In the flow they made their own field's column taller, and `items-center`
          then centred the COLUMN rather than the box — so the value sat half a hint-line above the
          unit beside it. Reserved rather than conditional on a hint being present, so switching
          "Show tolerance" on does not move everything under it. */}
      <div className={`flex items-center gap-2 ${anyToleranceHint ? 'pb-5' : ''} ${
        answerRowNeedsWidth(d)
          ? '@max-[33rem]:flex-col'
          : '@max-sm:flex-col'}`}>
        <div className='relative'>
          {/* `scientific`, NOT `expression`, and the difference is a grading bug rather than a
              label. evaluateNumerical reads the submitted value with `parseFloat`, so an
              expression field here invited `100*pi` and then graded it as 100 — silently, and in
              the student's favour or against them depending on the answer. The grammar now matches
              what the evaluator can actually read. */}
          {/* Integer-only makes this an integer field, which also switches inputMode to `numeric`
              — so a phone offers a keypad with no decimal point at all, and the requirement is
              carried by the affordance rather than by a message after the fact. */}
          <NumberField
            grammar={d.integerOnly ? 'integer' : 'scientific'}
            allowNegative
            value={value}
            onChange={onValue}
            ariaLabel={d.unitMode === 'display' && d.unitLabel
              ? t('blocks.numerical.testValueAriaWithUnit', { unit: stripHtmlForLabel(d.unitLabel) })
              : t('blocks.numerical.testValueAria')}
            className='w-40 [&_input]:text-center' />
          {/* UNDER THE NUMBER IT QUALIFIES, and out of the flow so it cannot push the box out of
              line with the ones beside it. With an error box the row holds two numbers and a unit,
              so a hint below the whole of it named none of them. Only when there IS a second
              number — otherwise it stays where it always was, below the row. */}
          {uncertaintyAsked(d) && toleranceHint && (
            <p className={`${TOLERANCE_HINT_CLASS} ${HINT_POSITION}`}>{toleranceHint}</p>
          )}
        </div>
        {/* `± [box]`, between the value and the unit, because that is the order the answer is
            WRITTEN in — `9.8 ± 0.3 m/s²` — and one unit closes the whole of it.

            The sign is a sibling rather than a prefix inside the box: the student does not type it
            and it is not graded, the same reason the display unit is a caption beside its field.
            `self-center` keeps it on the number line when the row stacks, where the fields grow a
            label each and a baseline-aligned glyph would ride up.

            Integer-only never reaches here — it takes the error box off the question entirely,
            since every value agreeing with an ordinary uncertainty carries a decimal. */}
        {uncertaintyAsked(d) && (<>
          <span aria-hidden='true' className='shrink-0 self-center text-sm text-ws-muted'>±</span>
          {/* `relative`, exactly as the value's wrapper is: the two columns have to be the same
              shape or the boxes stop lining up the moment one of them has a hint. */}
          <div className='relative'>
            <NumberField
              grammar='scientific'
              value={uncertainty}
              onChange={onUncertainty}
              ariaLabel={t('blocks.numerical.testUncertaintyAria')}
              className='w-40 [&_input]:text-center' />
            {uncertaintyToleranceHint && (
              <p className={`${TOLERANCE_HINT_CLASS} ${HINT_POSITION}`}>{uncertaintyToleranceHint}</p>
            )}
          </div>
        </>)}
        {d.unitMode === 'display' && d.unitLabel && (
          // A sibling of the field, not an overlay inside it: the student does not type this and
          // it is not graded, so it reads as a caption beside the box rather than as content in it.
          <PreviewHtml html={d.unitLabel} className='ws-rich-text inline-block shrink-0 text-sm text-ws-muted' />
        )}
        {d.unitMode === 'withUnits' && (
          <UnitField
            value={unit}
            onChange={onUnit}
            ariaLabel={t('blocks.numerical.testUnitAria')}
            // The student's box, and the only one gated: what leaves this field always parses, so
            // an unrecognised unit can never be submitted. A student who cannot produce one is
            // graded Incomplete Answer, which says what happened, rather than Incorrect, which
            // would claim they answered in the wrong unit.
            strict
            // Matched to the number field beside it, and to the teacher's unit rows. `w-32` left
            // the suggestion list narrower than its own widest row, which then scrolled sideways.
            className='w-40' />
        )}
      </div>
      {/* The single-number case, unchanged: one number in the row, so a hint below the row names
          it unambiguously. With an error box it moves under the value itself, just above. */}
      {!uncertaintyAsked(d) && toleranceHint
        && <p className={TOLERANCE_HINT_CLASS}>{toleranceHint}</p>}
    </div>
  );
}

// ─── Correct bar entry computation ───────────────────────────────────────────
// Returns valid accepted-answer strings + a count of incomplete answers that were skipped.
// Returns null only when nothing is configured at all ("No Correct Answers").
// Partial state: { entries: [], incompleteCount > 0 } — teacher has entered data but it's not
// yet resolvable (e.g. number present but unit missing, or unit present but value missing).

// True when the teacher has entered any answer value or canonical unit — i.e. setup is in progress.

/** `lo`/`hi` are the interval this entry ACCEPTS, and `unit` the unit it is expressed in — together
 *  they identify it, so the legend can light only the band a submission actually landed in rather
 *  than every band of the state. Unit is part of the identity because a block with alternative
 *  units publishes one entry per unit for the same answer, and a submission is in exactly one. */


// ─── Overlap detection ────────────────────────────────────────────────────────

/** The accepted-range config, resolved against one roll. Three call sites had their own copy. */

/**
 * Numerical's analysis: the shared verdicts plus the one extra claim it quantifies on the same
 * sweep. `claimHoldsAlways` is renamed at this boundary so the call sites read as what it means
 * here rather than as the generic engine's vocabulary.
 */
export interface TriggerOverlapAnalysis extends OverlapAnalysis {
  /** Whether Default Incorrect can never fire — the band plus these conditions cover the whole
   *  line — in EVERY roll. */
  defaultIncorrectUnreachable: boolean;
}

export function analyseTriggerOverlap(
  d: NumericalBlockData,
  /** The STATES, not bare strings: each trigger is a field and carries its own angle mode. */
  triggerStates: { id: string; trigger: string }[],
  variables: TaskVariable[],
): TriggerOverlapAnalysis {
  /**
   * Every verdict here is about the ACCEPTED BAND, and outside graded there is no accepted band
   * in force — correct is never checked, so nothing is subsumed by it and Default Incorrect is
   * not in play at all.
   *
   * Returning the graded verdicts in a participation mode would call a condition dead that the
   * evaluator will happily fire, which is a disagreement between the editor and the grader of
   * exactly the kind `numerical-agreement.test.ts` exists to catch.
   */
  /*
   * UNLESS A SET ANSWER CARD CLAIMS IT. The note above was written before the preset existed and
   * was true then: with nothing pointing at the band, a condition inside it collides with
   * nothing, because the catch-all is catching those values anyway.
   *
   * A Set Answer card IS the band, so a condition landing inside it fires at the same moment —
   * and unlike Correct, the preset is NOT terminal, so both fire and the student gets two cards.
   * `valueFeedbackWarning` says it differently per mode for exactly that reason: dead in graded,
   * merely duplicated here.
   */
  const triggers = triggerStates.map(st => st.trigger);
  const presetClaimsBand = d.gradingMode === 'survey' && triggers.includes(SET_ANSWER_TRIGGER);
  if (d.gradingMode !== 'graded' && !presetClaimsBand) {
    const clear = { alwaysSubsumed: false, alwaysOverlaps: false, mayCollide: false };
    return {
      per: triggers.map(() => ({ ...clear })),
      any: { ...clear },
      claimHoldsAlways: false,
      defaultIncorrectUnreachable: false,
    };
  }

  const names = chipNamesIn([...bandFormulas(d), ...triggers.flatMap(triggerValuesOf)]);
  const analysis = analyseOverlapAcrossRolls({
    triggers, names, variables,
    atRoll: vals => {
      const ranges = computeAcceptedRangesShared(numericalRangeConfig(d, vals));
      // A band that does not resolve is not evidence either way.
      if (!ranges.every(r =>
        (Number.isFinite(r.lo) || r.lo === -Infinity) && (Number.isFinite(r.hi) || r.hi === Infinity))) {
        return null;
      }
      const resolved = new Map(triggers.map((trigger, i) =>
        [trigger, numericalResolveTrigger(trigger, val =>
          evaluateFormulaRaw(val, vals, angleModeFor(d, `feedback.${triggerStates?.[i]?.id}.trigger`)))]));
      const at = (trigger: string) => resolved.get(trigger) ?? trigger;
      return {
        subsumed: trigger => isValueFeedbackFullySubsumed(at(trigger), ranges),
        overlaps: trigger => doesValueFeedbackOverlapAccepted(at(trigger), ranges),
        // Rides the same sweep because it reads the very same two things: the band, and these
        // conditions resolved against it. Checked at the band's two extremes once, which is not a
        // bound on it at all — the extremes of the accepted window say nothing about the gaps
        // BETWEEN conditions.
        claim: isValueDefaultIncorrectUnreachable(ranges, [...resolved.values()]),
      };
    },
  });
  return { ...analysis, defaultIncorrectUnreachable: analysis.claimHoldsAlways };
}

/**
 * The accepted condition stated SYMBOLICALLY — variables left as chips, for a panel with no
 * scenario picked.
 *
 * Every condition type, which is the fix. Both callers used to build this themselves and both only
 * knew how to draw an EXACT answer; anything else fell through to a literal `(EQUATION)` placeholder
 * — so a `between` answer with a formula in it, or any of the four thresholds, described itself to
 * the teacher as the word EQUATION in brackets. The gate above these was already widened to every
 * condition type; only the drawing was left behind.
 *
 * Built by translating first and chipifying after, exactly as the numeric path does: the sentences
 * are the same `acceptedRange` / `acceptedPreview*` strings, so the symbolic and resolved readings
 * of one condition cannot drift into two different phrasings, and no locale has to be told about a
 * second way of saying it.
 */
/** One entry per authored answer. `inert` marks a row the EVALUATOR drops — a `between` row with
 *  one bound missing — so the chip can say it is being ignored rather than print a bound that does
 *  not exist. `? to 20` on its own read as an unfinished answer that still counted; it does not. */



/**
 * The Correct condition as chips — the ONE derivation both surfaces draw.
 *
 * The test bar used to join the accepted previews into a single string with `computeAcceptedPreviews`
 * while the feedback panel mapped `computeBarEntries` into one chip per entry. Two preview builders,
 * two shapes, one condition. This keeps the panel's shape (a chip per accepted value, joined by
 * "or") and the panel's source (`computeBarEntries`, which is the unit-aware one), so a block with
 * alternative units finally reads the same in both places.
 *
 * `roll` is the whole difference between the surfaces: null keeps variables as `{n}` chips (the
 * feedback panel at rest), a roll resolves them to numbers (the test bar always, and the panel once
 * a scenario is picked).
 */

function computeNumericRanges(fields: NumericalBlockData): Array<[number, number]> | null {
  if (fields.conditionType === 'exact') {
    if (fields.answers.length < 2) return null;
    const out: Array<[number, number]> = [];
    for (const ans of fields.answers) {
      const v = parseFloat(ans.value);
      if (!Number.isFinite(v)) return null;
      if (fields.toleranceSymmetry === 'asymmetric') {
        const above = parseFloat(ans.toleranceAbove || '0');
        const below = parseFloat(ans.toleranceBelow || '0');
        if (!Number.isFinite(above) || !Number.isFinite(below) || above < 0 || below < 0) return null;
        const lo = fields.toleranceMode === 'absolute' ? v - below : v * (1 - below / 100);
        const hi = fields.toleranceMode === 'absolute' ? v + above : v * (1 + above / 100);
        out.push([lo, hi]);
      } else {
        const err = parseFloat(ans.toleranceValue || '0');
        if (!Number.isFinite(err) || err < 0) return null;
        const lo = fields.toleranceMode === 'absolute' ? v - err : v * (1 - err / 100);
        const hi = fields.toleranceMode === 'absolute' ? v + err : v * (1 + err / 100);
        out.push([lo, hi]);
      }
    }
    return out;
  }
  if (fields.conditionType === 'between') {
    if (fields.betweenAnswers.length < 2) return null;
    const out: Array<[number, number]> = [];
    for (const ans of fields.betweenAnswers) {
      const lo = parseFloat(ans.low), hi = parseFloat(ans.high);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
      if (lo > hi) return null; // invalid — don't run overlap check on bad data
      out.push([lo, hi]);
    }
    return out;
  }
  return null;
}

function hasRangeOverlap(ranges: Array<[number, number]>): boolean {
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      const [lo1, hi1] = ranges[i]!;
      const [lo2, hi2] = ranges[j]!;
      if (lo1 <= hi2 && lo2 <= hi1) return true;
    }
  }
  return false;
}

// ─── Feedback trigger helpers ─────────────────────────────────────────────────

/** A trigger bound with nothing usable in it. Blank and unparseable are the same failure here: both
 * make every comparison against it false, so the state can never fire. */

function parseTrigger(trigger: string): { type: NumTriggerType; v1: string; v2: string } {
  const parts = trigger.split(':');
  const raw = parts[0] ?? 'exact';
  const type = (['lt', 'lte', 'gt', 'gte', 'range'].includes(raw) ? raw : 'exact') as NumTriggerType;
  return { type, v1: parts[1] ?? '', v2: parts[2] ?? '' };
}

// Values are sanitised, not trusted: a trigger is ONE delimited string, so a colon inside a value
// silently re-splits it into a different trigger. See sanitizeTriggerValue.
function buildTrigger(type: NumTriggerType, v1: string, v2: string): string {
  const a = sanitizeTriggerValue(v1), b = sanitizeTriggerValue(v2);
  return type === 'range' ? `range:${a}:${b}` : `${type}:${a}`;
}


/** The mechanically-derived, non-editable title for a value-feedback state — reuses the same
 * symbol summary shown in the trigger-picker chip (e.g. "> 3"), wrapped in a verb so it reads as
 * a title ("Submitted > 3"). Always reflects exactly the condition, so two feedbacks can only
 * collide when they genuinely share the same operator+value (a real setup error). */
function numericalValueTargetedTitle(trigger: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  // No verb prefix. "Submitted {{condition}}" existed only in numerical and slider; every audited
  // block names a chip by the answer that triggers it and nothing else, and the prefix read badly
  // once a condition could contain variable chips ("Submitted {river} to {water}").
  return numericalTriggerSummary(trigger, t);
}

/** The mechanically-derived, non-editable title for a unit-feedback state — always reflects
 * exactly which unit it targets. */

/**
 * Every unit option, with its identity attached.
 *
 * Replaces an enumeration of spellings. That approach materialised `gram`, `grams`, `gramme` and
 * `grammes` as locked options so a string match would find them, which worked for a simple unit and
 * could never work for a compound one: `mg/L` has no enumerable list, and the same unit written
 * `mg/liter` or `g/m³` was still marked wrong. A signature identifies the unit however it is
 * written, so nothing needs listing.
 *
 * Recomputed on every load rather than stored once, because the registry can gain units (a new
 * spelling, a new custom unit) and a signature computed under an older one would silently stop
 * matching.
 */
function withUnitIdentity(unitOptions: UnitOption[]): UnitOption[] {
  return unitOptions.map(o => ({ ...o, ...unitIdentityFor(o.label, 'option') }));
}

/**
 * Every way a unit may be written, as a patch onto whichever unit reference asked.
 *
 * Returned as a patch, rather than as a bare list, so the canonical and an option row can share one
 * derivation despite storing it under different field names — the two must never disagree about
 * what counts as the same unit.
 */
function unitIdentityFor(label: string, shape: 'option'): { spellings?: string[] };
function unitIdentityFor(label: string): { canonicalSpellings?: string[] };
function unitIdentityFor(label: string, shape?: 'option') {
  const spellings = acceptedSpellings(label);
  const list = spellings.length ? spellings : undefined;
  return shape === 'option' ? { spellings: list } : { canonicalSpellings: list };
}

function resyncNumericalUnitTitles(unitOptions: UnitOption[], states: FeedbackState[], t: (k: string, opts?: Record<string, unknown>) => string): FeedbackState[] {
  return states.map(s => (s.type === 'targeted' && s.trigger.startsWith('unit_'))
    ? { ...s, title: numericalUnitTargetedTitle(s.trigger, unitOptions, t) }
    : s);
}

/** A unit-targeted feedback's evaluation always mirrors its target unit's role — keeps the two
 * from silently drifting apart, same fix as Dropdown's `resyncDropdownTargetedEvaluations`.
 * `UnitOption.role` names its incorrect state `'wrong'` (not `'incorrect'`, unlike every other
 * block's role field) — mapped here to the shared `FeedbackState.evaluation` vocabulary. */
function resyncNumericalUnitEvaluations(unitOptions: UnitOption[], states: FeedbackState[]): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted' || !s.trigger.startsWith('unit_')) return s;
    const unit = unitOptions.find(u => u.id === s.trigger.slice('unit_'.length));
    if (!unit || unit.role === 'correct') return s;
    return { ...s, evaluation: unit.role === 'neutral' ? 'neutral' : 'incorrect' };
  });
}

// ─── Value feedback components ────────────────────────────────────────────────

function NumericalTriggerEditor({ trigger, onChange, variables, rolledValues, onPlainFieldFocus, gradingMode, answerTaken, answerConditionChips, fieldId }: {
  /** The angle-mode key for this trigger's fields. A range's two bounds share it, because two
   *  bounds of one measurement disagreeing about degrees is never what anyone meant. */
  fieldId: string;
  trigger: string; onChange: (t: string) => void;
  variables: TaskVariable[]; rolledValues: Record<string, string>;
  /** Releases the shared rich-text toolbar — see FormulaField's `onFocus`. */
  onPlainFieldFocus: () => void;
  /** Decides whether the Answer/Custom picker appears at all — see `TriggerKindSelect`. */
  gradingMode: GradingMode;
  /** Another card already holds the Answer preset. Optional, because the preset only exists in
   *  survey — `TriggerKindSelect` returns null in every other mode — so a graded-only caller
   *  like the error box's targeted bin has no answer to be taken and nothing to show for it. */
  answerTaken?: boolean;
  /** The authored condition, read-only, where the custom editor would be. */
  answerConditionChips?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const kind = triggerKindOf(trigger);
  const { type: trigType, v1, v2 } = parseTrigger(trigger);
  // Same inversion check as the Correct answer's own Between fields (betweenErrors'
  // `inverted` above) — feedback triggers don't support formulas, so this compares the
  // plain floats directly rather than needing resolveForRanges/checkNumericField.
  const loNum = parseFloat(v1);
  const hiNum = parseFloat(v2);
  const rangeInverted = trigType === 'range' && v1 !== '' && v2 !== ''
    && Number.isFinite(loNum) && Number.isFinite(hiNum) && loNum > hiNum;
  const TRIGGER_TYPES: { id: NumTriggerType; symbol: string; labelKey: string }[] = [
    { id: 'exact', symbol: '=',  labelKey: 'blocks.numerical.triggerTypeExact' },
    { id: 'lt',    symbol: '<',  labelKey: 'blocks.numerical.triggerTypeLt'    },
    { id: 'lte',   symbol: '≤',  labelKey: 'blocks.numerical.triggerTypeLte'   },
    { id: 'gt',    symbol: '>',  labelKey: 'blocks.numerical.triggerTypeGt'    },
    { id: 'gte',   symbol: '≥',  labelKey: 'blocks.numerical.triggerTypeGte'   },
    { id: 'range', symbol: '↔',  labelKey: 'blocks.numerical.triggerTypeRange' },
  ];

  return (
    <div className='flex flex-col gap-2'>
      <TriggerKindSelect
        trigger={trigger}
        gradingMode={gradingMode}
        onChange={onChange}
        answerTaken={answerTaken}
        customDefault={buildTrigger('exact', '', '')}
      />
      {/* The PRESET replaces the condition editor: it states the authored answer, read only, so
          the teacher sees what it will match without being able to edit it here. Editing the
          answer upstream moves it. */}
      {kind === 'answer' ? (
        <div className='pointer-events-none opacity-60'>{answerConditionChips}</div>
      ) : (
      <>
      <div className='flex gap-0.5 overflow-hidden rounded-lg border border-ws-border bg-ws-btn p-0.5'>
        {TRIGGER_TYPES.map(({ id, symbol, labelKey }) => (
          <button
            key={id}
            type='button'
            aria-label={t(labelKey as any)}
            onClick={() => onChange(buildTrigger(id, v1, v2))}
            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
              trigType === id ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted hover:text-ws-text'
            }`}
          >
            {symbol}
          </button>
        ))}
      </div>
      {trigType === 'range' ? (
        <>
          {/* STACKED, matching slider's own trigger pair. Two bounds sharing one row inside an
              already-narrow feedback card left each about half a field wide. */}
          <div className='flex flex-col gap-1.5'>
            <FormulaField
              fieldId={`${fieldId}.low`} angleModeKey={fieldId}
              hideEval
              value={v1}
              onChange={v => onChange(buildTrigger('range', v, v2))}
              variables={variables}
              rolledValues={rolledValues}
              hasError={rangeInverted}
              ariaLabel={t('blocks.triggers.triggerRangeMin')}
              placeholder={t('blocks.triggers.triggerRangeMin')}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
            <span className='self-center text-xs text-ws-muted'>{t('blocks.connectors.to')}</span>
            <FormulaField
              fieldId={`${fieldId}.high`} angleModeKey={fieldId}
              hideEval
              value={v2}
              onChange={v => onChange(buildTrigger('range', v1, v))}
              variables={variables}
              rolledValues={rolledValues}
              hasError={rangeInverted}
              ariaLabel={t('blocks.triggers.triggerRangeMax')}
              placeholder={t('blocks.triggers.triggerRangeMax')}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
          </div>
        </>
      ) : (
        <FormulaField
          fieldId={fieldId}
          hideEval
          value={v1}
          onChange={v => onChange(buildTrigger(trigType, v, ''))}
          variables={variables}
          rolledValues={rolledValues}
          ariaLabel={t('blocks.numerical.triggerValuePlaceholder')}
          /* No placeholder. A greyed "value" in an empty condition field reads as a value
                that is already there, which is the same confusion the invented `lt:10` seed caused
                one line up. The field's aria-label still names it for a screen reader; sighted
                users have the condition's own symbol immediately to its left. */
          className='w-full' 
          onFocus={onPlainFieldFocus}/>
      )}
      </>
      )}
    </div>
  );
}

function NumericalTriggerChip({ trigger, evaluation, scenario, angleMode }: {
  trigger: string; evaluation: FeedbackState['evaluation'];
  scenario: Record<string, string>; angleMode: AngleMode;
}) {
  const { t } = useTranslation();
  const chipColor =
    chipToneClass(evaluation);
  return (
    // `full`, not `auto`: this chip holds a whole condition, and with no maximum it pushed its own
    // row a few pixels past the collapsed card's edge. Bounded by the parent, its label wraps.
    <span className={chipClass(chipColor, { width: 'full', grow: true })}>
      <span className={TRIGGER_CHIP_TEXT_CLASS}>
        {numericalTriggerChipContent(trigger, angleMode, scenario, t)}
      </span>
    </span>
  );
}


// ─── Unit feedback trigger components ─────────────────────────────────────────
// Unit feedback targets one non-correct unit option (correct units can't be targeted,
// same "feedback can't target correct" rule as everywhere else) — a simple picker rather
// than the numeric condition editor above, since a unit choice is a discrete pick, not
// a range.

// Role-based color/icon, matching MCQ's evaluation-based trigger dropdown/chip —
// 'wrong' reads like MCQ's incorrect (X, error color), 'neutral' like MCQ's neutral
// (Minus, neutral color). Correct units are never targetable here (see `targetable`
// below), so that case never actually renders, but is handled for completeness.
/** A unit option's role in chip terms. The last copy of the tone+icon table, keyed on `role`
 * rather than on an evaluation — same three states under two vocabularies, so it translates
 * instead of restating them. */
/**
 * The chip tone for a unit's role — the VERDICT half of which the mode can take away.
 *
 * `correct` is acceptance ("the question takes this unit") and survives every mode, so it keeps
 * its tone. `wrong` is a verdict, so outside Has Correct it reads as `neutral` alongside the
 * role control that no longer offers it. Three chip sites share this, and all three were drawing
 * a ✗ in Participation: the trigger dropdown's current value, each option inside it, and the
 * collapsed card's summary.
 */
function unitRoleEvaluation(
  role: UnitOption['role'] | undefined,
  gradingMode: NumericalBlockData['gradingMode'],
): ChipEvaluation {
  if (role === 'correct') return 'correct';
  return role === 'wrong' && gradingMode === 'graded' ? 'incorrect' : 'neutral';
}

function NumericalUnitTriggerEditor({ trigger, unitOptions, gradingMode, onChange }: { trigger: string; unitOptions: UnitOption[]; gradingMode: NumericalBlockData['gradingMode']; onChange: (t: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const optionBtnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedId = trigger.slice('unit_'.length);
  const targetable = unitOptions.filter(u => u.role !== 'correct');
  const current = targetable.find(u => u.id === selectedId);
  const unitEval = unitRoleEvaluation(current?.role, gradingMode);

  useEffect(() => {
    if (!open) return;
    const selectedIdx = targetable.findIndex(u => u.id === selectedId);
    const focusIdx = selectedIdx >= 0 ? selectedIdx : 0;
    const id = requestAnimationFrame(() => { optionBtnRefs.current[focusIdx]?.focus(); });
    return () => cancelAnimationFrame(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleListboxKeyDown(e: KeyboardEvent<HTMLUListElement>) {
    const refs = optionBtnRefs.current.filter((r): r is HTMLButtonElement => r !== null);
    const currentIdx = refs.indexOf(document.activeElement as HTMLButtonElement);
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); refs[Math.min(currentIdx + 1, refs.length - 1)]?.focus(); break;
      case 'ArrowUp':   e.preventDefault(); refs[Math.max(currentIdx - 1, 0)]?.focus(); break;
      case 'Home':      e.preventDefault(); refs[0]?.focus(); break;
      case 'End':       e.preventDefault(); refs[refs.length - 1]?.focus(); break;
      case 'Escape':    e.preventDefault(); setOpen(false); btnRef.current?.focus(); break;
    }
  }

  return (
    <div
      ref={wrapperRef}
      className='relative w-full min-w-0'
      onBlur={(e) => { if (!wrapperRef.current?.contains(e.relatedTarget as Node)) setOpen(false); }}
    >
      <button
        ref={btnRef}
        type='button'
        onClick={() => setOpen(v => !v)}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={t('blocks.numerical.unitTriggerPlaceholder')}
        className={chipButtonClass(unitEval)}
      >
        <ChipEvalIcon evaluation={unitEval} />
        <ChipDivider />
        <span className='min-w-0 flex-1 truncate text-left'>{current?.label || t('blocks.numerical.unitTriggerPlaceholder')}</span>
        <ChevronDown aria-hidden='true' className={`h-3 w-3 shrink-0 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul
          role='listbox'
          aria-label={t('blocks.numerical.unitTriggerPlaceholder')}
          onKeyDown={handleListboxKeyDown}
          className='absolute left-0 top-full z-20 mt-1 w-max min-w-full max-w-[min(22rem,80vw)] max-h-48 overflow-y-auto overscroll-contain rounded-lg border border-ws-border bg-ws-elevated py-1 shadow-lg'
        >
          {targetable.map((u, i) => {
            const optEval = unitRoleEvaluation(u.role, gradingMode);
            return (
              <li key={u.id}>
                <button
                  ref={(el) => { optionBtnRefs.current[i] = el; }}
                  type='button'
                  role='option'
                  aria-selected={u.id === selectedId}
                  tabIndex={-1}
                  onClick={() => { onChange(`unit_${u.id}`); setOpen(false); btnRef.current?.focus(); }}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-ws-hover focus:bg-ws-hover focus:outline-none ${u.id === selectedId ? 'font-medium text-ws-primary' : 'text-ws-text'}`}
                >
                  <ChipEvalIcon evaluation={optEval} />
                  <ChipDivider />
                  {u.label || t('blocks.numerical.unitOptionLabel')}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function NumericalUnitTriggerChip({ trigger, unitOptions, gradingMode }: { trigger: string; unitOptions: UnitOption[]; gradingMode: NumericalBlockData['gradingMode'] }) {
  const { t } = useTranslation();
  const unit = unitOptions.find(u => u.id === trigger.slice('unit_'.length));
  const unitEval = unitRoleEvaluation(unit?.role, gradingMode);
  return (
    <span className={chipClass(unitEval, { width: 'auto' })}>
      <ChipEvalIcon evaluation={unitEval} />
      <ChipDivider />
      {unit?.label || t('blocks.numerical.unitTriggerPlaceholder')}
    </span>
  );
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function NumericalBlock({ block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver, isMain = false, embedded, onClose, mobileIsEditing, blocks, onRename }: NumericalBlockProps) {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /**
   * THE SCENARIO POINTER, which belongs to the LEADER.
   *
   * At runtime a whole group resolves from one key, `@version:<leaderId>` (§3.1), so the editor
   * uses the same one: the leader's prose on the canvas, this block's live test and the row
   * being authored cannot name three different scenarios. Keyed on this block when it follows
   * nothing, where it is simply this block's own test pointer and goes unread — a hook cannot
   * be called conditionally.
   */
  const storedForLink = initData(block.data);
  /** The leader itself, not just its variants — the ghosted stem renders by its TYPE. */
  const leaderBlock = (blocks ?? []).find(b => b.id === storedForLink.followsBlockId);
  const leaderVersions = ((blocks ?? []).find(b => b.id === storedForLink.followsBlockId)?.data as
    { versions?: DisplayVersion[] } | undefined)?.versions;
  const [leaderShownRaw, setLeaderShown] = useVersionInForce(storedForLink.followsBlockId ?? block.id, 'test');
  const shownScenarioId = storedForLink.followsBlockId
    ? (leaderShownRaw ?? leaderVersions?.[0]?.id) : undefined;

  /**
   * WHAT THE TEST BAR PAGES WHEN THIS BLOCK FOLLOWS — the leader's scenarios, not its own bank.
   *
   * Same shape `useScenarioFollower` returns for the blocks that use it, and the same one
   * Dropdown and MCQ already build. A follower has no bank of its own (§2.2 makes the two
   * exclusive), so its test bar had nothing to page and a teacher could not step the scenarios
   * from the surface they were testing on — the only pager was in Answer Setup. The pointer is
   * shared, so this and the leader's own card move together rather than disagreeing.
   */
  const testPager = storedForLink.followsBlockId
    ? { versions: (leaderVersions ?? []) as { id: string }[], activeId: shownScenarioId,
      onSelect: setLeaderShown, noun: 'scenario' as const }
    : null;
  /**
   * Through the version AND the scenario: the block's own top-level answer is vestigial once
   * either selector exists, and they are mutually exclusive so the order cannot matter.
   */
  const resolveForEditor = (d: NumericalBlockData, versionId: string | undefined) =>
    applyScenarioRow(
      applyNumericalVersion(d, versionId) as unknown as Record<string, unknown>,
      shownScenarioId, NUMERICAL_VERSION_KEYS,
    ) as unknown as NumericalBlockData;
  const initial = useRef(resolveForEditor(initData(block.data), editorVersion));
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];
  const collapsedContentRef = useRef<HTMLDivElement | null>(null);

  // Shared toolbar state — a single toolbar box anchored at the top of the editing area
  // (and, separately, the feedback panel) targets whichever RichTextEditor last received
  // focus, across the question editor, every value/unit FeedbackCard, and every Math Error
  // category's content editor (see MathErrorRow below) — one toolbar for the whole surface
  // instead of each editor showing its own on focus.
  const sharedToolbar = useSharedEditorToolbar();
  const { setActiveEditor, activeEditorRef } = sharedToolbar;
  /** Releases the shared rich-text toolbar. Every plain and equation field in this editor
   *  focuses through here — see FormulaField's `onFocus` for why. */
  const disconnectSharedToolbar = sharedToolbar.disconnect;
  const unitLabelEditorRef = useRef<Editor | null>(null);
  const questionEditorRef = useRef<Editor | null>(null);
  // Numerical was the only block without this: it passed autoFocus={embedded} and nothing
  // else, so opening a freshly created block put the cursor nowhere while all ten others
  // put it in the question box.
  const shouldFocusEditor = useRef(false);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Declared here, above the state it decides the shape of: only the EDITING instance holds state.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question && !initial.current.answers[0]?.value));
  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);

  /**
   * One stateful copy, not two.
   *
   * Every block on screen is TWO mounted instances at once on desktop — the collapsed card in the
   * canvas list, and the editor panel when it is selected. Only one can be editing, so only that
   * one holds state; the card derives from props every render, cannot go stale, and so never needs
   * rebuilding. The collapsed view below already reads `initData(block.data)` directly for
   * everything it draws, so it sees no change at all.
   *
   * The rest — telling an undo from the echo of this block's own write, and why the markers that
   * do it have to be state rather than refs — is in `use-restored-data`, which every migrated
   * block shares.
   */
  const [editedFields, setFields] = useState<NumericalBlockData>(initial.current);
  // Declared before the re-derive, which runs during render and needs to reach the feedback hook's
  // re-sync — the hook itself is created further down.
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);

  const restored = useRestoredData<NumericalBlockData>({
    raw: block.data,
    isEditing,
    derive: (raw) => resolveForEditor(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    current: editedFields,
    apply: seedEditorFrom,
  });
  /** Push one version's content into the editor's local state — an undo, another device's save
   *  and switching version are the same act from here. */
  function seedEditorFrom(next: NumericalBlockData) {
    setFields(next);
    /*
     * From `block.data`, NOT from `next`.
     *
     * Every other block's editor type carries its feedback, so re-seeding from the version-
     * resolved data is right there. Numerical's does not — feedback is threaded separately — so
     * `next.feedback` is always undefined, and resyncing to that silently emptied the feedback
     * model. `undo-sequences` D2 caught it: the ring had no trigger left to point at.
     */
    /*
     * A FOLLOWER's feedback belongs to the ROW, not the block (§5): the explanation is
     * scenario-specific — "the river is moving right, so the velocity is positive" — so paging
     * to another scenario must bring its own feedback with it, exactly as it brings its answer.
     */
    const row = pickScenarioBranch(initData(block.data).scenarioBranches, shownScenarioId);
    resyncFeedbackRef.current?.(row ? row.feedback : (block.data as { feedback?: unknown }).feedback);
  }

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(resolveForEditor(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(resolveForEditor(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = () => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump();
  };

  const fields = isEditing ? editedFields : restored.data;

  /*
   * THE THREE PRECISION SETTINGS, gated one at a time rather than as a block.
   *
   * They are independent questions — notation is not precision — so a question using sig figs
   * shows that row and not the other two. Each is what the owner called "stripping down the
   * overpowered numeric block so it doesn't shock a novice teacher": every one of them rejects a
   * numerically correct answer for the way it was written, which is a second thing to teach
   * before it is a thing to grade.
   *
   * `integerOnly` and the unit mechanism sit in this same section and are deliberately NOT here.
   * Note that `integerOnly` CLEARS all three when it is switched on, so an ungated control writes
   * to gated state — one more reason a setting in use keeps its row: the teacher watches it clear
   * instead of losing it silently.
   */
  const showExponential = useExtraBlockSetting(
    fields.requireExponentialNotation || initial.current.requireExponentialNotation);
  const showSigFigs = useExtraBlockSetting(
    fields.sigFigsRequired || initial.current.sigFigsRequired);
  const showUncertainty = useExtraBlockSetting(
    fields.uncertaintyRequired || initial.current.uncertaintyRequired);
  /**
   * The scenario row on screen — graded-only, like every answer key, and read off the LEADER's
   * pointer above rather than an index of this block's own.
   *
   * Placed here rather than beside the pointer because it needs the LIVE grading mode: a block
   * switched to Participation has nothing for these rows to say, and `storedForLink`'s copy
   * lags a mode change by one save.
   */
  const activeScenario = fields.gradingMode === 'graded' && storedForLink.scenarioBranches?.length
    ? (storedForLink.scenarioBranches.find(b => b.scenarioId === shownScenarioId)
      ?? storedForLink.scenarioBranches[0])
    : undefined;
  const activeScenarioVersion = activeScenario
    ? scenarioInForce(leaderVersions, activeScenario.scenarioId) : null;
  /**
   * The leader's variant on the shared test pointer, for the TEST surface's ghosted copy of
   * the stem — deliberately NOT `activeScenarioVersion`.
   *
   * That one is gated on graded mode, correctly: it names the row whose ANSWER KEY is being
   * authored, and a participation block has none. The prose is a different question. A
   * participation block still asks about the scenario, and a teacher testing one still needs
   * to read it, so this reads the pointer and nothing else.
   */
  const shownScenarioVersion = storedForLink.followsBlockId
    ? scenarioInForce(leaderVersions, shownScenarioId) : null;
  const ghostedScenario = (
    <ScenarioStimulus leader={leaderBlock} version={shownScenarioVersion as Record<string, unknown> | null} ghosted />
  );
  /**
   * PAGING TO ANOTHER SCENARIO RE-SEEDS THE EDITOR, the same way switching version does.
   *
   * Necessary here and not in Dropdown because of where the answer lives: Dropdown derives the
   * active row's roles per render, while this block holds its resolved answer in `fields`
   * state. Without this the fields keep showing the row you came from, and the next keystroke
   * writes that row's answer into the one you moved to.
   *
   * The shared hook, not an effect of its own — it already carries the "only the pointer"
   * dependency rule that keeps a re-seed from landing in the middle of live typing.
   */
  useVersionSeeding(shownScenarioId, () => {
    seedEditorFrom(resolveForEditor(initData(block.data), editorVersion));
  });

  /**
   * Follow a leader, or stop. The rows are a PROJECTION of its variants (§4.1), so this reads
   * the leader's bank rather than asking the teacher to build a list — one row per variant,
   * each a copy of the answer setup the block already had, marked unreviewed because a copy is
   * not a decision.
   */
  /** A bank and a link cannot coexist (§2.2), so linking a banked block asks first — §7.1. */
  const [pendingLink, setPendingLink] = useState<string | null>(null);

  function linkToLeader(followsBlockId: string | undefined, remap?: 'remap' | 'empty') {
    const current = initData(block.data);
    if (followsBlockId && !remap && (current.versions?.length ?? 0) > 1) {
      setPendingLink(followsBlockId);
      return;
    }
    if (!followsBlockId) {
      // Local state too. The editor writes `fields` back wholesale on every edit, so a `fields`
      // that still carries the link would re-link the block on the next keystroke — and the
      // mirror of that, below, is what stopped the FIRST keystroke after linking from wiping it.
      setFields(f => ({ ...f, followsBlockId: undefined, scenarioBranches: undefined }));
      emit(versionStructure(block.data, {
        ...current, followsBlockId: undefined, scenarioBranches: undefined,
      } as unknown as Record<string, unknown>));
      return;
    }
    const leaderBank = ((blocks ?? []).find(b => b.id === followsBlockId)?.data as
      { versions?: ScenarioLeaderVersion[] } | undefined)?.versions;
    /*
     * COLLAPSE THE BANK TO VERSION 1 FIRST, whichever way the teacher answered.
     *
     * A block holds one question and a follower has no bank to pick from, so something has to
     * become that question — and the modal promises it is version 1's.
     *
     * `initData`'s own copy is NOT version 1's. `writeFieldVersion` freezes the block's
     * top-level question when the first version is made and never writes to it again, so it
     * holds whatever version 1 said THEN. Edit version 1 afterwards and the two diverge
     * silently: reported as a migration that restored a question the teacher had replaced
     * long ago. Dropdown already resolved version 1 for this reason; this did not.
     */
    const v1 = current.versions?.[0];
    const base = (current.versions?.length ?? 0) > 1 && v1
      ? applyNumericalVersion(current, v1.id)
      : current;
    /*
     * Numerical's feedback rides outside its data (see `emitFields`), so applying a version
     * cannot bring it along — it has to be lifted off the version by hand.
     */
    const baseFeedback = ((current.versions?.length ?? 0) > 1
      ? (v1?.feedback as { states: FeedbackState[] } | undefined) : undefined) ?? feedback;
    /** The block's own answer key, for a row no version reached — version 1's, after the collapse. */
    const fromBlock = () => ({
      feedback: baseFeedback,
      ...Object.fromEntries(NUMERICAL_VERSION_KEYS.map(k => [k, base[k]])),
    } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>);
    /*
     * REMAP: this block's versions become its scenario rows, paired by position. The value
     * family carries the same key set either way — §5.1's split is a property of the block,
     * not of what selects it — so the version's payload transfers field for field.
     */
    const { branches } = remap === 'remap'
      ? remapVersionsToScenarios<FieldVersion<{ states: FeedbackState[] }>, ScenarioFieldBranch<{ states: FeedbackState[] }>>(
        leaderBank, current.versions,
        v => ({
          feedback: v.feedback,
          ...Object.fromEntries(NUMERICAL_VERSION_KEYS.filter(k => k in v).map(k => [k, v[k]])),
        } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>),
        fromBlock,
      )
      : syncScenarioBranches<ScenarioFieldBranch<{ states: FeedbackState[] }>>(
        leaderBank, current.scenarioBranches, fromBlock,
      );
    // Same backfill Dropdown carries: a row arriving with no feedback gets the block's, so
    // relinking repairs rather than faithfully restoring.
    const healed = branches.map((b): ScenarioFieldBranch<{ states: FeedbackState[] }> =>
      (b.feedback?.states?.length ? b : { ...b, feedback: baseFeedback }));
    // From `base`, not from `f`: the editor was showing whichever version was open, and after
    // the collapse the block holds version 1's question.
    setFields({ ...base, followsBlockId, scenarioBranches: healed, versions: undefined });
    resyncFeedbackRef.current?.(baseFeedback);
    emit(versionStructure(block.data, {
      // `base`, not `current`: version 1's question and answer key become the block's, which
      // is what the modal promised.
      ...base, feedback: baseFeedback, followsBlockId, scenarioBranches: healed, versions: undefined,
    } as unknown as Record<string, unknown>));
  }

  /** The projection, kept live — a variant added to the leader becomes a row here. */
  useScenarioRowProjection<ScenarioFieldBranch<{ states: FeedbackState[] }>>({
    followsBlockId: storedForLink.followsBlockId,
    blocks,
    rows: storedForLink.scenarioBranches,
    template: () => ({
      feedback,
      ...Object.fromEntries(NUMERICAL_VERSION_KEYS.map(k => [k, fields[k]])),
    } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>),
    onSync: (next) => {
      const current = initData(block.data);
      emit(versionStructure(block.data, {
        ...current, scenarioBranches: next,
      } as unknown as Record<string, unknown>));
    },
  });

  const remapModal = pendingLink ? (
    <ScenarioRemapModal
      versionCount={storedForLink.versions?.length ?? 0}
      variantCount={((blocks ?? []).find(b => b.id === pendingLink)?.data as
        { versions?: unknown[] } | undefined)?.versions?.length ?? 0}
      leaderName={(blocks ?? []).find(b => b.id === pendingLink)?.name ?? ''}
      onCancel={() => setPendingLink(null)}
      onRemap={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'remap'); }}
      onLinkEmpty={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'empty'); }}
    />
  ) : null;

  /** Browse-only, which is §4.1's projection rule in the UI: the rows are the leader's. */
  const scenarioPager = activeScenario ? (
    <VersionSelector
      versions={(leaderVersions ?? []) as { id: string }[]}
      activeId={activeScenario.scenarioId}
      onSelect={setLeaderShown}
      noun='scenario'
    />
  ) : null;
  const dismissUnreviewed = () => {
    const current = initData(block.data);
    emit(versionStructure(block.data, {
      ...current,
      scenarioBranches: markScenarioReviewed(current.scenarioBranches ?? [], activeScenario!.scenarioId),
    } as unknown as Record<string, unknown>));
  };

  const emit = (data: Record<string, unknown>, opts?: BlockUpdateOptions) => {
    restored.markEmitted(data);
    onUpdate(data, opts);
  };

  /* One version per row of the driver's answer space. `addNumericalVersion` already knows this
     block's answer key, so nothing numerical is named here. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({
    block, data: initData(block.data), emit, addVersion: addNumericalVersion,
  });


  /**
   * Every field write, routed into the version it belongs to — see mcq-block.tsx's note.
   *
   * FEEDBACK is handled by hand here, and only here. Numerical's editor type does not carry it —
   * it is threaded separately through `useInputBlockFeedback` — so `initData` produces no
   * `feedback` key, and the version head would copy that absent value back onto the block and
   * wipe the real feedback. The caller's own `feedback` is the one that should survive; a caller
   * that did not send one must not cause a key to be written at all.
   */
  const emitFields = (
    /* `mathErrorReachable` is a DERIVED flag the block stores alongside its data; it is not part
       of the editor's own type and never part of a version's answer key. */
    raw: Partial<NumericalBlockData> & { feedback?: unknown; mathErrorReachable?: unknown },
    opts?: BlockUpdateOptions,
  ) => {
    const base = initData(block.data);
    /*
     * WHAT THE BLOCK VARIES WITH IS THE STORED BLOCK'S, never this payload's.
     *
     * Every caller here hands over `{ ...fields }` — the editor's full local copy, seeded at
     * mount and deliberately NOT re-seeded when the block's own `emit` writes (`markEmitted`
     * suppresses it). The driven-link menu writes through that `emit`, so dropping a link
     * leaves the copy holding the old address, and the next keystroke puts it back. Reported
     * as "if i set to none and then close the block, when i return it's reconnected to the
     * same block again"; the mirror of it, a FIRST keystroke clearing a link that had just
     * been made, is `12b64a52`.
     *
     * Here rather than only in the version writers because this block has a third path they
     * never see: with no bank and no scenario rows it emits the payload directly.
     */
    const next = restateLinkFields(base, raw);
    /*
     * An unbanked block emits EXACTLY what the caller passed, as it always did.
     *
     * Widening it to `{ ...base, ...next }` changed what the undo machinery sees: the ring reads
     * the diff between what was emitted and what was stored, and a payload carrying every
     * normalised field instead of the handful that changed stopped it isolating the one the
     * teacher touched — `undo-sequences` D2 caught exactly that. Only a banked block needs the
     * merge, because only then is there a version to route the write into.
     */
    /*
     * A SCENARIO ROW takes the write before a version can, and the two cannot both apply (§2.2).
     *
     * The same bargain `writeNumericalVersion` makes, one selector over: the answer-key fields
     * go into the row showing, the shared ones stay on the block. It has to happen on the
     * unbanked path too — a follower has no versions by construction, so the early return below
     * would otherwise emit the edit straight onto the block and every scenario would share one
     * answer.
     */
    if (base.scenarioBranches?.length) {
      const merged = { ...base, ...next } as NumericalBlockData;
      const written = writeScenarioRow(
        base as unknown as Record<string, unknown>, shownScenarioId,
        merged as unknown as Record<string, unknown>, NUMERICAL_VERSION_KEYS,
      ) as Record<string, unknown>;
      /*
       * The feedback goes into the ROW as well, where the version path puts it on the block.
       * Same reason the answer does: it is scenario-specific. The block's own copy is left
       * alone and becomes vestigial, exactly as its answer fields do.
       */
      if ('feedback' in next && shownScenarioId) {
        written.scenarioBranches = (written.scenarioBranches as ScenarioFieldBranch<{ states: FeedbackState[] }>[])
          .map(b => (b.scenarioId === shownScenarioId
            ? { ...b, feedback: next.feedback as { states: FeedbackState[] }, unreviewed: undefined } : b));
      }
      delete written.feedback;
      emit(written, opts);
      return;
    }
    if (!base.versions?.length) {
      emit(next as Record<string, unknown>, opts);
      return;
    }
    const written = writeNumericalVersion(
      base, editorVersion, { ...base, ...next } as NumericalBlockData,
    ) as unknown as Record<string, unknown>;
    if ('feedback' in next) written.feedback = next.feedback;
    else delete written.feedback;
    emit(written, opts);
  };

  const scenarioLinkMenu = (
    <ScenarioLinkMenu
      block={block}
      blocks={blocks}
      value={storedForLink.followsBlockId}
      onChange={linkToLeader}
      emit={emit}
      applyVersion={applyNumericalVersion}
    />
  );

  // An answer typed as an equation is malformed at almost every keystroke on the way to being
  // valid, and `answer-field-invalid` fires on exactly that. Two filters, because the card's list
  // is built from `block.data` and the editor's from `fields`, and the two surfaces are often
  // rendered by different instances of this component. No `equation`: Numerical has no branching
  // selector, only equation-valued answers.
  const { settleLive, settleStored } = useIssueSettling(
    { answers: answerPositionsKey(fields) },
    { answers: answerPositionsKey(initData(block.data)) },
  );
  const currentDataRef = useRef(fields);
  currentDataRef.current = fields;

  // Feedback is stale (suppressed, not deleted) once its target unit is marked correct — value
  // feedback is deliberately never hidden this way (see visibleValueFeedbackStates below).
  const isStaleTargeted = (s: FeedbackState) => isUnreachableUnitTargeted(s.trigger, fields);

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle =
    fields.gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle') :
    fields.gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle') :
    t('blocks.feedbackDefaults.anyResponseTitle');

  const { feedback, setFeedback, resyncFeedback, updateFeedbackState, deleteFeedbackState, getTitle } = useInputBlockFeedback({
    initialRaw: block.data.feedback,
    defaults: {
      unansweredTitle:   t('blocks.feedbackDefaults.noAnswerTitle'),
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
      incorrectTitle:    t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent:  `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle:      positiveSlotTitle,
      correctContent:    fields.gradingMode === 'graded' ? positiveSlotContent.correct : fields.gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct:    positiveSlotTitle,
      incorrect:  t('blocks.feedbackDefaults.incorrectTitle'),
      extra: {
        incomplete_answer: t('blocks.feedbackDefaults.incompleteAnswerTitle'),
        unit_error:     t('blocks.numerical.unitErrorFeedback'),
        notation_error: t('blocks.numerical.notationErrorFeedback'),
        sigfig_error:   t('blocks.numerical.sigfigErrorFeedback'),
        uncertainty_sigfig_error:   t('blocks.numerical.uncertaintySigfigFeedback'),
        agreement_error:            t('blocks.numerical.agreementErrorFeedback'),
        math_error_sign_flip:          t('blocks.numerical.mathErrorSignFlipFeedback'),
        math_error_dropped_parens:     t('blocks.numerical.mathErrorDroppedParensFeedback'),
        math_error_exponent_misread:   t('blocks.numerical.mathErrorExponentMisreadFeedback'),
        math_error_swapped_operands:   t('blocks.numerical.mathErrorSwappedOperandsFeedback'),
        math_error_ignored_precedence: t('blocks.numerical.mathErrorIgnoredPrecedenceFeedback'),
        math_error_wrong_angle_mode:   t('blocks.numerical.mathErrorWrongAngleModeFeedback'),
        math_error_log_ln_confusion:   t('blocks.numerical.mathErrorLogLnFeedback'),
      },
    },
    extraDefaults: numericalExtraDefaults(t),
    positiveTrigger: positiveTriggerForMode(fields.gradingMode),
    onUpdate: (nextFeedback) => emitFields({ ...currentDataRef.current, feedback: nextFeedback }),
  });

  resyncFeedbackRef.current = resyncFeedback;

  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  const hasVariables = hasRollableVariables(variables);
  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>(() => rollVariableValues(variables));
  // Preview must show ONE task-level roll shared by every block (see TaskPreviewRollContext): a task
  // whose blocks all read {n} has to agree about n, and seeding Preview from this block's own roll made
  // them disagree. Block-level testing keeps its private roll plus reroll, which is the whole point of
  // it — a teacher steps through combinations for the one block they're testing.
  const taskPreviewRoll = useTaskPreviewRoll();
  const rerollRecalls = useRerollRecalls();
  /* Recalled answers ride the same map the variables do — see `useRolledWithRecalls`. The
     equation field, the answer bar and the issue list then all read one roll. */
  const rolledValues = useRolledWithRecalls(isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues);
  /* Just the recalls, for `scenarioValues` far below. A HOOK, so it is called here and not
     beside its one use: down there it sits past the `isPreview` early return, and that prop
     FLIPS in place when a teacher switches Edit to Test, so the hook count would change
     between two renders of the same mounted block. */
  const recallValues = useRecallFormulaValues(rolledValues);
  function reroll() { setRolledValues(rollVariableValues(variables)); }

  // Detect variable renames and update local formula strings + question HTML.
  // NumericalBlock owns its fields in local state so the workspace-level block
  // data rename (which handles prop-reading blocks like TextBlock) is not enough.
  const prevVarsRef = useRef(variables);
  useEffect(() => {
    const prev = prevVarsRef.current;
    prevVarsRef.current = variables;
    const renames: Record<string, string> = {};
    for (const curr of variables) {
      const p = prev.find(v => v.id === curr.id);
      if (p?.name && curr.name && p.name !== curr.name) renames[p.name] = curr.name;
    }
    if (!Object.keys(renames).length) return;
    const applyFormula = (val: string) =>
      Object.entries(renames).reduce((s, [o, n]) => renameVarInFormula(s, o, n), val);
    const applyHtml = (html: string) =>
      Object.entries(renames).reduce((s, [o, n]) => renameVarInHtml(s, o, n), html);
    setFields(prev => ({
      ...prev,
      question:       applyHtml(prev.question),
      answers:        prev.answers.map(a => ({ ...a, value: applyFormula(a.value) })),
      betweenAnswers: prev.betweenAnswers.map(a => ({ ...a, low: applyFormula(a.low), high: applyFormula(a.high) })),
      threshold:      applyFormula(prev.threshold),
    }));
  }, [variables]);  

  // ── Live test state ─────────────────────────────────────────────────────────
  // The card (while selected), the phone Test tab and Preview all answer through these — one
  // submitted answer, graded by `evaluateNumerical`. `testValue` stays a raw STRING rather than a
  // number because sig-fig checking counts digits from what was actually typed: parsing "1.50" to
  // 1.5 here would destroy the one thing that check reads.
  /**
   * Seeded from the last attempt on a revisit -- see `usePriorAnswer`. Walking back into a task
   * does not wipe it, and this is the half that was missing: the counts and the roll already
   * survived, so a student sent to remediation and back kept their leniency but lost their work.
   *
   * The DRAFT is seeded as well as the snapshot, because here the two are separate pieces of
   * state: restoring only `submitted` would show the student their feedback above an empty box.
   */
  const prior = usePriorAnswer<NumericalAnswer>();
  const [testValue, setTestValue] = useState(prior?.value ?? '');
  const [testUnit, setTestUnit] = useState(prior?.unit ?? '');
  const [testUncertainty, setTestUncertainty] = useState(prior?.uncertainty ?? '');
  const [submitted, setSubmitted] = useState<NumericalAnswer | null>(prior ?? null);
  // The SUBMITTED value, never the draft: recording the draft would make the attempt a snapshot
  // of the student's typing rather than of what they chose to hand in.
  useReportAnswer(submitted ?? undefined);
  const [continuous, setContinuous] = useState(true);
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');

  const currentAnswer = (
    value = testValue, unit = testUnit, uncertainty = testUncertainty,
  ): NumericalAnswer => ({ value, unit, uncertainty });
  function handleTestSubmit() { setSubmitted(currentAnswer()); }
  function handleTestReset() {
    setTestValue(''); setTestUnit(''); setTestUncertainty(''); setSubmitted(null);
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next) setSubmitted(currentAnswer());
  }
  /** Commits a field and, in continuous mode, grades immediately. Takes the new value explicitly:
   * the setter has not flushed by the time this runs. */
  function commitTest(patch: { value?: string; unit?: string; uncertainty?: string }) {
    const next = currentAnswer(
      patch.value ?? testValue, patch.unit ?? testUnit, patch.uncertainty ?? testUncertainty);
    if (patch.value !== undefined) setTestValue(patch.value);
    if (patch.unit !== undefined) setTestUnit(patch.unit);
    if (patch.uncertainty !== undefined) setTestUncertainty(patch.uncertainty);
    if (continuous) setSubmitted(next);
  }

  /**
   * DESELECTING resets the test, so the next teacher to open the card sees a clean surface rather
   * than someone else's half-finished answer — same convention as every other tested block.
   *
   * ON THE TRANSITION, never on mount. An effect keyed on `isSelected` also runs the first time,
   * and in Preview a block is ALWAYS unselected — so this fired on arrival and wiped the answer
   * `usePriorAnswer` had just seeded. That is why a revisit showed an empty box however the
   * task's On Return policy was set: the seeding worked and was erased a tick later.
   *
   * `wasSelected` makes the effect answer "did it just become unselected" rather than "is it
   * unselected", which is what the comment above always meant.
   */
  const wasSelected = useRef(isSelected);
  useEffect(() => {
    if (isSelected) setRolledValues(rollVariableValues(variables));
    else if (wasSelected.current) { handleTestReset(); setPhoneTestMode('edit'); }
    wasSelected.current = isSelected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);

  // Phone's own entry into the Test tab. `isSelected` is always false on phone (selection is
  // tracked via mobileEditingId), so the effect above never fires there — without this, formula
  // answers would sit unresolved until the teacher hit Reroll.
  useEffect(() => {
    if (phoneTestMode === 'test') setRolledValues(rollVariableValues(variables));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneTestMode]);

  // Preview's single page-level Submit. Reacts to the signal CHANGING, never to a per-block click.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) setSubmitted(currentAnswer());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  const [armedAnswerId,   setArmedAnswerId]   = useState<string | null>(null);
  const [armedBetweenId,  setArmedBetweenId]  = useState<string | null>(null);
  const [armedUnitOptId,  setArmedUnitOptId]  = useState<string | null>(null);
  // Whether the answer bar entries are expanded beyond the first 3.
  const [barExpanded, setBarExpanded] = useState(false);
  /**
   * The one scenario the whole editor reads — Answer tab and Feedback tab alike.
   *
   * There were three states doing this job and they disagreed by construction: `barRolled` decided
   * whether the answer bar showed numbers, `feedbackTestValues` decided whether the feedback panel
   * did, and the ambient roll behind both was the CARD's — seeded at random on mount, so the editor
   * opened already showing one arbitrary sample and calling it nothing in particular. A teacher
   * could roll the bar to one set of numbers and the panel to another and see two answers to one
   * question on two tabs.
   *
   * `null` is at rest and means chips stay chips, everywhere. That is the locked three-mode rule
   * reaching the one surface that was exempt from it.
   */
  const [scenario, setScenario] =
    useState<{ mode: 'min' | 'max' | 'random'; values: Record<string, string> } | null>(null);

  const addAnswerBtnRef    = useRef<HTMLButtonElement>(null);
  const addBetweenBtnRef   = useRef<HTMLButtonElement>(null);
  const addUnitOptBtnRef   = useRef<HTMLButtonElement>(null);
  const pendingFocusUnitOptId = useRef<string | null>(null);

  // Self-heals `mathErrorReachable` — which math-error categories can actually produce a
  // distinguishable wrong answer for the CURRENT formula, the same "Active"/"Inactive" the
  // Math Error feedback rows below show per-category — the moment this block mounts, not just
  // when its editor closes (handleDone, further down). Without this, a block configured before
  // this field existed would show every math-error category as a live possibility in Flow until
  // a teacher happened to reopen it. Placed here (before the isPreview/collapsed early returns)
  // so the hook always runs, unconditionally, regardless of which view this render produces.
  //
  // Every block on screen is actually TWO mounted instances at once on desktop — one collapsed
  // in the main canvas list, one embedded in the side panel when selected — both running this
  // same effect independently. Deriving from `fields`/`feedback` (this instance's OWN local
  // state, frozen at ITS mount) and writing `{ ...fields, feedback, mathErrorReachable }` back
  // was a real bug: the collapsed sibling's `fields` never tracks edits made in the embedded
  // instance, so its copy of this effect could re-fire on a `block.data` change from that
  // sibling's edit, see its own stale (pre-edit) fields, and clobber the just-written change by
  // writing its whole stale snapshot back over it. Deriving from `initData(block.data)` (the
  // live PROP, identical in both instances) and writing only `{ ...block.data, mathErrorReachable
  // }` (merged onto the current prop, not onto a local copy) makes this safe regardless of which
  // instance's copy of the effect happens to fire.
  useEffect(() => {
    const liveData = initData(block.data);
    if (!liveData.mathErrorEnabled) return;
    // The PROBE, not a roll. This writes `mathErrorReachable` back to persisted data, and every
    // block on screen is two mounted instances with two independent random rolls — so a roll here
    // meant the two could compute different answers and take turns overwriting each other's, and
    // what Flow then read depended on which one fired last.
    const resolveWith = (vals: Record<string, string>) =>
      (val: string): number => evaluateFormulaRaw(val, vals, liveData.angleMode);
    /* THE ERROR BOX'S EQUATION COUNTS TOO, now that the seven categories are one shared set of
       cards fed by both. Without it a category only a propagation mutation can produce was
       recorded unreachable, the editor greyed its card, and Flow greyed the rung routing on it —
       all while the grader fired it perfectly well.

       An APPROXIMATION, deliberately in the safe direction: these candidates are tested against
       the value's accepted range rather than the uncertainty's own band, so a category can be
       called reachable when only the other number could produce it. Over-reporting reachable
       costs a card that never fires; under-reporting silently removes a routing decision the
       teacher made. */
    const uncertaintyEq = uncertaintyAsked(liveData)
      ? (isEquationShaped(liveData.uncertaintyValue)
        ? liveData.uncertaintyValue
        : (liveData.uncertaintyMathErrorEquation ?? '')).trim()
      : '';
    const equations = liveData.conditionType === 'exact'
      ? [
          ...liveData.answers.map(a => (isEquationShaped(a.value) ? a.value : a.mathErrorEquation.trim())),
          uncertaintyEq,
        ].filter(s => s !== '')
      : liveData.conditionType === 'between'
      ? liveData.betweenAnswers.flatMap(a => [
          isEquationShaped(a.low) ? a.low : a.mathErrorEquationLow.trim(),
          isEquationShaped(a.high) ? a.high : a.mathErrorEquationHigh.trim(),
        ]).filter(s => s !== '')
      : [];
    const rangeConfigFor = (vals: Record<string, string>) => {
      const r = resolveWith(vals);
      return {
        conditionType: liveData.conditionType,
        answers: liveData.answers.map(a => ({
          id: a.id, value: r(a.value),
          toleranceValue: parseFloat(a.toleranceValue || '0'), toleranceAbove: parseFloat(a.toleranceAbove || '0'), toleranceBelow: parseFloat(a.toleranceBelow || '0'),
          // Carried, never defaulted — see `numericalEvaluatorData`.
          toleranceSymmetry: a.toleranceSymmetry, toleranceMode: a.toleranceMode,
        })),
        betweenAnswers: liveData.betweenAnswers.map(a => ({ id: a.id, low: r(a.low), high: r(a.high) })),
        toleranceMode: liveData.toleranceMode, toleranceSymmetry: liveData.toleranceSymmetry, threshold: r(liveData.threshold),
      };
    };
    const anyFor = (vals: Record<string, string>) => {
      const c = collectMathErrorCandidates(equations, liveData.angleMode, vals, rangeConfigFor(vals));
      return Object.fromEntries(MATH_ERROR_CATEGORIES.map(cat => [cat, (c[cat]?.length ?? 0) > 0]));
    };

    /**
     * SWEPT, not probed.
     *
     * This asked one roll — `probeRoll`, which is the first value in each domain — and wrote the
     * answer down as though it held for the whole domain. Reported on `log({test})-2` with test
     * from 1: at test = 1 the log/ln candidate is `ln(1)-2`, the correct value is `log(1)-2`, and
     * every logarithm of 1 is 0, so the two coincide. The one sampled point was the single point
     * in the domain where the mistake is undetectable, and the category was marked dead.
     *
     * A sweep keeps what the probe was for — the comment below explains it must be deterministic,
     * because two mounted instances otherwise overwrite each other with different random rolls —
     * since enumerating a domain is as deterministic as taking its first element.
     *
     * FAILS OPEN. `forEachRoll` returns false for "no answer", not "no rolls": a name that
     * resolves to nothing enumerable, or more combinations than the budget allows. Claiming
     * unreachable on that basis is how a working feature gets marked dead, and the cost of the
     * other error is only a category that never fires.
     */
    /*
     * A RECALLED ANSWER FAILS OPEN, for the reason the paragraph above gives and a case it
     * does not cover.
     *
     * That guard catches `forEachRoll` DECLINING — a domain too large, a name nothing defines.
     * It does not catch the sweep running perfectly and every evaluation coming back NaN,
     * which is what a recall did: `chipNamesIn` sees `{waterfall}`, enumerates it happily, and
     * then `(2*{waterfall}/9.8)^0.5*[ara_…]` cannot resolve because no roll carries the alias.
     * Every category came back undetectable and every math-error card was marked dead —
     * reported as feedback reading "unreachable" for cards the testing block fires happily.
     *
     * Substituting a value was the obvious fix and is the wrong one twice over. The live
     * sampled value makes a STRUCTURAL claim depend on which test value is showing, and this
     * effect keys on `block.data` alone, so it would go stale the moment the chip is cycled.
     * A fixed stand-in is worse: the paragraph above records `1` as precisely the point where
     * a log/ln mistake becomes undetectable, so probing with it would mark that category dead
     * for the same reason, one layer along.
     *
     * A recalled answer has no domain to sweep. Fail open and say so.
     */
    const names = chipNamesIn(equations);
    let computed: Record<string, boolean>;
    if (equations.some(hasRecallChip)) {
      computed = Object.fromEntries(MATH_ERROR_CATEGORIES.map(c => [c, true]));
    } else if (names.length === 0) {
      // No variables: one evaluation IS the whole domain.
      computed = anyFor({});
    } else {
      const found: Record<string, boolean> = Object.fromEntries(MATH_ERROR_CATEGORIES.map(c => [c, false]));
      const swept = forEachRoll(names, variables, rolled => {
        const here = anyFor(rolled);
        for (const cat of MATH_ERROR_CATEGORIES) if (here[cat]) found[cat] = true;
        return MATH_ERROR_CATEGORIES.every(c => found[c]) ? 'stop' : undefined;
      });
      computed = swept ? found : Object.fromEntries(MATH_ERROR_CATEGORIES.map(c => [c, true]));
    }
    const stored = (block.data as { mathErrorReachable?: Record<string, boolean> }).mathErrorReachable;
    if (JSON.stringify(stored) === JSON.stringify(computed)) return;
    // Bookkeeping, not an edit: saved, but never an undo step. See `BlockUpdateOptions`.
    emit({ ...block.data, mathErrorReachable: computed }, { normalization: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.data]);

  // ── Field updaters ──────────────────────────────────────────────────────────

  function setField<K extends keyof NumericalBlockData>(key: K, value: NumericalBlockData[K]) {
    const next = { ...fields, [key]: value };
    setFields(next);
    emitFields({ ...next, feedback });
  }

  /*
   * Every formula field in this block, wherever it sits — see `AngleModeProvider`.
   *
   * Through `setField`, NOT straight to `emit`. This block keeps a full local copy of its data
   * while editing (`fields = isEditing ? editedFields : restored.data`), so a write that only
   * emitted left that copy untouched: the toggle's face updated — it reads the live block data
   * through the provider — while every evaluator went on reading the stale local one. Switching
   * deg/rad flipped the button and changed no number on the screen.
   *
   * `setField` is what every other control here uses, and it does both halves.
   */
  const angleModeProps = useBlockAngleModes(
    fields as unknown as Record<string, unknown>,
    patch => setField('angleModes', patch.angleModes),
  );

  function handleGradingMode(mode: NumericalBlockData['gradingMode']) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, mode, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });
    const next = { ...fields, gradingMode: mode };
    setFields(next);
    emitFields({ ...next, feedback: { states: nextFeedbackStates } });
  }

  function setFieldMulti(updates: Partial<NumericalBlockData>) {
    const next = { ...fields, ...updates };
    setFields(next);
    emitFields({ ...next, feedback });
  }

  function updateAnswer(id: string, patch: Partial<ExactAnswer>) {
    const next = { ...fields, answers: fields.answers.map(a => a.id === id ? { ...a, ...patch } : a) };
    setFields(next);
    emitFields({ ...next, feedback });
  }

  /**
   * The two TYPE controls for ONE answer, riding that answer's tolerance row.
   *
   * They used to be block-level and took a labelled row of their own once there was more than
   * one answer — the trailing row that made a multi-answer setup look unfinished. Per answer
   * they belong on the row they govern, every row is the same shape, and the extra row is gone.
   *
   * The mode's word is dropped: four controls and a box share the line, and `aria-label`
   * carries the name either way.
   *
   * `intOnly` still disables percentage on EVERY answer, because that is genuinely a block-wide
   * fact — a block that forbids decimals cannot express a percentage of anything.
   */
  /**
   * THE PAIR OF TOLERANCE TYPE CONTROLS, for whichever tolerance is asking.
   *
   * Parameterised rather than copied when the error box gained its own tolerance row: the two
   * rows have to be the same control at the same size, and two spellings of these buttons is
   * exactly how they would stop being.
   */
  function renderToleranceTypeControls(o: {
    idPrefix: string;
    pct: boolean;
    asym: boolean;
    pctDisabled?: boolean;
    pctDisabledTitle?: string;
    onMode: (next: ToleranceMode) => void;
    onSymmetry: (next: 'symmetric' | 'asymmetric') => void;
  }) {
    const { idPrefix, pct, asym } = o;
    return (
      <>
        <button
          type='button'
          data-field-id={`${idPrefix}.toleranceMode`}
          disabled={o.pctDisabled}
          aria-pressed={pct}
          aria-label={t('blocks.tolerance.toleranceModeAriaLabel')}
          title={o.pctDisabled ? o.pctDisabledTitle : undefined}
          onClick={() => o.onMode(pct ? 'absolute' : 'percentage')}
          className={`flex items-center justify-center gap-2 rounded-lg ${toleranceControlShape(true)} border border-ws-border bg-ws-btn py-2 text-sm font-medium text-ws-text transition-colors duration-300 hover:border-ws-btn-active-fg hover:text-ws-btn-active-fg disabled:cursor-not-allowed disabled:opacity-40`}
        >
          {pct ? <Percent aria-hidden='true' className='h-4 w-4' /> : <Ruler aria-hidden='true' className='h-4 w-4' />}
        </button>
        <button
          type='button'
          data-field-id={`${idPrefix}.toleranceSymmetry`}
          aria-pressed={asym}
          aria-label={t('blocks.tolerance.toleranceSymmetryAriaLabel')}
          onClick={() => o.onSymmetry(asym ? 'symmetric' : 'asymmetric')}
          className={`flex items-center justify-center gap-2 rounded-lg ${toleranceControlShape(true)} border border-ws-border bg-ws-btn py-2 text-sm font-medium text-ws-text transition-colors duration-300 hover:border-ws-btn-active-fg hover:text-ws-btn-active-fg`}
        >
          {/* aria-hidden: the glyph is decoration, the button is named by its aria-label.
              Unhidden it announced as "±" or "−/+", punctuation read where a name should be. */}
          <span aria-hidden='true' className='font-mono font-semibold leading-none'>
            {asym ? <><sup>−</sup><span className='mx-px opacity-50'>/</span><sub>+</sub></> : '±'}
          </span>
        </button>
      </>
    );
  }

  /** The answer's pair. `intOnly` wins over whatever the answer stores — the same precedence the
   *  grader applies in `modeForAnswer`; without it a locked control sat showing `%` on a block
   *  that grades absolute, which is wrong and unfixable at once. */
  function renderTypeControls(ans: ExactAnswer) {
    return renderToleranceTypeControls({
      idPrefix: ans.id,
      pct: !intOnly && answerToleranceMode(ans, fields, 'absolute') === 'percentage',
      asym: answerSymmetry(ans, fields) === 'asymmetric',
      pctDisabled: intOnly,
      pctDisabledTitle: t('blocks.numerical.modeDisabledIntegerOnly'),
      onMode: next => updateAnswer(ans.id, { toleranceMode: next }),
      /* Carries the numbers across the switch, so going asymmetric does not wipe the `±2`
         already typed and leave two empty boxes that grade as an exact match. */
      onSymmetry: next => updateAnswer(ans.id, next === 'symmetric'
        ? { toleranceSymmetry: 'symmetric', toleranceValue: ans.toleranceAbove || ans.toleranceBelow }
        : { toleranceSymmetry: 'asymmetric', toleranceAbove: ans.toleranceValue, toleranceBelow: ans.toleranceValue }),
    });
  }

  function addAnswer() {
    /* Born absolute on an integer-only block. Inheriting the block's stale `percentage`
       through the fallback put the new answer on a mode its own control could not change,
       which is how a block ended up with some answers on percent and some on absolute and no
       way back. The display below forces it too, so stored data cannot contradict the lock. */
    const ans = { ...newExactAnswer(), ...(fields.integerOnly ? { toleranceMode: 'absolute' as const } : {}) };
    const next = { ...fields, answers: [...fields.answers, ans] };
    setFields(next);
    emitFields({ ...next, feedback });
  }

  function removeAnswer(id: string) {
    if (fields.answers.length <= 1) return;
    const next = { ...fields, answers: fields.answers.filter(a => a.id !== id) };
    setFields(next);
    emitFields({ ...next, feedback });
    requestAnimationFrame(() => addAnswerBtnRef.current?.focus());
  }

  function updateBetweenAnswer(id: string, patch: Partial<BetweenAnswer>) {
    const next = { ...fields, betweenAnswers: fields.betweenAnswers.map(a => a.id === id ? { ...a, ...patch } : a) };
    setFields(next);
    emitFields({ ...next, feedback });
  }

  function addBetweenAnswer() {
    const ans = newBetweenAnswer();
    const next = { ...fields, betweenAnswers: [...fields.betweenAnswers, ans] };
    setFields(next);
    emitFields({ ...next, feedback });
  }

  function removeBetweenAnswer(id: string) {
    if (fields.betweenAnswers.length <= 1) return;
    const next = { ...fields, betweenAnswers: fields.betweenAnswers.filter(a => a.id !== id) };
    setFields(next);
    emitFields({ ...next, feedback });
    requestAnimationFrame(() => addBetweenBtnRef.current?.focus());
  }

  function addUnitOption() {
    const opt: UnitOption = { id: crypto.randomUUID(), label: '', role: 'neutral', conversionFactor: 1, conversionOffset: 0 };
    pendingFocusUnitOptId.current = opt.id;
    setField('unitOptions', [...fields.unitOptions, opt]);
  }

  /**
   * Break one of the canonical's spellings out into a row of its own.
   *
   * Defaults to CORRECT, because it already was: every spelling on that line is accepted, and the
   * break-out exists so a teacher can single one out — not to silently withdraw acceptance from a
   * unit the moment they click it. Changing the role is then the deliberate second step.
   *
   * The spelling stays on the canonical's list; what changes is that a row now speaks for it, and
   * rows are matched first. Deleting the row is therefore the undo — the line renders whatever has
   * no row of its own, so the spelling reappears there with nothing else to clean up.
   */
  function breakOutSpelling(label: string) {
    const conv = getConversionFactor(fields.canonicalUnit, label, fields.tempConvMode);
    setField('unitOptions', [...fields.unitOptions, {
      id: crypto.randomUUID(), label, role: 'correct' as const,
      conversionFactor: conv?.factor ?? 1, conversionOffset: conv?.offset ?? 0,
      ...unitIdentityFor(label, 'option'),
    }]);
  }

  function updateUnitOption(id: string, patch: Partial<UnitOption>) {
    const next = fields.unitOptions.map(o => {
      if (o.id !== id) return o;
      const updated = { ...o, ...patch };
      if ('label' in patch && fields.canonicalUnit) {
        const conv = getConversionFactor(fields.canonicalUnit, updated.label, fields.tempConvMode);
        if (conv !== null) {
          // Convertible: reset to neutral so partial-input 'wrong' state doesn't bleed through.
          // Teacher can cycle the role after typing; that's a direct role patch, not a label change.
          return { ...updated, conversionFactor: conv.factor, conversionOffset: conv.offset,
                   ...unitIdentityFor(updated.label, 'option'), role: 'neutral' as const };
        } else {
          // Not convertible, so not a unit this block can reason about, and nothing to accept in
          // its place: no spellings.
          return { ...updated, conversionFactor: 1, conversionOffset: 0,
                   spellings: undefined, role: 'wrong' as const };
        }
      }
      return updated;
    });
    const nextFieldsState = { ...fields, unitOptions: next };
    setFields(nextFieldsState);
    // Keep any feedback targeting this unit's title/evaluation in sync — a label change can
    // rename the unit (title) or force a role reset via the convertibility check above
    // (evaluation), and a direct role patch (the teacher's role-cycle click) changes evaluation
    // on its own — either way, both are resynced together so they can never silently drift apart.
    const titleSynced = 'label' in patch
      ? resyncNumericalUnitTitles(next, feedback.states, t)
      : feedback.states;
    const nextFeedback = { states: resyncNumericalUnitEvaluations(next, titleSynced) };
    setFeedback(nextFeedback);
    emitFields({ ...nextFieldsState, feedback: nextFeedback });
  }

  // Deleting a unit option also deletes any feedback tied to it (unit_<id>) — same
  // "delete = wipe, role change = hide" exception applied to option deletion in
  // MCQ/dropdown/keyword; unlike a role change, a removed unit is gone for good.
  function removeUnitOption(id: string) {
    const nextUnitOptions = fields.unitOptions.filter(o => o.id !== id);
    const nextFields = { ...fields, unitOptions: nextUnitOptions };
    const nextFeedback = {
      states: feedback.states.filter(s => !(s.type === 'targeted' && s.trigger === `unit_${id}`)),
    };
    setFields(nextFields);
    setFeedback(nextFeedback);
    emitFields({ ...nextFields, feedback: nextFeedback });
  }

  function handleConditionTypeChange(newType: ConditionType) {
    // Carry the primary value across type switches to avoid data loss
    const primaryValue =
      fields.conditionType === 'exact'   ? (fields.answers[0]?.value ?? '') :
      fields.conditionType === 'between' ? (fields.betweenAnswers[0]?.low ?? '') :
      fields.threshold;

    setFieldMulti({
      conditionType: newType,
      answers: newType === 'exact'
        ? [{ ...newExactAnswer(), id: fields.answers[0]?.id ?? crypto.randomUUID(), value: primaryValue }]
        : fields.answers,
      betweenAnswers: newType === 'between'
        ? [{ ...newBetweenAnswer(), id: fields.betweenAnswers[0]?.id ?? crypto.randomUUID(), low: primaryValue }]
        : fields.betweenAnswers,
      threshold: ONE_SIDED.includes(newType) ? primaryValue : fields.threshold,
    });
  }

  function handleDone() {
    // Persists which math-error categories can actually produce a distinguishable wrong
    // answer for the CURRENT formula (the same "Active"/"Inactive" this editor already shows
    // per-row) — Flow reads this so a category the block itself marks Inactive never shows up
    // as a live possibility there, without duplicating this formula analysis outside this file.
    const mathErrorReachable = fields.mathErrorEnabled
      ? Object.fromEntries(MATH_ERROR_CATEGORIES.map(cat => [cat, (mathErrorCandidates?.[cat]?.length ?? 0) > 0]))
      : undefined;
    emitFields({ ...fields, feedback, mathErrorReachable });
    if (embedded) { onClose?.(); return; }
    setIsEditing(false);
    onClose?.();
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    // Preserves every default state generically (unanswered/incomplete_answer/sigfig_error/
    // correct/any_answer/any_response/incorrect/anything else), not a hardcoded list — a
    // hardcoded list here would silently drop incomplete_answer/sigfig_error every time
    // feedback is added.
    const defaults = feedback.states.filter(s => s.type === 'default');
    const byTrigger = (trigger: string) => defaults.filter(s => s.trigger === trigger);
    const positive = defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response');
    const named = new Set(['unanswered', 'incomplete_answer', 'correct', 'any_answer', 'any_response', 'incorrect', 'unit_error', 'notation_error', 'sigfig_error']);
    // Units always sort before values within the targeteds block, matching the panel's own
    // section order (Math Error, Unit Feedback, Value Feedback, Sig Figs) — each group's own
    // relative order (drag-reordered by the teacher) is preserved, only the unit/value grouping
    // itself is enforced.
    const unitTargeteds = targeteds.filter(s => s.trigger.startsWith('unit_'));
    const valueTargeteds = targeteds.filter(s => !s.trigger.startsWith('unit_'));
    return [
      ...byTrigger('unanswered'),
      ...byTrigger('incomplete_answer'),
      ...positive,
      ...unitTargeteds,
      ...byTrigger('unit_error'),
      ...byTrigger('notation_error'),
      ...byTrigger('sigfig_error'),
      ...defaults.filter(s => !named.has(s.trigger)),
      ...valueTargeteds,
      ...byTrigger('incorrect'),
    ];
  }

  /** The card the Add button last produced, so it opens ready to type into — the owner's
   *  preference for this family, and the reason `startExpanded` exists. Keyed by mode, because
   *  the panel reorders its sections on a switch and the cards remount; a bare id would reopen
   *  the last one every time. State, not a ref: it is read during render. */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${fields.gradingMode}:${id}`;

  /** The error box's own Add. `exact:` bare, prefixed on the way in — see the section below. */
  function addUncertaintyFeedback() {
    const targeteds = feedback.states.filter(s => s.type === 'targeted');
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: numericalValueTargetedTitle('exact:', t),
      trigger: `${UNCERTAINTY_TRIGGER_PREFIX}exact:`, content: '', evaluation: 'incorrect',
    };
    const nextFeedback = { states: rebuildStates([...targeteds, newState]) };
    setFeedback(nextFeedback);
    emitFields({ ...fields, feedback: nextFeedback });
  }

  function addValueFeedback() {
    const targeteds = feedback.states.filter(s => s.type === 'targeted');
    const trigger = 'exact:';
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: numericalValueTargetedTitle(trigger, t),
      trigger, content: '', evaluation: 'incorrect',
    };
    const nextFeedback = { states: rebuildStates([...targeteds, newState]) };
    setFeedback(nextFeedback);
    emitFields({ ...fields, feedback: nextFeedback });
  }

  function addUnitFeedback() {
    const targeteds = feedback.states.filter(s => s.type === 'targeted');
    const firstNonCorrectUnit = fields.unitOptions.find(u => u.role !== 'correct' && !targeteds.some(s => s.trigger === `unit_${u.id}`));
    const trigger = firstNonCorrectUnit ? `unit_${firstNonCorrectUnit.id}` : 'unit_';
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: numericalUnitTargetedTitle(trigger, fields.unitOptions, t),
      trigger,
      content: '', evaluation: firstNonCorrectUnit?.role === 'neutral' ? 'neutral' : 'incorrect',
    };
    const nextFeedback = { states: rebuildStates([...targeteds, newState]) };
    setFeedback(nextFeedback);
    emitFields({ ...fields, feedback: nextFeedback });
  }

  // Unit feedback and value feedback reorder independently within their own section —
  // dragging within one list never disturbs the other's relative order.
  function makeFeedbackDragHandler(isMember: (s: FeedbackState) => boolean) {
    return ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return;
      const all = feedback.states.filter(s => s.type === 'targeted');
      const subset = all.filter(isMember);
      const rest = all.filter(s => !isMember(s));
      const from = subset.findIndex(s => s.id === String(active.id));
      const to = subset.findIndex(s => s.id === String(over.id));
      if (from === -1 || to === -1) return;
      const nextFeedback = { states: rebuildStates([...arrayMove(subset, from, to), ...rest]) };
      setFeedback(nextFeedback);
      emitFields({ ...fields, feedback: nextFeedback });
    };
  }
  const handleUnitFeedbackDragEnd  = makeFeedbackDragHandler(s => s.trigger.startsWith('unit_'));
  const handleValueFeedbackDragEnd = makeFeedbackDragHandler(s =>
    !s.trigger.startsWith('unit_') && !s.trigger.startsWith(UNCERTAINTY_TRIGGER_PREFIX));
  const handleUncertaintyFeedbackDragEnd = makeFeedbackDragHandler(s =>
    s.trigger.startsWith(UNCERTAINTY_TRIGGER_PREFIX));

  // ── Grading mode helpers ────────────────────────────────────────────────────

  const modeIdx      = GRADING_MODES.findIndex(m => m.id === fields.gradingMode);
  const currentMode  = GRADING_MODES[modeIdx] ?? GRADING_MODES[0]!;
  const nextMode     = GRADING_MODES[(modeIdx + 1) % GRADING_MODES.length]!;

  const unansweredState   = feedback.states.find(s => s.trigger === 'unanswered');
  const partialAnswerState = feedback.states.find(s => s.trigger === 'incomplete_answer');
  const correctAnyState   = feedback.states.find(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response');
  const incorrectAnyState = feedback.states.find(s => s.trigger === 'incorrect');
  const unitErrorState = feedback.states.find(s => s.trigger === 'unit_error');
  const notationErrorState = feedback.states.find(s => s.trigger === 'notation_error');
  const sigfigErrorState  = feedback.states.find(s => s.trigger === 'sigfig_error');
  /* The error box's own form faults, which share the sig-fig slot — see the section below. */
  const uncSigfigState    = feedback.states.find(s => s.trigger === 'uncertainty_sigfig_error');
  const agreementState    = feedback.states.find(s => s.trigger === 'agreement_error');
  const mathErrorStates: Partial<Record<MathErrorCategory, FeedbackState>> = Object.fromEntries(
    MATH_ERROR_CATEGORIES.map(cat => [cat, feedback.states.find(s => s.trigger === `math_error_${cat}`)]),
  );
  // Through the shared filter like every other block, so a mode that cannot fire a targeted
  // state does not list one — see `targetedFiresIn`.
  const targetedStates  = visibleTargeted(feedback.states, fields.gradingMode);
  const unitFeedbackStates    = targetedStates.filter(s => s.trigger.startsWith('unit_'));
  /* THREE WAYS, not two. A bare `!unit_` used to sweep everything else into the value bin, which
     is where an uncertainty trap would have landed — graded correctly and shown under the wrong
     heading. The default `uncertainty_sigfig_error` cannot be caught here: this partitions
     TARGETED states only. */
  const uncertaintyFeedbackStates = targetedStates.filter(s =>
    s.trigger.startsWith(UNCERTAINTY_TRIGGER_PREFIX));
  const valueFeedbackStates   = targetedStates.filter(s =>
    !s.trigger.startsWith('unit_') && !s.trigger.startsWith(UNCERTAINTY_TRIGGER_PREFIX));

  // ── Preview ───────────────────────────────────────────────────────────────────

  if (isPreview) {
    // The version THIS student drew, from the task roll — see `versionRollKey`.
    const drawnVersionId = taskPreviewRoll?.[versionRollKey(block.id)];
    const raw = initData(block.data);
    // The ONE task roll both blocks read, which is what makes the leader's prose and this
    // block's answer key describe the same scenario (§3.1).
    const drawnScenario = drawnScenarioId(raw.followsBlockId, taskPreviewRoll);
    const d = resolveNumericalScenario(
      applyNumericalVersion(raw, drawnVersionId) as never, drawnScenario,
    ) as unknown as NumericalBlockData;
    const previewStates = normalizedNumericalStates(block.data.feedback, d.gradingMode, t);
    const testData = memoizedByKey(
      numericalTestDataCache, block.data, [t, submitted, i18n.language, rolledValues, drawnVersionId],
      () => computeNumericalTestData(d, previewStates, t, i18n.language, submitted, rolledValues, []),
    );
    /* `wide` ONLY for the three-box row — see `answerRowNeedsWidth`. The standard column is 448px
       and that row needs 513px, so without this its container query can never come out false and
       the boxes stack however wide the window is. Two boxes still take the standard column. */
    /* The same derivation asked about the DRAFT -- `currentAnswer()` rather than what was
       submitted, which is how this block says "as typed". Reported, never rendered. */
    const draftData = memoizedByKey(
      numericalDraftChipsCache, block.data,
      [t, testValue, testUnit, testUncertainty, i18n.language, rolledValues, drawnVersionId],
      () => computeNumericalTestData(d, previewStates, t, i18n.language, currentAnswer(), rolledValues, []),
    );
    return (
      <AnswerDisplayFrame question={d.question} rolledValues={rolledValues}
        wide={answerRowNeedsWidth(d)}>
        <NumericalTestInput d={d} value={testValue} unit={testUnit} uncertainty={testUncertainty}
          onValue={setTestValue} onUnit={setTestUnit} onUncertainty={setTestUncertainty} t={t} />
        {/* The per-block Submit that used to sit here was wired to nothing at all, next to a
            permanently disabled input. Preview has ONE page-level Submit (previewSubmitSignal),
            which is what this block now answers to. */}
        {/* See keyword-block: only the block knows which conditions are in force. */}
        <ReportTestChips chips={draftData.testChips} states={draftData.testResult} />
        {renderNumericalFiredFeedback(testData.testResult)}
      </AnswerDisplayFrame>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────────

  if (!isEditing) {
    // Through the version in force, so the card tests what the pager is pointed at.
    const d = resolveNumericalScenario(
      applyNumericalVersion(initData(block.data), shownVersionId) as never, shownScenarioId,
    ) as unknown as NumericalBlockData;
    // Local NumericalBlockData (authoring-time, string-based) doesn't carry feedback —
    // it's threaded separately via useInputBlockFeedback, so read the raw field directly.
    const collapsedFeedbackStates = normalizedNumericalStates(block.data.feedback, d.gradingMode, t);
    const collapsedHasChipFormulas = hasVariables && formulasFromData(d).length > 0;
    // Reroll is offered whenever ANYTHING on this surface can change between students, which
    // includes a chip in the question — the six blocks that got this right all start their own
    // `hasChips` with `htmlHasVariableChips(d.question)`. Kept separate from the answer-formula
    // flag beside it, which still gates the equation band: the bar prints the ANSWER's formulas,
    // and a variable that only appears in the question has none to print.
    const collapsedHasChips = hasVariables && (formulasFromData(d).length > 0 || htmlHasVariableChips(d.question));

    // Derive grading mode display from committed data, not editor state
    const collapsedModeIdx = GRADING_MODES.findIndex(m => m.id === d.gradingMode);
    const collapsedMode = GRADING_MODES[collapsedModeIdx] ?? GRADING_MODES[0]!;
    const collapsedChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';

    const modifiers: string[] = [
      ...(d.sigFigsRequired     ? [t('blocks.numerical.chipSigFigs', { count: d.sigFigsCount })] : []),
      ...(d.requireExponentialNotation ? [t('blocks.numerical.chipExpNotation')]  : []),
      ...(d.integerOnly         ? [t('blocks.numerical.chipIntegerOnly')]          : []),
      ...(uncertaintyAsked(d) ? [t('blocks.numerical.chipUncertainty')] : []),
    ];

    const numAriaLabel = (() => {
      const q = d.question ? d.question.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) : '';
      const staticPreviews = collapsedHasChipFormulas ? null : computeAcceptedPreviews(d, t, i18n.language, {});
      const answer = staticPreviews?.join(', ') ?? '';
      return t('blocks.numerical.editAriaBase') + (q ? `: ${q}` : '') + (answer ? `, ${answer}` : '') + (modifiers.length ? `, ${modifiers.join(', ')}` : '');
    })();

    // Memoized like every other block's: this runs on EVERY render of EVERY block in the task (the
    // parent's `blocks` array is a fresh reference on any edit), and the extrema search inside the
    // issue derivation enumerates variable combinations.
    const collapsedTestData = memoizedByKey(
      numericalTestDataCache, block.data, [t, submitted, i18n.language, rolledValues, variables, shownVersionId],
      () => computeNumericalTestData(
        d, collapsedFeedbackStates, t, i18n.language, submitted, rolledValues,
        settleStored(numericalIssues(d, { ...issueCtx, feedbackStates: collapsedFeedbackStates })),
      ),
    );

    return (
      <CollapsedInputBlock
        block={block} blockRef={blockRef} contentRef={collapsedContentRef}
        icon={<Hash className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.numerical.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={t(collapsedMode.labelKey)}
        GradingModeIcon={collapsedMode.Icon}
        gradingModeChipClass={collapsedChipClass}
        isSelected={isSelected} isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={numAriaLabel}
        onActivate={() => { if (onSelect) onSelect(); else { shouldFocusEditor.current = true; setIsEditing(true); } }}
        answerBarSlot={() => numericalCollapsedBar(d, t, i18n.language, collapsedTestData.notices)}
        testEvaluation={submitted && collapsedTestData.testResult ? (collapsedTestData.testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: submitted !== null,
          chips: collapsedTestData.testChips,
          onReset: handleTestReset,
          onReroll: collapsedHasChips
            ? () => {
              reroll();
            }
            : undefined,
          /* No branches here, so this row carries only the version pager and the way into
             editing it — the same component every banked block uses, which renders nothing at
             all for a block with no bank. */
          topNotices: (
            <VersionBranchNotice
              versions={testPager?.versions ?? shownVersions}
              activeVersionId={testPager ? testPager.activeId : shownVersionId}
              onSelectVersion={testPager?.onSelect ?? setTestVersion}
              noun={testPager?.noun ?? 'version'}
              editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={null}
              onEdit={() => {
                editShownVersion();
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          // The settled issues panel lives HERE, not in the answer bar — nine of the ten other
          // blocks put it in exactly this slot, and numerical had it in none of them.
          notices: (
            <IssuesPanel issues={collapsedTestData.notices} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
      >
        {/* The placeholder is DISPLAY STATE ONLY, gated on !isSelected exactly as GPS/Compass/Angle
            already gate theirs: a selected card IS the live test surface, and a student must never
            be shown authoring scaffolding. These two were the only blocks rendering it above the
            isSelected branch, so their test surface said "No question yet" where the other nine —
            and their own Preview — correctly showed nothing. */}
        <BlockPreviewProvider value={isSelected ? rolledValues : null}>
          {/* Selected only: the card is the live test surface then, and the stem belongs with
              the question being answered. Unselected it is the inert summary on a canvas where
              the leader block is itself visible, so a second copy would be noise. */}
          {isSelected && ghostedScenario}
          <BlockQuestion html={d.question} spaced showEmptyNote={!isSelected} />
        </BlockPreviewProvider>
        {/* Selected, the card IS the live test surface — a real answerable field. Unselected it
            stays the inert summary, so a canvas full of blocks doesn't invite stray typing. */}
        {isSelected ? (
          <div className='flex flex-col gap-3'>
            <NumericalTestInput
              d={d} value={testValue} unit={testUnit} uncertainty={testUncertainty}
              onValue={v => commitTest({ value: v })}
              onUnit={v => commitTest({ unit: v })}
              onUncertainty={v => commitTest({ uncertainty: v })}
              t={t}
            />
            {renderNumericalFiredFeedback(collapsedTestData.testResult)}
          </div>
        ) : (
          <NumericalAnswerSummary d={d} />
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Editing ───────────────────────────────────────────────────────────────────

  // Determine whether any answer field actually contains a variable-chip reference.
  // When true, the rolling UI is shown (re-roll button, formula chips).
  // Only inspects the currently active condition type's field(s) — same branching as
  // formulasFromData (used by the collapsed view) — because switching condition types
  // carries the primary value into the new type's field but leaves the previous type's
  // own field(s) untouched (see handleConditionTypeChange). Scanning all three
  // unconditionally meant a formula left behind in an inactive field (e.g. Between's low/
  // high after switching to Exact) could permanently pin this true even once the active
  // answer was a plain number.
  const hasChipFormulas = hasVariables && formulasFromData(fields).length > 0;
  /**
   * Anything in this block that moves with the dice — the ANSWER or a feedback condition.
   *
   * Separate from `hasChipFormulas`, which stays about the answer alone because that is what the
   * accepted band is made of. This one gates the scenario bar, so a teacher who put a variable only
   * in a targeted condition gets a way to test it. They had none: the panel was behind a predicate
   * that only ever looked at the answer.
   */
  const triggerFormulas = triggerFormulasOf(feedback.states);
  /*
   * A RECALLED ANSWER EARNS THE STRIP TOO, even with no variables in the task.
   *
   * The gate was `hasVariables && ...`, which is right for what the strip did when it was
   * written: resolve a formula against a roll. An equation quoting an earlier answer varies
   * for exactly the same reason a variable one does — it reads a number this surface has no
   * student to supply — and without the strip there was no control on the card to resolve it
   * with at all. The chip stayed a name and the answer bar stayed an equation, permanently.
   */
  const hasRecallFormulas = Object.keys(probeValuesForRecalls(JSON.stringify(fields))).length > 0;
  const hasScenarioFormulas =
    (hasVariables && (hasChipFormulas || triggerFormulas.length > 0)) || hasRecallFormulas;
  // Same widening as the collapsed card's — see its note.
  const hasChips = hasVariables && (formulasFromData(fields).length > 0 || htmlHasVariableChips(fields.question));

  /**
   * The editor's three ways of reading a formula, which are NOT interchangeable.
   *
   * `scenarioValues` is what the teacher picked, `{}` until they pick something — so a display
   * surface reading it shows nothing resolved, which is correct. `scenarioRoll` is the same thing
   * as `null` rather than `{}` for anything going through `BlockPreviewProvider`, where those two
   * mean opposite things (see BlockPreviewContext: `{}` says "resolve me", and with nothing to
   * substitute it strips the chip to bare text).
   *
   * `probeValues` is neither. It answers "is this arithmetic at all", and it has to exist, because
   * at rest there is no scenario and `evaluateFormulaRaw('{n}*2', {})` is NaN — which every
   * well-formedness check here would otherwise read as a broken formula and say so, inline, under a
   * perfectly good answer. It is the same roll `numericalIssues` probes with, so the inline
   * error beside a field and the issue in the bar cannot disagree.
   */
  /*
   * ...AND THE RECALLED ANSWERS, which a scenario does not carry.
   *
   * A scenario IS the variable roll — Min, Max and Random all produce one — so with a scenario
   * active the bar resolved the variables and met the recall with nothing. Reported as: press
   * Random and `{waterfall}` becomes 109 while `Kayak Velocity` stays a name and the bar reads
   * "No Valid Answer", over an equation that read perfectly at rest.
   *
   * Merged HERE rather than written into `scenario.values` when Random fires, because the
   * reroll and the scenario are set in one tick: `setTestAnswer` has not re-rendered yet, so
   * the scenario would have captured the PREVIOUS recall value and then held it, stale, until
   * the next reroll. Read at use, it is always the current one.
   */
  const scenarioValues = scenario ? { ...recallValues, ...scenario.values } : {};
  // Recall stand-ins too, for the reason the paragraph above gives: this must stay the SAME
  // roll `numericalIssues` probes with, or the inline error beside a field and the issue in
  // the bar start disagreeing — which is precisely what happened while only one of them knew
  // what a `[ara_...]` was.
  const probeValues = { ...probeRoll(variables), ...probeValuesForRecalls(JSON.stringify(fields)) };
  const resolveProbe = (val: string, fieldId: string): number =>
    evaluateFormulaRaw(val, probeValues, angleModeFor(fields, fieldId));

  const barEntries = computeBarEntries(fields, t, i18n.language, scenarioValues);
  /**
   * Whether an answer EXISTS — resolved against the probe, not the scenario.
   *
   * The scenario is `{}` at rest, and a formula resolves to NaN against nothing, so reading this
   * off the display entries declared every equation answer unconfigured the moment no scenario was
   * picked — and Correct, which is gated on it, drew itself as "Unreachable" for a block whose
   * answer was perfectly well set. Introduced by the same change that made the editor stop showing
   * an arbitrary roll at rest; `numericalIssues` was already probing for this exact question, so
   * the two surfaces disagreed about whether the block had an answer at all.
   *
   * `barEntries` above keeps the scenario, because that one really is display: it prints the
   * accepted values, and at rest there are none to print.
   */
  const hasConfiguredAnswer = (() => {
    const probed = computeBarEntries(fields, t, i18n.language, probeValues);
    return probed !== null && probed.entries.length > 0;
  })();
  const numericRanges    = computeNumericRanges(fields);
  const hasOverlap       = numericRanges ? hasRangeOverlap(numericRanges) : false;

  // Correct genuinely cannot fire without a configured answer — shown with the same
  // muted "Inactive" treatment every other block uses for an unreachable state, rather
  // than a bespoke message in Correct's own green styling (which would misleadingly
  // suggest a real accepted window exists).
  const inactiveChip = (
    <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
  );

  // Resolve the canonical accepted-range config to plain numbers (same rolled/seed
  // values as the other previews above) so the shared reachability/overlap helpers —
  // which operate on numbers, not formula strings — can be reused for the Default
  // Incorrect "Inactive" check and the feedback-overlap warning below.
  // `numericalRangeConfig` is the one derivation — this was a fourth hand-written copy of it,
  // despite that function's own comment claiming it had already collected them all.
  const rangeConfigFor = (vals: Record<string, string>) => numericalRangeConfig(fields, vals);
  // Analysis, not display: reachability and subsumption are claims about the SETUP, so they resolve
  // against the probe and stay put while the teacher rolls scenarios past them.
  const resolveForRanges = resolveProbe;
  const resolvedRangeConfig = rangeConfigFor(probeValues);
  /**
   * The one sweep, hoisted here because two very different-looking claims both come out of it:
   * whether each targeted condition collides with Correct, and whether Default Incorrect can fire
   * at all. Both are questions about the band and the triggers together, quantified over every
   * roll; both used to be answered separately, at different rolls, by code hundreds of lines apart.
   *
   * The NaN guard that used to sit here went inside it — an unresolvable band is not evidence
   * either way, and the rule "never wrongly say Inactive" is easier to keep in the place that
   * builds the band than in every caller that reads it.
   */
  const overlapAnalysis = analyseTriggerOverlap(
    fields, valueFeedbackStates.map(s => ({ id: s.id, trigger: s.trigger })), variables);
  /**
   * What Minimum and Maximum mean here.
   *
   * The ANSWER BAND, whenever the answer holds a formula — the roll that makes the accepted window
   * smallest and largest. With no formula in the answer there is no band to extremise, so the
   * feedback boundaries stand in: "Minimum" becomes the roll that puts a targeted condition at its
   * lowest. One-directional, so a block that already had an envelope keeps exactly the one it had.
   *
   * Either way the search enumerates the whole domain rather than putting each variable at its own
   * extreme. Those are not the same thing as soon as two variables divide — the minimum of
   * `{a}/{b}` pairs a's floor with b's CEILING — and the per-variable version is silently wrong for
   * every quotient, square and interior extremum a teacher is likely to write.
   */
  /*
   * MINIMUM AND MAXIMUM DECLINE ON THEIR OWN once a recall is in the equation, and no guard
   * here is needed to make them.
   *
   * The search enumerates a domain. A variable has one; a recalled answer does not — it is a
   * number a student will produce later, unbounded on the wrong side of the band — so
   * `bandEdgesAt` meets a value it cannot resolve and returns null, which is the whole sweep
   * declining. "The roll that makes the answer band smallest" has no answer once one of its
   * inputs is a person.
   *
   * I wrote a `&& !hasRecallFormulas` here first and mutation testing showed it changed
   * nothing. It is left out rather than kept as belt-and-braces: a condition that never
   * decides anything reads as though it does, and the next person has to prove it does not.
   * `recall-in-equation.test.tsx` pins the OUTCOME instead, so if the sweep is ever taught to
   * substitute recalls — the probe already was — the test fails and the question gets asked
   * rather than Min/Max quietly offering an extremum over a student's future answer.
   */
  const formulaExtrema = hasScenarioFormulas
    ? computeFormulaExtrema(fields, variables, triggerFormulas)
    : null;
  const valueDefaultIncorrectUnreachable = overlapAnalysis.defaultIncorrectUnreachable;

  // ── Math Error ──────────────────────────────────────────────────────────────
  // One slot per expression-eligible answer/boundary — its own text if already an
  // equation, else the teacher's manual override (only shown/needed for bare-number
  // slots). Scoped to exact/between, mirroring Math Error's own scope restriction (a
  // one-sided threshold isn't "the answer" a student computes toward).
  interface MathErrorSlot {
    key: string; isEquationShaped: boolean; rawValue: string; override: string;
    storedValue: number; onOverrideChange: (v: string) => void;
    /** Mirrors `MathErrorSlotBase.scope` — see `mathErrorEquationFieldsOf` for why the two pools
     *  must not merge. Here it also picks which word the override field is labelled with. */
    scope: 'value' | 'uncertainty';
  }
  // Follows the scenario when there is one and the probe when there is not, so the generated
  // candidates keep working at rest — a formula answer with no roll would otherwise produce none,
  // and every Math Error category would read as unreachable — while still reacting to the control
  // the teacher is holding. The MISMATCH check below deliberately does not use this: whether an
  // override produces its own answer is a question about the setup, so it stays on the probe,
  // which is where `numericalIssues` asks it too.
  const mathErrorValues = scenario?.values ?? probeValues;

  // Graded only, matching `numericalStateIsLive`: the evaluator returns at the survey branch
  // before math errors are ever considered, so outside graded these cards cannot fire.
  const mathErrorSlots: MathErrorSlot[] = (!fields.mathErrorEnabled || fields.gradingMode !== 'graded') ? [] :
    fields.conditionType === 'exact'
      ? [
          ...fields.answers.map(a => ({
            key: a.id, isEquationShaped: isEquationShaped(a.value), rawValue: a.value,
            override: a.mathErrorEquation, storedValue: resolveForRanges(a.value, `${a.id}.value`),
            onOverrideChange: (v: string) => updateAnswer(a.id, { mathErrorEquation: v }),
            scope: 'value' as const,
          })),
          /* KEPT EQUAL to `mathErrorSlotsOf` by hand, because this copy carries the change handler
             and that one does not. Its `storedValue` is the uncertainty, so the mismatch check
             below asks whether the override produces the ±. */
          ...(uncertaintyAsked(fields) ? [{
            key: 'uncertainty',
            isEquationShaped: isEquationShaped(fields.uncertaintyValue),
            rawValue: fields.uncertaintyValue,
            override: fields.uncertaintyMathErrorEquation ?? '',
            storedValue: resolveForRanges(fields.uncertaintyValue, 'uncertaintyValue'),
            onOverrideChange: (v: string) => setField('uncertaintyMathErrorEquation', v),
            scope: 'uncertainty' as const,
          }] : []),
        ]
      : fields.conditionType === 'between'
      ? fields.betweenAnswers.flatMap(a => [
          { key: `${a.id}-low`, isEquationShaped: isEquationShaped(a.low), rawValue: a.low,
            override: a.mathErrorEquationLow, storedValue: resolveForRanges(a.low, a.id),
            onOverrideChange: (v: string) => updateBetweenAnswer(a.id, { mathErrorEquationLow: v }),
            scope: 'value' as const },
          { key: `${a.id}-high`, isEquationShaped: isEquationShaped(a.high), rawValue: a.high,
            override: a.mathErrorEquationHigh, storedValue: resolveForRanges(a.high, a.id),
            onOverrideChange: (v: string) => updateBetweenAnswer(a.id, { mathErrorEquationHigh: v }),
            scope: 'value' as const },
        ])
      : [];

  // Manual-override validation — same epsilon idiom as the rest of this file's formula
  // validation, reusing the app's own (correctly-scoped) evaluateFormulaRaw directly;
  // only packages/blocks needed its own duplicate scope for the mutation engine itself.
  /* THE SAME PRECISION THE CHIP ADVERTISES. `fmtLocale` has no sig-fig count to work from with an
     error box, so it fell back to six significant digits and this label read "Equation for 5"
     two inches under a chip reading "5.00 ± 0.13 m". */
  const overrideLabelValue = (n: number): string => {
    const u = parseFloat(fields.uncertaintyValue);
    return (uncertaintyAsked(fields) && Number.isFinite(u))
      ? fmtFixed(n, i18n.language, uncertaintyDecimalsFor(u, fields.uncertaintySigFigs ?? 1))
      : fmtLocale(n, i18n.language);
  };

  const checkMathErrorEquation = (overrideText: string, storedValue: number, fieldId: string): string => {
    const trimmed = overrideText.trim();
    if (!trimmed) return '';
    const v = evaluateFormulaRaw(trimmed, mathErrorValues, angleModeFor(fields, fieldId));
    if (!Number.isFinite(v)) return t('blocks.numerical.validationExpression');
    if (!valuesMatchWithinEpsilon(v, storedValue)) {
      return t('blocks.numerical.mathErrorEquationMismatch', { value: fmtLocale(storedValue, i18n.language) });
    }
    return '';
  };

  // ONE derivation, shared with the card's own candidate collection — see
  // `mathErrorEquationFieldsOf`. Each equation keeps the field it came from, because an
  // equation-shaped answer and an override are different fields and can be in different modes.
  const effectiveMathErrorFields = mathErrorEquationFieldsOf(mathErrorSlots);

  // mathErrorValues (see above) drives this — re-rolling via the Feedback view's own Random
  // button, or the sticky bar's dice before any scenario is picked, both naturally recompute
  // these candidates on next render, no new plumbing needed.
  const mathErrorCandidates = fields.mathErrorEnabled
    ? collectMathErrorCandidates(
      effectiveMathErrorFields.map(x => x.equation),
      i => angleModeFor(fields, effectiveMathErrorFields[i]?.fieldId ?? ''),
      mathErrorValues, resolvedRangeConfig)
    : null;

  /**
   * Each targeted dimension now depends on the thing it actually needs, not on the whole answer.
   *
   * Both used to vanish while `hasConfiguredAnswer` was false — hidden, not deleted, but with no
   * way to see or edit them — on the reasoning that "a value threshold only means something
   * relative to a complete correct answer". That holds for UNITS: an alternative unit is defined
   * against the canonical one, so with no units set there is nothing to be alternative TO. It does
   * not hold for VALUES: `exact:0` means "if they answer zero, say this", which stands on its own
   * exactly as Slider's positions and Compass/Angle's headings do — and all three of those blocks
   * let you author targeted feedback before any answer exists.
   *
   * The overlap case is already handled the better way further down: a value trigger that falls
   * inside the accepted range keeps its card and gains a warning, rather than disappearing.
   */
  const hasCanonicalUnit = fields.unitMode === 'withUnits' && fields.canonicalUnit.trim() !== '';
  const canonicalIsUnderstood = unitSignature(fields.canonicalUnit) !== null;
  // The "also accepted" line: every spelling of the canonical that does not already have a row of
  // its own. Subtracting rather than tracking a flag is what makes deleting a row the undo — the
  // spelling simply reappears here, with no state to reconcile. The canonical itself is never in
  // the set (`acceptedSpellings` excludes it): listing it would invite a teacher to break out a row
  // duplicating their own canonical, which is an error the moment it exists.
  const unbrokenSpellings = (fields.canonicalSpellings ?? [])
    .filter(sp => !fields.unitOptions.some(o => o.label.trim() === sp));
  // Trimmed, so `kg` and `kg ` count as the one unit they would both grade as. Evaluation resolves
  // a submitted unit with `.find(...)`, which returns the FIRST match, so a duplicate is not a
  // preference the teacher can hold — every later copy is unreachable and the first silently
  // decides all of them.
  const duplicateUnitLabels = (() => {
    const labels = new Set(
      findDuplicateGroups(fields.unitOptions, o => o.label.trim()).map(g => g[0]!.label.trim()));
    // The CANONICAL unit counts as one of the copies, and is the worse of the two duplicates.
    // Evaluation matches an option's exact label ahead of the canonical, so a row repeating it does
    // not sit there inert — it TAKES OVER the canonical unit, and a role of `wrong` there marks the
    // teacher's own reference unit incorrect.
    const canon = fields.canonicalUnit.trim();
    if (canon && fields.unitOptions.some(o => o.label.trim() === canon)) labels.add(canon);
    return labels;
  })();
  const visibleUnitFeedbackStates = !hasCanonicalUnit ? [] : unitFeedbackStates.filter(s => !isStaleTargeted(s));

  // Always rendered (never filtered out) once there's a configured answer — a value
  // feedback card whose condition now falls inside the accepted range (e.g. exact:42
  // once 42 is itself correct) used to be hidden here entirely, with no way to see, edit,
  // or delete it short of first un-subsuming it by changing Correct back. Now it stays
  // visible with a `warning` (below) explaining why it won't currently fire — "suppress
  // via warning, don't disappear" rather than "suppress, don't destroy."
  const visibleValueFeedbackStates = valueFeedbackStates;
  /** Each trigger resolved in ITS OWN field's mode, looked up by the state that owns it. */
  const modeOfTriggerText = new Map(visibleValueFeedbackStates.map(s =>
    [s.trigger, `feedback.${s.id}.trigger`] as const));
  const resolvedTriggerOf = (trigger: string) =>
    resolveTriggerValues(trigger, (val: string) =>
      resolveForRanges(val, modeOfTriggerText.get(trigger) ?? ''));
  const overlappingTriggerSet = overlappingTriggerSetOf(
    visibleValueFeedbackStates.map(s => s.trigger), resolvedTriggerOf);
  /**
   * The card's own warning, from the SAME sweep the issue bar reads.
   *
   * These were two derivations of one fact and they read two different rolls, so a card could sit
   * silent beside a bar that was warning about it — the fourth time this file has grown that exact
   * split. `analyseTriggerOverlap` answers both questions at once: `.per[i]` for the card that owns
   * the trigger, `.any` for the bar that owns the block.
   */
  /**
   * The card's ERROR — a condition that is broken, not merely awkward.
   *
   * Same derivation the issue bar counts (`numericalTriggerFault`), so the two cannot disagree.
   * They were not connected at all before: the bar said "a trigger has no value set" about a block
   * with eight conditions in it, and no card carried a mark, which left the teacher to open every
   * one and compare. The bar names the fault; only the card can name which state has it.
   */
  function valueFeedbackError(s: FeedbackState): string | undefined {
    const fault = numericalTriggerFault(
      s.trigger, (val: string) => resolveProbe(val, `feedback.${s.id}.trigger`),
      new Set(variables.map(v => v.name)), new Set(Object.keys(probeValues)));
    return fault ? triggerFaultMessage(fault, t) : undefined;
  }

  // Worst-first: dead (wholly inside Correct) beats narrower-than-authored (partly inside) beats
  // MAY collide beats overlapping a sibling. All four at once on one card would be noise once the
  // strongest already explains the problem.
  function valueFeedbackWarning(s: FeedbackState): string | undefined {
    const graded = fields.gradingMode === 'graded';
    /*
     * BOTH SIDES OF A COLLISION ARE LIT, which is what makes one findable. A value-vs-value pair
     * already did — `overlappingTriggerSet` holds both of each pair — and Set Answer was the
     * one-sided case: it states the band rather than overlapping it, which is true and is not
     * the point. A teacher looking at one lit card should not have to hunt for the other.
     */
    if (s.trigger === SET_ANSWER_TRIGGER) {
      return overlapAnalysis.per.some((v, i) =>
        valueFeedbackStates[i]?.trigger !== SET_ANSWER_TRIGGER
        && (v.alwaysSubsumed || v.alwaysOverlaps))
        ? t('blocks.numerical.feedbackOverlapWarning') : undefined;
    }
    const v = overlapAnalysis.per[valueFeedbackStates.indexOf(s)];
    /*
     * The same fact, two sentences, because the situation genuinely differs: in graded Correct
     * wins first and is terminal, so a condition inside the band is DEAD; in participation the
     * preset is not terminal, so both fire and the teacher may be writing two answers to one
     * submission.
     */
    if (v?.alwaysSubsumed) {
      return graded ? t('blocks.numerical.feedbackSubsumedWarning')
        : t('blocks.numerical.feedbackOverlapWarning');
    }
    if (v?.alwaysOverlaps) {
      return graded ? t('blocks.numerical.feedbackOverlapsCorrectWarning')
        : t('blocks.numerical.feedbackOverlapWarning');
    }
    if (v?.mayCollide && graded) return t('blocks.numerical.feedbackMayOverlapCorrectWarning');
    if (overlappingTriggerSet.has(s.trigger)) {
      return t('blocks.numerical.feedbackOverlapWarning');
    }
    return undefined;
  }

  // ── Standardized issue bar ──────────────────────────────────────────────────
  // ONE derivation, shared with the collapsed card's test-bar notices — see `numericalIssues`.
  // The twelve flags this call used to assemble inline were the reason the card had no issues
  // panel: they could only be computed from the editor's own local state, so the card had nothing
  // to render. `fields` is the live editor state, `d` is the persisted data; the function reads
  // whichever it is handed.
  const editorIssues = settleLive(numericalIssues(fields, { ...issueCtx, feedbackStates: feedback.states }));
  // Which rows to ring, read from the ISSUES rather than re-running the checks — the pattern
  // `BlockIssue.fields` exists for, so the ring and the message can never describe different rows.
  const unitFieldsWithIssues = issueFieldIds(editorIssues);

  // The listed units that have no feedback card of their own, and so fall through to Any Other
  // Unit. Empty without a complete answer — same reasoning as visibleUnitFeedbackStates: nothing
  // about alternative units is meaningfully "non-correct" relative to an answer that is not set up.
  const uncoveredNonCorrectUnits = hasCanonicalUnit
    ? fields.unitOptions.filter(u => u.role !== 'correct' && !unitFeedbackStates.some(s => s.trigger === `unit_${u.id}`))
    : [];

  /*
   * Which units land on Any Other Unit. They are NOT a Default Incorrect chip, which is where they
   * used to sit and where they claimed a route that does not exist.
   *
   * `evaluateNumerical` resolves a non-correct unit as `this unit's own feedback ?? unit_error ??
   * incorrect`, and `unit_error` is live whenever units are on with a canonical unit — so the third
   * rung is dead in any current block, and Default Incorrect cannot be reached by unit at all. It
   * is the VALUE dimension's card and nothing else.
   *
   * No italics. A neutral-role unit is not inactive or provisional: it terminates evaluation just
   * as a wrong-role one does (the evaluator tests only `role !== 'correct'`), and it lands on a
   * card seeded `incorrect`. Styling it as faded said the opposite of what happens.
   */
  const uncoveredUnitChips: ConditionChip[] = uncoveredNonCorrectUnits.map(u => ({
    kind: 'chip',
    label: u.label || t('blocks.numerical.unitOptionLabel'),
    // Through the shared rule, which was a FOURTH hand-rolled copy of it here and the only one
    // that never learned the mode: these chips kept a ✗ in Participation after the role control
    // and the cards had both stopped.
    evaluation: unitRoleEvaluation(u.role, fields.gradingMode),
  }));

  /*
   * The named units first, then the catch-all they share it with: "cg or Any Other Unit".
   *
   * The card has to keep its OWN chip, because a `triggerZone` REPLACES `lockedTriggerLabel`
   * rather than joining it — handing the card only the unit chips cost it its identity and it read
   * as a card about `cg`. Default Incorrect can afford that trade because the heading above it
   * repeats its name; this one sits under "Units Feedback", which names the section, not the card.
   *
   * Built as a `ConditionChips` list through `joinChips`, not hand-rolled markup, so it is the same
   * derivation and the same renderer as every other chip row in the panel — which is what the
   * previous hand-rolled version got wrong: a different gap, weight and width cap sitting right
   * beside the card's own chip. The catch-all's evaluation is read live, so it tracks the cycler.
   *
   * Undefined when nothing falls through, which lets the card render its own label chip exactly as
   * it did before any of this.
   */
  const unitErrorContent = uncoveredUnitChips.length > 0 && unitErrorState
    ? (
      <ConditionChipRow
        chips={joinChips([
          ...uncoveredUnitChips,
          { kind: 'chip', label: t('blocks.numerical.feedbackTriggerAnyOtherUnit'),
            /* The card's own tone already goes through this; its chip had been reading the raw
               stored value, so the two halves of one card disagreed in Participation. */
            evaluation: displayEvaluation(fields.gradingMode, unitErrorState.evaluation) as ChipEvaluation },
        ], 'or')}
        unreachableLabel={t('blocks.feedbackPanel.unreachable')}
      />
    )
    : undefined;

  // Min/Max search (hoisted above, shared with the Default Incorrect reachability claim) drives
  // the scenario bar, and the same scenario drives the gap/range/entry resolution here. One roll
  // for the whole editor now; the two independent ones this comment used to describe were the
  // thing that let the answer bar and the feedback panel show different numbers at once.
  // Open to EVERY condition type. This was scoped to 'exact' for two reasons, both now resolved:
  // the boundary-symbol hazard died with the hand-rolled strict flip (each gap carries its own
  // inclusivity, resolved from the same values that built it, so a scenario's gaps are
  // self-consistent whatever the condition), and computeFormulaExtrema — which backs the Min/Max
  // controller a scenario is driven from — now enumerates between and threshold formulas too.
  // The resolution below always handled all three shapes; only the gate was narrow.
  const feedbackScenarioActive = hasChipFormulas && scenario !== null;

  // Formula answers, with no test scenario active (feedbackScenarioActive false), can't show
  // a resolved gap: the true boundary shifts with every roll, so this mirrors the existing
  // "not X" convention (gapNotEqualList below) symbolically — the unresolved formula chip
  // plus its tolerance, prefixed with "not". Once the teacher picks a scenario, this switches to
  // real numbers resolved against it.
  //
  // Still 'exact' only, and NOT because of the extrema search any more — that now covers every
  // condition. What's missing is the symbolic chip shape itself: `symbolicToleranceSuffix` renders
  // ONE derivation, shared with the collapsed test bar. The panel passes its own scenario (null
  // until the teacher picks one, so formulas stay symbolic); the bar passes its roll.
  const defaultIncorrectChips = numericalIncorrectChips(
    fields,
    targetedStates,
    feedbackScenarioActive ? scenarioValues : hasChipFormulas ? null : {},
    t,
    i18n.language,
  );
  const incorrectIsUnreachable = defaultIncorrectChips.every(c => c.kind === 'unreachable');
  // One dimension now: the value gaps. Units route to Any Other Unit, so an unreachable value
  // track means the card genuinely cannot fire, with nothing left to qualify that.
  const defaultIncorrectContent = incorrectIsUnreachable ? (
    <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
  ) : (
    <ConditionChipRow chips={defaultIncorrectChips} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />
  );

  // Formulas containing variables — displayed with resolved chips above the accepted range.
  const formulasToShow = hasChipFormulas ? formulasFromData(fields) : [];

  // Resolved canonical value of the formula (shown as "= N" next to variable chips when rolled).
  const resolvedEquationValue: string | null = (() => {
    if (!hasChipFormulas || !scenario || !barEntries || !barEntries.entries.length) return null;
    const fmt = (n: number) => fmtLocale(n, i18n.language, fields.sigFigsRequired ? fields.sigFigsCount : undefined, fields.requireExponentialNotation);
    if (fields.conditionType === 'exact') {
      const v = evaluateFormulaRaw(fields.answers[0]?.value ?? '', scenarioValues, angleModeFor(fields, `${fields.answers[0]?.id}.value`));
      return Number.isFinite(v) ? fmt(v) : null;
    }
    if (fields.conditionType === 'between') {
      const lo = evaluateFormulaRaw(fields.betweenAnswers[0]?.low ?? '', scenarioValues, angleModeFor(fields, fields.betweenAnswers[0]?.id ?? ''));
      const hi = evaluateFormulaRaw(fields.betweenAnswers[0]?.high ?? '', scenarioValues, angleModeFor(fields, fields.betweenAnswers[0]?.id ?? ''));
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
      return `${fmt(lo)} ${t('blocks.connectors.to')} ${fmt(hi)}`;
    }
    if (ONE_SIDED.includes(fields.conditionType)) {
      const v = evaluateFormulaRaw(fields.threshold, scenarioValues, angleModeFor(fields, 'threshold'));
      return Number.isFinite(v) ? fmt(v) : null;
    }
    return null;
  })();

  // Inline validation for the currently visible inputs
  const intOnly = fields.integerOnly;
  /* The error box's own tolerance, read the way every tolerance in the product is read: absent
     means absolute and symmetric, exactly as it does on an answer. */
  const uncertaintyPct = fields.uncertaintyToleranceMode === 'percentage';
  const uncertaintyAsym = fields.uncertaintyToleranceSymmetry === 'asymmetric';

  // Expression evaluation is always available here, independent of whether the task has
  // named variables — a plain math expression like "100*pi" should work whether or not
  // {variable} chips are involved (mathjs still understands constants/arithmetic on its own).
  // Plain numeric literals go through numberError first so ordinary input keeps identical
  // validation (integer-only, etc.) to before.
  // Against the PROBE, never the scenario: at rest there is no scenario, and judging `{n}*2`
  // against an empty roll would put "not a valid expression" under a formula answer that is fine.
  const checkNumericField = (val: string, fieldId: string): string => {
    if (Number.isFinite(Number(val))) return numberError(val, t, { integerOnly: intOnly });
    const v = resolveProbe(val, fieldId);
    if (!Number.isFinite(v)) return t('blocks.numerical.validationExpression');
    if (intOnly && Math.abs(v - Math.round(v)) > 1e-9) return t('blocks.numerical.validationFormulaNotInteger');
    return '';
  };

  const answerErrors = fields.conditionType === 'exact'
    ? fields.answers.map(a => ({
        id: a.id,
        // Blank is an absence, not a malformed value: `answer-field-blank` (an incompletion)
        // reports it on the bar at settled, and nothing highlights it. Only a value that is
        // present and unusable is a defect worth pointing at.
        value: a.value.trim() === '' ? '' : checkNumericField(a.value, `${a.id}.value`),
        eValue: numberError(a.toleranceValue, t, { allowNegative: false, integerOnly: intOnly }),
        eAbove: numberError(a.toleranceAbove, t, { allowNegative: false, integerOnly: intOnly }),
        eBelow: numberError(a.toleranceBelow, t, { allowNegative: false, integerOnly: intOnly }),
      }))
    : [];

  const betweenErrors = fields.conditionType === 'between'
    ? fields.betweenAnswers.map(a => {
        const low  = checkNumericField(a.low, a.id);
        const high = checkNumericField(a.high, a.id);
        // Only flag inversion when both fields are individually valid and lo > hi —
        // resolved through formulas (via resolveForRanges below) so a formula-based
        // bound still gets compared numerically instead of being skipped entirely.
        const loVal = resolveForRanges(a.low, a.id);
        const hiVal = resolveForRanges(a.high, a.id);
        const inverted = !low && !high && a.low !== '' && a.high !== ''
          && Number.isFinite(loVal) && Number.isFinite(hiVal) && loVal > hiVal;
        return { id: a.id, low, high, inverted };
      })
    : [];

  const thresholdError = ONE_SIDED.includes(fields.conditionType)
    ? checkNumericField(fields.threshold, 'threshold')
    : '';

  // ── Sticky answer bar ───────────────────────────────────────────────────────
  const BAR_COLLAPSE = 3;
  const answerBarSlot: React.ReactNode = (() => {
    if (fields.gradingMode !== 'graded') return collapsedModeBar(fields.gradingMode, t, 'numerical', undefined, numericalSurveyBarKey(fields));
    // No amber "No correct answers" / "setup incomplete" bars here any more. Unlike the card, this
    // surface already has an issues surface of its own — `warningBarSlot` renders IssuesPanel
    // directly beneath this bar — so those two branches were saying, uncategorised, what the panel
    // one line below now says as "Answer Setup: ...". This slot's job is the ANSWER; when there
    // isn't one to print it shows nothing and lets the panel do the reporting.
    // Nothing at all when there is no answer to print, rather than reserved-but-blank space. Holding
    // the height was tried, borrowing GPS's readout placeholder, and does not transfer: there the
    // blank sits inside a stable readout column, while here it lands directly under the unit error
    // that caused it, and an empty box beneath an error reads as broken rather than as pending.
    // The layout does move as a unit is typed; that is the lesser of the two.
    /*
     * The answer bar RENDERS the Correct chips; it does not derive them.
     *
     * It had its own path — `formulasFromData` (which filters, dropping literals), then a node list
     * stuffed into one bar — and that path is why this bar disagreed with the feedback card beside
     * it about which answers exist and how many chips they are. `numericalCorrectChips` already
     * takes the roll and already handles both modes, so the bar just draws it.
     *
     * `ConditionChipRow`, not one span: a span wraps everything in a single chip, which is how
     * per-answer content ends up as one giant chip with "or" inside it.
     */
    const correctChips = numericalCorrectChips(fields, scenario ? scenarioValues : null, t, i18n.language);
    // `answerBarHasContent`, not `length > 0`: an unreachable list is one entry long, so gating on
    // length drew the tinted shell around nothing — an empty coloured bar on a fresh block, where
    // slider, compass and angle correctly show no bar at all.
    if (answerBarHasContent(correctChips, numericalHasAnswerText(fields))) {
      return (
        <CollapsedBlockBar tone='success' align='start'>
          <div className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
            <AnswerBarText chips={correctChips}
                noValidAnswerLabel={numericalHasAnswerText(fields) ? t('blocks.answers.noValidAnswer') : undefined} />
          {/* NO UNIT HERE. The chips carry it — `symbolicAnswerLabels` appends it to every
              label and `previewsForUnit` puts it in every resolved preview — so a second one
              printed the bar as `{river}+{kayak} m/sm/s`. This span predates the value-range
              chip unification, which moved the unit into the chips and left it behind. */}
          </div>
        </CollapsedBlockBar>
      );
    }
    if (barEntries === null || barEntries.entries.length === 0) return null;
    // Graded — static answers or rolled formula (barEntries guaranteed non-null here)
    const { entries: barList } = barEntries;
    const displayed = barExpanded ? barList : barList.slice(0, BAR_COLLAPSE);
    const extra = barList.length - BAR_COLLAPSE;
    return (
      // Not CollapsedBlockBar: this is the one bar in the set that is a COLUMN — a formula header,
      // a rule, then the entries — so the shared bar's single icon+content row doesn't model it.
      // The tone class is taken from the same table by hand rather than reinvented; it read
      // `bg-ws-success-bg` (the solid, darker token) until now, which is exactly the drift the
      // shared component exists to stop.
      // One job: what will be accepted. The formula, its resolved value and the roll controls all
      // moved up to the scenario bar, where they are visible on the Feedback tab too — this bar is
      // not, and a teacher reading the feedback chips had no way to see where their numbers came
      // from.
      <div className='flex flex-col bg-ws-success-fg/[7%] px-2 py-2.5'>
        {/* Accepted entries — joined onto one flowing line (wraps naturally when long)
            instead of one row per entry, still collapsible past BAR_COLLAPSE entries. */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-start gap-2 text-sm text-ws-success-fg'>
            <Check aria-hidden='true' className='mt-0.5 h-4 w-4 shrink-0' />
            <span className='min-w-0'>{displayed.map(e => e.preview).join(', ')}</span>
          </div>
          {barList.length > BAR_COLLAPSE && (
            <button
              type='button'
              onClick={() => setBarExpanded(v => !v)}
              className='self-start pl-6 text-xs italic text-ws-success-fg underline-offset-2 hover:text-ws-success-fg hover:underline'
            >
              {barExpanded
                ? t('blocks.numerical.barCollapseAll')
                : t('blocks.numerical.barShowMore', { count: extra })}
            </button>
          )}
          {hasOverlap && (
            <div className='mt-0.5 flex items-center gap-1.5 pl-6 text-xs text-ws-warning-fg'>
              <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
              <span>{t('blocks.numerical.overlapWarning')}</span>
            </div>
          )}
        </div>
      </div>
    );
  })();

  // ── Scenario bar ────────────────────────────────────────────────────────────
  // Lets the teacher pick a concrete scenario (Minimum / Maximum / Random) to resolve formula
  // answers against, so the accepted band and every feedback chip show real numbers to check
  // against — without ever implying that any one scenario is "the" answer.
  //
  // ONE control for the whole editor, sitting above the answer bar and rendered on BOTH tabs. It
  // used to live inside the feedback panel only, with the answer bar carrying a separate dice of
  // its own: the tab where the numbers were hardest to interpret was the tab with no way to see
  // where they came from.
  //
  // Minimum and Maximum name the roll that makes the ANSWER BAND smallest and largest, found by
  // enumerating the whole domain rather than putting every variable at its own extreme — which is
  // not the same thing the moment two variables divide, and is why the calculation line below
  // matters: with `{a}/{b}` at its minimum, "Minimum" is only legible once you can see that it
  // chose a's floor and b's ceiling.
  function applyScenario(mode: 'min' | 'max' | 'random') {
    if (mode === 'random') {
      // Recalled answers move with the variables, each staying the KIND of answer the chip's
      // cycle set it to — see `useRerollRecalls`. It writes through `setTestAnswer`, which the
      // sentence and the equation both already read, so nothing here has to pass it on.
      rerollRecalls();
      setScenario({ mode, values: rollVariableValues(variables) });
    } else if (formulaExtrema) {
      setScenario({ mode, values: mode === 'min' ? formulaExtrema.loValues : formulaExtrema.hiValues });
    }
  }

  /**
   * Where the numbers in the chips came from — `equation → value`, the shape the branching blocks'
   * test bars already use, and for the reason their own comment gives: a teacher should not have to
   * do the arithmetic themselves to check what they are looking at.
   *
   * Shown only under a live scenario. At rest the equation is already on the answer bar with its
   * variables unrolled, and repeating it there with nothing resolved would be the same line twice.
   */
  const scenarioCalculation: React.ReactNode = !scenario ? null : (
    // Centred, under a control row that now spans the full width. Left-aligning it made it read as
    // a continuation of `Set Variables:`, which it is not: it is the RESULT of what that row was
    // just used to set, and it belongs under the middle of the row rather than tucked beneath its
    // first word.
    <div className='flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-xs text-ws-text'>
      {formulasToShow.length > 0 ? (
        <>
          <span className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
            {formulasToShow.map((f, i) =>
              <span key={i}><FormulaWithValues formula={f} rolledValues={scenarioValues} /></span>)}
          </span>
          {resolvedEquationValue && (
            <span className='font-semibold tabular-nums'>{' → '}{resolvedEquationValue}</span>
          )}
        </>
      ) : (
        // No formula in the ANSWER, so there is no answer calculation to show — but the roll itself
        // is still the thing a teacher needs to see, because every resolved number in the feedback
        // chips below came out of it.
        <span className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
          {chipNamesIn(triggerFormulas).map(name =>
            <FormulaWithValues key={name} formula={`{${name}}`} rolledValues={scenarioValues} />)}
        </span>
      )}
    </div>
  );

  // Matches SegmentedControl elsewhere in this block's settings — same track/pill treatment,
  // text-only (no icons: a directional icon for "minimum/maximum" reads ambiguously, e.g. as
  // upload/download, where the word does not).
  const scenarioBarSlot: React.ReactNode = !hasScenarioFormulas ? null : (
    <VariableTestController
      mode={scenario?.mode ?? null}
      onSelect={applyScenario}
      onClear={() => setScenario(null)}
      extremaAvailable={!!formulaExtrema}
      // Only when the ANSWER varies. With variables confined to feedback conditions, Min/Max is a
      // stretch of the word to begin with, and a line explaining why it is unavailable would raise
      // the idea only to take it away.
      calculation={scenarioCalculation}
    />
  );

  // ── Phone Test tab ──────────────────────────────────────────────────────────
  // On desktop the canvas card already shows the live test beside this editor; on phone the editor
  // covers that card entirely, so without this there is no route to testing at all. Reads the LIVE
  // editor state (`fields`), not persisted data — a teacher testing from inside the editor is
  // testing what they are currently typing.
  const phoneTest = computeNumericalTestData(
    fields, feedback.states, t, i18n.language, submitted, rolledValues,
    editorIssues,
  );
  const phoneTestSlot = (
    // Without the provider the Test tab renders the question's variable chips raw instead of
    // resolved against the roll being tested.
    <BlockPreviewProvider value={rolledValues}>
      <div className='flex flex-col gap-3'>
        {/* The phone needs this most: the leader is never on screen beside the block. */}
        {ghostedScenario}
        <BlockQuestion html={fields.question} />
        <NumericalTestInput
          d={fields} value={testValue} unit={testUnit} uncertainty={testUncertainty}
          onValue={v => commitTest({ value: v })}
          onUnit={v => commitTest({ unit: v })}
          onUncertainty={v => commitTest({ uncertainty: v })}
          t={t}
        />
        {renderNumericalFiredFeedback(phoneTest.testResult)}
      </div>
    </BlockPreviewProvider>
  );
  const phoneTestBar: TestBarProps = {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: submitted !== null,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: hasChips
      ? () => {
        reroll();
      }
      : undefined,
    /* The same row as the canvas card's. The EDITOR's pointer here: on a phone, Edit and Test
       are two tabs over ONE session. */
    topNotices: (
      <VersionBranchNotice
        versions={testPager?.versions ?? shownVersions}
        activeVersionId={testPager ? testPager.activeId : shownVersionId}
        onSelectVersion={testPager?.onSelect ?? selectVersion}
        noun={testPager?.noun ?? 'version'}
        editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
        stacked
        branch={null}
        onEdit={() => { editShownVersion(); setPhoneTestMode('edit'); }}
      />
    ),
    notices: (
      <IssuesPanel issues={phoneTest.notices} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  };

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      icon={<Hash className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.numerical.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.numerical.closeEditor')}
      onClose={handleDone}
      gradingMode={fields.gradingMode}
      gradingModeLabel={t(currentMode.labelKey)}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: t(currentMode.labelKey), next: t(nextMode.labelKey) })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      sharedToolbar={sharedToolbar}
      answerBarSlot={answerBarSlot}
      scenarioBarSlot={scenarioBarSlot}

      settingsSlot={
        <>
          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>

            <CheckboxRow fieldId='integerOnly'
              checked={fields.integerOnly}
              onToggle={() => {
                const next = !fields.integerOnly;
                setFieldMulti({
                  integerOnly: next,
                  // Per answer now: a block that forbids decimals cannot express a percentage
                  // of anything, so every answer still on percentage comes back to absolute.
                  ...(next ? { answers: fields.answers.map(a => ({ ...a, toleranceMode: 'absolute' as const })) } : {}),
                  requireExponentialNotation: next ? false : fields.requireExponentialNotation,
                  sigFigsRequired: next ? false : fields.sigFigsRequired,
                  // Every value agreeing with an ordinary uncertainty carries a decimal, and this
                  // forbids every one of them. `evaluateNumerical` reads the pair the same way.
                  uncertaintyRequired: next ? false : fields.uncertaintyRequired,
                });
              }}
              label={t('blocks.numerical.integerOnly')}
            />

            {showExponential && (
              <CheckboxRow fieldId='requireExponentialNotation'
                checked={fields.requireExponentialNotation}
                onToggle={() => setField('requireExponentialNotation', !fields.requireExponentialNotation)}
                label={t('blocks.numerical.requireExponentialNotation')}
                disabled={fields.integerOnly || !settingAvailable('requireExponentialNotation', fields.gradingMode)}
              />
            )}

            {showSigFigs && (
              <CheckboxRow fieldId='sigFigsRequired'
                checked={fields.sigFigsRequired}
                onToggle={() => {
                  const next = !fields.sigFigsRequired;
                  setFieldMulti({
                    sigFigsRequired: next,
                    integerOnly: next ? false : fields.integerOnly,
                  });
                }}
                label={t('blocks.numerical.sigFigsToggle')}
                disabled={fields.integerOnly || fields.uncertaintyRequired
                  || !settingAvailable('sigFigsRequired', fields.gradingMode)}
              />
            )}

            {/*
              * THE ERROR BOX, and why it sits next to the sig-figs row rather than anywhere else.
              *
              * The two are ONE CHOICE about precision, so each switches the other off. A fixed
              * figure count exists only because, with no uncertainty stated, nothing else fixes
              * precision — and once the student states one, the value's precision is derived from
              * it. Since the correct uncertainty is a formula over that student's own roll, the
              * precision it implies rolls per student, and a fixed count would mark a correct
              * answer wrong on some draws with nothing in the editor able to show it.
              *
              * Greyed by integers-only for the plainer reason: every agreeing value carries a
              * decimal, and that setting rejects all of them.
              */}
            {showUncertainty && (
              <CheckboxRow fieldId='uncertaintyRequired'
                checked={fields.uncertaintyRequired}
                onToggle={() => {
                  const next = !fields.uncertaintyRequired;
                  setFieldMulti({
                    uncertaintyRequired: next,
                    sigFigsRequired: next ? false : fields.sigFigsRequired,
                    // One reported result, so one accepted answer — the same collapse `withUnits`
                    // performs, for the same reason.
                    ...(next && fields.answers.length > 1 ? { answers: [fields.answers[0]!] } : {}),
                  });
                }}
                label={t('blocks.numerical.uncertaintyToggle')}
                disabled={fields.integerOnly || fields.conditionType !== 'exact'
                  || !settingAvailable('sigFigsRequired', fields.gradingMode)}
              />
            )}

            {/* Units sits under Answer Format, not Student Display: its "Require unit submission" mode
                changes what counts as a valid answer, and a single radio can only live in one section —
                so it goes where its strongest meaning is. NOT sub-labelled: ordering (audited) carries
                two RadioGroups in its drawer with no visible header, naming them by `aria-label`
                alone, and every option here already says "unit", so a visible label restated it. */}
            <RadioGroup fieldId='unitMode'
              aria-label={t('blocks.numerical.unitModeGroupLabel')}
              value={fields.unitMode}
              onChange={v => {
                const next: Partial<NumericalBlockData> = { unitMode: v as NumericalBlockData['unitMode'] };
                if (v === 'withUnits' && fields.answers.length > 1) {
                  next.answers = [fields.answers[0]!];
                }
                setFieldMulti(next);
              }}
              options={[
                { value: 'none',      label: t('blocks.numerical.unitModeNone')      },
                { value: 'display',   label: t('blocks.numerical.unitModeDisplay')   },
                { value: 'withUnits', label: t('blocks.numerical.unitModeWithUnits') },
              ]}
            />
          </OptionsSection>

          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>

            <CheckboxRow fieldId='showAcceptedRange'
              checked={fields.showAcceptedRange}
              onToggle={() => setField('showAcceptedRange', !fields.showAcceptedRange)}
              label={t('blocks.numerical.showRange')}
              // Exact-only by design, not an unfinished feature: see `showAcceptedRange`. And the
              // tolerance it reveals is the accepted band, which only graded has.
              disabled={fields.conditionType !== 'exact' || !settingAvailable('showAcceptedRange', fields.gradingMode)}
            />
          </OptionsSection>


          {/* ── Feedback ──
              `mathErrorEnabled` does not change what counts as a correct answer — it decides
              whether the block can RECOGNISE specific wrong workings (sign flip, dropped parens,
              misread exponent, and the rest of MATH_ERROR_CATEGORIES) and say something about
              them. That is feedback generation, so it gets its own section rather than sitting
              under Answer Format, and it goes last, because feedback is the last thing a teacher
              sets up. The drawer opens from the feedback editor too, so it is reachable from where
              it matters. Numerical is its only user today; the header is shared all the same. */}
          <OptionsSection title={t('blocks.optionsSections.feedback')}>

            <CheckboxRow fieldId='mathErrorEnabled'
              checked={fields.mathErrorEnabled}
              onToggle={() => setField('mathErrorEnabled', !fields.mathErrorEnabled)}
              label={t('blocks.numerical.mathErrorToggle')}
              /*
               * HAS CORRECT ONLY, which finishes a rule this control already half-kept.
               *
               * A math error is a WRONG ANSWER the teacher predicted, derived from the authored
               * one — so it was already refused in Optional, which has neither. Participation has
               * the authored answer but does not treat it as the method a student should have
               * used, and that is the line the whole mode now follows: it diagnoses the FORM of an
               * answer, never the METHOD behind it. Notation, significant figures and units are
               * statements about the string the student typed and need no reference; "you would
               * get this with a sign flip" is a statement about an equation and cannot be made
               * without one.
               *
               * It was settable here and did nothing: `numericalStateIsLive` kills every
               * `math_error_*` state outside graded, so a teacher could switch this on, watch the
               * categories appear, and get no behaviour at all.
               *
               * The teacher's setup is NOT cleared by switching mode — `mathErrorEnabled` and the
               * categories stay in the data, so Has Correct restores exactly what they had, the
               * same way the stored option roles survive the trip.
               */
              disabled={ONE_SIDED.includes(fields.conditionType) || !settingAvailable('mathErrorEnabled', fields.gradingMode)}
            />
          </OptionsSection>
        </>
      }

      settingsA11yLabel={t('blocks.numerical.optionsDrawerToggle')}

      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      feedbackPanelProps={{
        noAnswerSlot: unansweredState && (() => {
          const s = unansweredState;
          const ORDER = ['neutral', 'incorrect'] as const;
          return (
            <FeedbackCard fieldId={s.id} title={getTitle(s)} content={s.content} evaluation={s.evaluation}
              locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
              onTitleChange={() => {}}
              onContentChange={html => updateFeedbackState(s.id, { content: html })}
              onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
              onDelete={() => {}}
              activeEditorRef={activeEditorRef}
              setActiveEditor={setActiveEditor}
            />
          );
        })(),
        // `numericalStateIsLive`, not a restatement of its rule. Each of these gates was its own
        // copy of a condition the legend also kept, and the copies are what drifted.
        partialAnswerSlot: partialAnswerState && numericalStateIsLive(partialAnswerState, fields) && (() => {
          const s = partialAnswerState;
          const ORDER = ['neutral', 'incorrect'] as const;
          return (
            <FeedbackCard fieldId={s.id} title={getTitle(s)} content={s.content} evaluation={s.evaluation}
              locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.incompleteAnswerTitle')}
              onTitleChange={() => {}}
              onContentChange={html => updateFeedbackState(s.id, { content: html })}
              onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
              onDelete={() => {}}
              activeEditorRef={activeEditorRef}
              setActiveEditor={setActiveEditor}
            />
          );
        })(),
        correctSlot: correctAnyState && (() => {
          const s = correctAnyState;
          // ONE derivation, shared with the collapsed test bar — `numericalCorrectChips`. The only
          // thing that differs between the two surfaces is the roll: the panel keeps variables as
          // chips until the teacher picks a scenario (showing one sample roll at rest would read as
          // "the" answer), while the bar always resolves against its own. Passing `{}` rather than
          // null when there are no chip formulas keeps a block with dangling chips but no declared
          // variables on the numeric path, exactly where it was before.
          const correctChips = (
            <ConditionChipRow
              chips={numericalCorrectChips(
                fields,
                feedbackScenarioActive ? scenarioValues : hasChipFormulas ? null : {},
                t,
                i18n.language,
              )}
              unreachableLabel={t('blocks.feedbackPanel.unreachable')}
            />
          );
          // `tick` false once a value is targeted — same rule as the label beside it.
          const genericPositiveChip = (label: string, tick = true) => (
            <span className={chipClass(tick ? 'correct' : 'neutral', { width: 'auto' })}>
              {tick
                ? <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />
                : <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />}
              <ChipDivider />
              {label}
            </span>
          );
          /*
           * The tick keys on TARGETS, not on whether a title came back.
           *
           * `catchAllTitle` used to return undefined when nothing was targeted, so "did it return
           * something" doubled as "is anything targeted" — and the two came apart when it started
           * owning the title in both shapes. The proxy then read every participation block as
           * targeted and dropped the tick from a plain questionnaire's catch-all.
           */
          const anyValueTargeted = targetedStates.some(x => !x.trigger.startsWith('unit_'));
          /*
           * A FORMAT GATE counts too, exactly as on the test bar. "Any Answer" is only true
           * while nothing else can fire, and a required notation can fire — the panel was still
           * promising it after the bar beside it had stopped.
           *
           * The tick goes with the name, for the reason the note above gives: once something
           * else intercepts, the catch-all is no longer the whole story, so affirming it
           * unconditionally overstates what the block accepts.
           */
          const anyOtherCatch = anyValueTargeted || numericalHasFormatGate(fields);
          const participationLabel = catchAllTitle(fields.gradingMode, anyOtherCatch, t);
          const positiveTriggerZone =
            fields.gradingMode === 'survey' ? genericPositiveChip(participationLabel ?? t('blocks.feedbackDefaults.anyAnswerTitle'), !anyOtherCatch) :
            fields.gradingMode === 'optional' ? genericPositiveChip(participationLabel ?? t('blocks.feedbackDefaults.anyResponseTitle'), !anyOtherCatch) :
            (!hasConfiguredAnswer ? inactiveChip : correctChips);
          /**
           * "Any Answer" is only true while it is the only thing that can fire — beside a list of
           * targeted values it is the OTHER answers, exactly as in every other block. The tick
           * goes with the name, for the same reason: an answer the teacher singled out must not
           * be affirmed less than one they never considered.
           */
          const positiveLabel =
            catchAllTitle(fields.gradingMode, anyOtherCatch, t)
            ?? (fields.gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle')
              : fields.gradingMode === 'optional' ? t('blocks.feedbackDefaults.anyResponseTitle')
              : t('blocks.feedbackDefaults.correctTitle'));
          const correctInactive = fields.gradingMode === 'graded' && !hasConfiguredAnswer;
          return (
            <FeedbackCard fieldId={s.id} title={positiveLabel} content={s.content}
              evaluation={fields.gradingMode !== 'graded' && anyValueTargeted ? 'neutral' : s.evaluation}
              locked evalLocked inactive={correctInactive}
              lockedTriggerLabel={positiveLabel}
              triggerZone={positiveTriggerZone}
              triggerZoneSummary={positiveTriggerZone}
              onTitleChange={() => {}} onEvaluationCycle={() => {}} onDelete={() => {}}
              onContentChange={html => updateFeedbackState(s.id, { content: html })}
              activeEditorRef={activeEditorRef}
              setActiveEditor={setActiveEditor}
            />
          );
        })(),
        targetedSections: orderTargetedSections([
          /*
           * HAS CORRECT ONLY, matching the toggle that owns this section.
           *
           * `mathErrorEnabled` is deliberately NOT cleared when the mode changes — a teacher
           * switching to Participation and back finds their setup intact — so a stored `true`
           * reaches here in a mode where `numericalStateIsLive` has already killed every
           * `math_error_*` state. Without the mode in this condition the panel drew a section of
           * cards that nothing could fire, under a toggle the teacher could no longer see.
           */
          ...(fields.mathErrorEnabled && fields.gradingMode === 'graded'
            && !ONE_SIDED.includes(fields.conditionType) ? [{
            key: 'math-error',
            heading: t('blocks.numerical.feedbackSectionMathError'),
            content: (
              <div className='flex flex-col gap-3'>
                {mathErrorSlots.filter(s => !s.isEquationShaped).map(s => {
                  const err = checkMathErrorEquation(s.override, s.storedValue, `mathError.${s.key}`);
                  return (
                    /* WRAPPED IN THE DATA KEY for the uncertainty's slot, the way its tolerance
                       row is. The field inside is tagged `mathError.uncertainty`, which is what
                       `angleModeFor` keys on and must stay, while the field an edit NAMES is
                       `uncertaintyMathErrorEquation` — the reveal ladder collects every
                       `[data-field-id]` in the tree, so the ancestor carries the name undo looks
                       for. An answer's own override needs none of this: it lives inside a
                       container already tagged with the answer's id. */
                    <div key={s.key} className='flex flex-col gap-1'
                      {...(s.scope === 'uncertainty' ? { 'data-field-id': 'uncertaintyMathErrorEquation' } : {})}>
                      <label className='text-xs text-ws-muted'>
                        {t(s.scope === 'uncertainty'
                          ? 'blocks.numerical.mathErrorEquationOverrideLabelUncertainty'
                          : 'blocks.numerical.mathErrorEquationOverrideLabel',
                        { value: overrideLabelValue(s.storedValue) })}
                      </label>
                      <FormulaField hideEval className='min-w-0' fieldId={`mathError.${s.key}`} value={s.override} onChange={s.onOverrideChange}
                          variables={variables} rolledValues={mathErrorValues}
                          ariaLabel={t(s.scope === 'uncertainty'
                            ? 'blocks.numerical.mathErrorEquationOverrideLabelUncertainty'
                            : 'blocks.numerical.mathErrorEquationOverrideLabel',
                          { value: overrideLabelValue(s.storedValue) })}
                          placeholder={t('blocks.numerical.mathErrorEquationOverridePlaceholder')} hasError={!!err}
                          onFocus={disconnectSharedToolbar} />
                    </div>
                  );
                })}
                {MATH_ERROR_CATEGORIES.map(cat => {
                  const state = mathErrorStates[cat];
                  if (!state) return null;
                  const enabled = fields.mathErrorCategories[cat];
                  const catching = enabled && (mathErrorCandidates?.[cat]?.length ?? 0) > 0;
                  // Combined Neutral → Incorrect → Deactivated → Neutral cycle: the first two
                  // steps just update evaluation; stepping into Deactivated turns the category
                  // off; stepping back out re-enables it and resets evaluation to Neutral, so
                  // the cycle always lands on a predictable state rather than resuming
                  // whatever evaluation was set before it was last disabled.
                  const advance = () => {
                    if (!enabled) {
                      setField('mathErrorCategories', { ...fields.mathErrorCategories, [cat]: true });
                      updateFeedbackState(state.id, { evaluation: 'neutral' });
                    } else if (state.evaluation === 'neutral') {
                      updateFeedbackState(state.id, { evaluation: 'incorrect' });
                    } else {
                      setField('mathErrorCategories', { ...fields.mathErrorCategories, [cat]: false });
                    }
                  };
                  return (
                    <MathErrorRow fieldId={`mathErrorCategories.${cat}`} key={cat} title={getTitle(state)} content={state.content}
                      evaluation={state.evaluation as 'neutral' | 'incorrect'}
                      enabled={enabled} catching={catching}
                      onContentChange={html => updateFeedbackState(state.id, { content: html })}
                      onAdvance={advance}
                      activeEditorRef={activeEditorRef}
                      setActiveEditor={setActiveEditor}
                    />
                  );
                })}
              </div>
            ),
          }] : []),
          ...(fields.unitMode === 'withUnits' ? [{
            key: 'unit-feedback',
            heading: t('blocks.numerical.feedbackSectionUnitFeedback'),
            // The catch-all sits BELOW the Add button, which acts on the list of specific units
            // above it. Two headings for one concept read as two features; one section ending in
            // "Any Other Unit" reads the way the evaluator resolves it — specific, then everything
            // else.
            footer: unitErrorState && (() => {
              const s = unitErrorState;
              const ORDER = ['neutral', 'incorrect'] as const;
              return (
                <FeedbackCard fieldId={s.id} title={getTitle(s)} content={s.content}
                  evaluation={displayEvaluation(fields.gradingMode, s.evaluation)}
                  /* ANSWER-FORM FAULT: graded owns the verdict, Participation does not.
                     Locked and shown neutral outside Has Correct, matching every targeted card
                     in every block — see the two-case rule in `feedback-evaluation-rule.test`.
                     The stored value is untouched, so Has Correct gets it back unchanged. */
                  locked evalLocked={fields.gradingMode !== 'graded'}
                  lockedTriggerLabel={t('blocks.numerical.feedbackTriggerAnyOtherUnit')}
                  /* The listed units that reach this card. No "unreachable" case: the label is
                     literal, so it always catches a unit the teacher never listed at all. */
                  triggerZone={unitErrorContent}
                  triggerZoneSummary={unitErrorContent}
                  onTitleChange={() => {}}
                  onContentChange={html => updateFeedbackState(s.id, { content: html })}
                  onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                  onDelete={() => {}}
                  activeEditorRef={activeEditorRef}
                  setActiveEditor={setActiveEditor}
                />
              );
            })(),
            canAddMore: uncoveredNonCorrectUnits.length > 0,
            isEmpty: visibleUnitFeedbackStates.length === 0,
            addLabel: t('blocks.feedbackPanel.add'),
            // Names the thing that is actually missing. Once units ARE set and every non-correct
            // one has feedback, the button simply disappears (as MCQ/Dropdown/Keyword do when
            // their options run out) and the shell's own sentence covers the empty case.
            addDisabledReason: !hasCanonicalUnit ? t('blocks.numerical.unitAddDisabledReason') : undefined,
            onAdd: addUnitFeedback,
            content: (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleUnitFeedbackDragEnd}
                accessibility={dndA11y(t, visibleUnitFeedbackStates.map(x => x.id), 'feedback')}
    >
                <SortableContext items={visibleUnitFeedbackStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  <div className='flex flex-col gap-3'>
                    {visibleUnitFeedbackStates.map(s => {
                      return (
                        <SortableFeedbackCard key={s.id} sortId={s.id} title={s.title} content={s.content}
                          /* Mirrors its unit's ROLE in Has Correct (kept honest by
                             `resyncNumericalUnitEvaluations`), neutral everywhere else. Already
                             hard-locked in every mode: the role is the one control. */
                          evaluation={displayEvaluation(fields.gradingMode, s.evaluation)}
                          startExpanded={justAddedKey(s.id) === justAddedStateId}
                          titleLocked
                          evalLocked
                          triggerZone={<NumericalUnitTriggerEditor trigger={s.trigger} unitOptions={fields.unitOptions} gradingMode={fields.gradingMode} onChange={trigger => {
                            const unit = fields.unitOptions.find(u => u.id === trigger.slice('unit_'.length));
                            updateFeedbackState(s.id, {
                              trigger,
                              title: numericalUnitTargetedTitle(trigger, fields.unitOptions, t),
                              evaluation: unit ? (unit.role === 'neutral' ? 'neutral' : 'incorrect') : s.evaluation,
                            });
                          }} />}
                          triggerZoneSummary={<NumericalUnitTriggerChip trigger={s.trigger} unitOptions={fields.unitOptions} gradingMode={fields.gradingMode} />}
                          onTitleChange={() => {}}
                          onContentChange={html => updateFeedbackState(s.id, { content: html })}
                          onEvaluationCycle={() => {}}
                          onDelete={() => deleteFeedbackState(s.id)}
                          activeEditorRef={activeEditorRef}
                          setActiveEditor={setActiveEditor}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            ),
          }] : []),
          {
            key: 'value-feedback',
            heading: (fields.unitMode === 'withUnits' || fields.sigFigsRequired)
              ? t('blocks.numerical.feedbackSectionValueFeedback')
              : t('blocks.feedbackPanel.targeted'),
            // A value trigger is absolute, so it can be written before the answer is — matching
            // Slider, Compass and Angle, and matching how a teacher actually drafts (the
            // distractor you already know about, before you settle the answer you want).
            canAddMore: true,
            isEmpty: visibleValueFeedbackStates.length === 0,
            addLabel: t('blocks.feedbackPanel.add'),
            onAdd: addValueFeedback,
            content: (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleValueFeedbackDragEnd}
                  accessibility={dndA11y(t, visibleValueFeedbackStates.map(x => x.id), 'feedback')}
    >
                  <SortableContext items={visibleValueFeedbackStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <div className='flex flex-col gap-3'>
                      {visibleValueFeedbackStates.map(s => {
                        const ORDER = ['neutral', 'incorrect'] as const;
                        /** The authored answer as chips, for a Set Answer card — the same
                         *  derivation the Correct card uses, so the two cannot disagree. */
                        const presetAnswerChips = (
                          <ConditionChipRow
                            chips={neutralChips(numericalCorrectChips(
                              fields,
                              feedbackScenarioActive ? scenarioValues : hasChipFormulas ? null : {},
                              t, i18n.language,
                            ))}
                            unreachableLabel={t('blocks.feedbackPanel.unreachable')} />
                        );
                        return (
                          <SortableFeedbackCard key={s.id} sortId={s.id} title={s.title} content={s.content}
                            /* Neutral and locked outside graded — the reported bug: a value
                               target rendered as ✗ and stayed cyclable in a mode with no
                               incorrect to cycle to. */
                            evaluation={displayEvaluation(fields.gradingMode, s.evaluation)}
                            evalLocked={fields.gradingMode !== 'graded'}
                            startExpanded={justAddedKey(s.id) === justAddedStateId}
                            titleLocked
                            triggerZone={<NumericalTriggerEditor fieldId={`feedback.${s.id}.trigger`} onPlainFieldFocus={disconnectSharedToolbar} variables={variables} rolledValues={scenarioValues} trigger={s.trigger} gradingMode={fields.gradingMode} answerTaken={visibleValueFeedbackStates.some(x => x.id !== s.id && x.trigger === SET_ANSWER_TRIGGER)} answerConditionChips={presetAnswerChips} onChange={trigger => updateFeedbackState(s.id, { trigger, title: numericalValueTargetedTitle(trigger, t) })} />}
                            /* The preset states the authored condition, from the same derivation
                               the Correct card draws — never "= ?" off a trigger holding no value. */
                            triggerZoneSummary={s.trigger === SET_ANSWER_TRIGGER
                              ? presetAnswerChips
                              : <NumericalTriggerChip trigger={s.trigger} evaluation={displayEvaluation(fields.gradingMode, s.evaluation)} scenario={scenarioValues} angleMode={angleModeFor(fields, `feedback.${s.id}.trigger`)} />}
                            warning={valueFeedbackWarning(s)}
                            error={valueFeedbackError(s)}
                            onTitleChange={() => {}}
                            onContentChange={html => updateFeedbackState(s.id, { content: html })}
                            onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                            onDelete={() => deleteFeedbackState(s.id)}
                            activeEditorRef={activeEditorRef}
                            setActiveEditor={setActiveEditor}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              </>
            ),
          },
          /*
           * THE ERROR BOX'S TARGETED BIN, and the only thing it needed was a way to author one.
           *
           * The prefix is carried at the EDGES: bare inside the editor so the trigger editor, its
           * chip and the title deriver all work unchanged, prefixed on `onChange`. Teaching three
           * components a second vocabulary to avoid two `slice` calls is the wrong trade.
           */
          /* GRADED ONLY, like Math Error and for the same reason: `evaluateNumerical` returns at
             the survey branch long before the uncertainty is judged, so outside graded these
             cannot fire and the editor would be offering cards nothing can reach. */
          ...(uncertaintyAsked(fields) && fields.gradingMode === 'graded' ? [{
            key: 'uncertainty-feedback',
            heading: t('blocks.numerical.feedbackSectionUncertaintyFeedback'),
            canAddMore: true,
            isEmpty: uncertaintyFeedbackStates.length === 0,
            addLabel: t('blocks.feedbackPanel.add'),
            onAdd: addUncertaintyFeedback,
            content: (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleUncertaintyFeedbackDragEnd}
                accessibility={dndA11y(t, uncertaintyFeedbackStates.map(x => x.id), 'feedback')}
              >
                <SortableContext items={uncertaintyFeedbackStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  <div className='flex flex-col gap-3'>
                    {uncertaintyFeedbackStates.map(s => {
                      const ORDER = ['neutral', 'incorrect'] as const;
                      const bare = s.trigger.slice(UNCERTAINTY_TRIGGER_PREFIX.length);
                      return (
                        <SortableFeedbackCard key={s.id} sortId={s.id} title={s.title} content={s.content}
                          evaluation={displayEvaluation(fields.gradingMode, s.evaluation)}
                          evalLocked={fields.gradingMode !== 'graded'}
                          startExpanded={justAddedKey(s.id) === justAddedStateId}
                          titleLocked
                          triggerZone={<NumericalTriggerEditor fieldId={`feedback.${s.id}.trigger`}
                            onPlainFieldFocus={disconnectSharedToolbar}
                            variables={variables} rolledValues={scenarioValues}
                            trigger={bare} gradingMode={fields.gradingMode}
                            onChange={trigger => updateFeedbackState(s.id, {
                              trigger: `${UNCERTAINTY_TRIGGER_PREFIX}${trigger}`,
                              title: numericalValueTargetedTitle(trigger, t),
                            })} />}
                          triggerZoneSummary={<NumericalTriggerChip trigger={bare}
                            evaluation={displayEvaluation(fields.gradingMode, s.evaluation)}
                            scenario={scenarioValues}
                            angleMode={angleModeFor(fields, `feedback.${s.id}.trigger`)} />}
                          onTitleChange={() => {}}
                          onContentChange={html => updateFeedbackState(s.id, { content: html })}
                          onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                          onDelete={() => deleteFeedbackState(s.id)}
                          activeEditorRef={activeEditorRef}
                          setActiveEditor={setActiveEditor}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            ),
          }] : []),
          ...(notationErrorState && numericalStateIsLive(notationErrorState, fields) ? [{
            key: 'notation',
            heading: t('blocks.numerical.feedbackSectionNotation'),
            content: (() => {
              const s = notationErrorState;
              const ORDER = ['neutral', 'incorrect'] as const;
              return (
                <FeedbackCard fieldId={s.id} title={getTitle(s)} content={s.content}
                  evaluation={displayEvaluation(fields.gradingMode, s.evaluation)}
                  /* ANSWER-FORM FAULT: graded owns the verdict, Participation does not.
                     Locked and shown neutral outside Has Correct, matching every targeted card
                     in every block — see the two-case rule in `feedback-evaluation-rule.test`.
                     The stored value is untouched, so Has Correct gets it back unchanged. */
                  locked evalLocked={fields.gradingMode !== 'graded'}
                  lockedTriggerLabel={t('blocks.numerical.feedbackTriggerNotation')}
                  onTitleChange={() => {}}
                  onContentChange={html => updateFeedbackState(s.id, { content: html })}
                  onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                  onDelete={() => {}}
                  activeEditorRef={activeEditorRef}
                  setActiveEditor={setActiveEditor}
                />
              );
            })(),
          }] : []),
          ...(sigfigErrorState && numericalStateIsLive(sigfigErrorState, fields) ? [{
            key: 'sigfig',
            heading: t('blocks.numerical.feedbackSectionSigFig'),
            content: (() => {
              const s = sigfigErrorState;
              const ORDER = ['neutral', 'incorrect'] as const;
              return (
                <FeedbackCard fieldId={s.id} title={getTitle(s)} content={s.content}
                  evaluation={displayEvaluation(fields.gradingMode, s.evaluation)}
                  /* ANSWER-FORM FAULT: graded owns the verdict, Participation does not.
                     Locked and shown neutral outside Has Correct, matching every targeted card
                     in every block — see the two-case rule in `feedback-evaluation-rule.test`.
                     The stored value is untouched, so Has Correct gets it back unchanged. */
                  locked evalLocked={fields.gradingMode !== 'graded'}
                  lockedTriggerLabel={t('blocks.numerical.feedbackTriggerSigFig', { count: fields.sigFigsCount })}
                  onTitleChange={() => {}}
                  onContentChange={html => updateFeedbackState(s.id, { content: html })}
                  onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                  onDelete={() => {}}
                  activeEditorRef={activeEditorRef}
                  setActiveEditor={setActiveEditor}
                />
              );
            })(),
          }] : []),
          /*
           * THE ERROR BOX'S FORM FAULTS, in the slot the sig-fig card leaves free.
           *
           * `sigFigsRequired` is forced off when an uncertainty is asked for and its toggle is
           * disabled, so the section above and this one can never draw together. One question in
           * three forms — is the answer written the way it was asked for — asked before any value
           * is judged, which is why they share a heading rather than opening a section each.
           */
          ...((() => {
            const live = [
              ...(uncSigfigState && numericalStateIsLive(uncSigfigState, fields)
                ? [{ st: uncSigfigState,
                  label: t('blocks.numerical.feedbackTriggerUncertaintyFigures',
                    { count: fields.uncertaintySigFigs }) }] : []),
              /* NOT A COUNT. With an error box the answer's precision is set by the uncertainty's
                 PLACE, which is per-student the moment the uncertainty is a formula, so there is
                 no number to name here — the label states the rule. */
              ...(agreementState && numericalStateIsLive(agreementState, fields)
                ? [{ st: agreementState, label: t('blocks.numerical.feedbackTriggerAgreement') }] : []),
            ];
            if (live.length === 0) return [];
            return [{
              key: 'sigfig',
              heading: t('blocks.numerical.feedbackSectionAnswerForm'),
              content: (
                <div className='flex flex-col gap-3'>
                  {live.map(({ st, label }) => {
                    const ORDER = ['neutral', 'incorrect'] as const;
                    return (
                      <FeedbackCard key={st.id} fieldId={st.id} title={getTitle(st)} content={st.content}
                        evaluation={displayEvaluation(fields.gradingMode, st.evaluation)}
                        /* Form faults, so they fire in participation too — and like every other
                           targeted card they are neutral and locked outside Has Correct. */
                        locked evalLocked={fields.gradingMode !== 'graded'}
                        lockedTriggerLabel={label}
                        onTitleChange={() => {}}
                        onContentChange={html => updateFeedbackState(st.id, { content: html })}
                        onEvaluationCycle={() => updateFeedbackState(st.id, { evaluation: ORDER[(ORDER.indexOf(st.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                        onDelete={() => {}}
                        activeEditorRef={activeEditorRef}
                        setActiveEditor={setActiveEditor}
                      />
                    );
                  })}
                </div>
              ),
            }];
          })()),
        ]),
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot: (
          <>
            {incorrectAnyState && fields.gradingMode !== 'survey' && (() => {
              const s = incorrectAnyState;
              return (
                <FeedbackCard fieldId={s.id} title={getTitle(s)} content={s.content} evaluation={s.evaluation}
                  locked evalLocked inactive={valueDefaultIncorrectUnreachable}
                  lockedTriggerLabel={hasConfiguredAnswer ? t('blocks.feedbackDefaults.incorrectTitle') : t('blocks.numerical.feedbackTriggerIncorrectAll')}
                  triggerZone={defaultIncorrectContent}
                  triggerZoneSummary={defaultIncorrectContent}
                  onTitleChange={() => {}} onEvaluationCycle={() => {}} onDelete={() => {}}
                  onContentChange={html => updateFeedbackState(s.id, { content: html })}
                  activeEditorRef={activeEditorRef}
                  setActiveEditor={setActiveEditor}
                />
              );
            })()}
          </>
        ),
      }}

      editingSlot={
        <div className='flex flex-col gap-4 px-6 py-4'>
          {/* Question editor */}
          {/* A bank is a niche feature: in the heading it is available without being advertised. */}
          {/* The scenario reads ABOVE the question, where a student meets it — read-only here,
              because it is authored in the leader. See `ScenarioRowFrame`'s note on why the
              selector stays with the answers and the prose does not. */}
          {remapModal}
          {/* The link control and what it points at, for BOTH kinds of link — see
              `VariesWithSection`, which decides on its own whether a DRIVEN block
              needs it. This was five lines of identical JSX in twelve blocks. */}
          <VariesWithSection
            following={!!activeScenario}
            linkMenu={scenarioLinkMenu}
            stimulus={<ScenarioStimulus leader={leaderBlock} version={activeScenarioVersion as Record<string, unknown> | null} />}
          />
          {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
              the answer setup, because a driven axis varies the question too. Renders its
              children bare when this block is not driven. See `DrivenRowFrame`. */}
          <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
          <EditorSectionHeading
            label={t('blocks.areas.question')}
            divider={!!activeScenario}
            action={activeScenario ? undefined : (
              /* `gap-2`, as on Dropdown: the pager's armed ring bleeds 5px (4px of padding plus
                 a 1px outline, which draws outside the box), so 4px leaves it on the link. */
              <div className='flex items-center gap-2'>
              {/* The LINK sits after the pager — see Dropdown's own note. */}
              {!storedForLink.followsBlockId && (
              <VersionSelector
                versions={(initData(block.data).versions ?? []) as { id: string }[]}
                activeId={editorVersionId}
                onSelect={selectVersion}
                onAdd={() => {
                  const next = addNumericalVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                  emit(versionStructure(block.data, next));
                  const added = next.versions?.[next.versions.length - 1];
                  if (added) selectVersion(added.id, next);
                }}
                onRemove={(id) => {
                  const next = removeNumericalVersion(initData(block.data), id);
                  emit(versionStructure(block.data, next));
                  const landing = next.versions?.[0];
                  selectVersion(landing?.id, next);
                  setTestVersion(landing?.id);
                }}
              />
              )}
              {/* Silent once there is a Varies-With section, which is where the control moves
                  to — see `QuestionHeadingLink`. This one was UNCONDITIONAL, so Numerical
                  showed the link twice for a scenario follower as well as a driven block. */}
              <QuestionHeadingLink following={!!activeScenario} linkMenu={scenarioLinkMenu} />
              </div>
            )}
          />
          <BlockQuestionEditor
            value={fields.question}
            onChange={(html) => setField('question', html)}
            toolbar={sharedToolbar}
            editorRef={questionEditorRef}
            shouldFocusRef={shouldFocusEditor}
            embedded={embedded}
          />

          {/* Divider + heading marking where answer setup begins — mirrors slider-block.tsx's
              own "Answer Setup" heading treatment. */}
          {fields.gradingMode !== 'optional' && <EditorSectionHeading label={t('blocks.areas.answerSetup')} />}

          {/* Answer section */}
          {/* gap-4, matching angle/compass/slider: their AnswerSetupFields returns a fragment whose
              * children land directly in the editor column's own gap-4, so the space between the
              * condition bar and the first answer row is 16px there and was 12px here. */}
            {/* The scenario's box, and nothing at all when this block follows nothing. The
                answers are INSIDE it because the box is what says which scope they belong to. */}
            <ScenarioSetupFrame
              scenario={activeScenario}
              name={activeScenarioVersion?.name}
              pager={scenarioPager}
              unreviewedCount={unreviewedCount(storedForLink.scenarioBranches)}
              onDismissUnreviewed={dismissUnreviewed}
            >
            <div className='flex flex-col gap-4'>

            {fields.gradingMode !== 'optional' && (<>
              {/* Armed-delete announcement for AT */}
              <div aria-live='assertive' aria-atomic='true' className='sr-only'>
                {(armedAnswerId || armedBetweenId || armedUnitOptId) ? t('blocks.actions.deleteArmed') : ''}
              </div>

              {/* Condition type selector — the shared control, as angle/compass/slider use.
                *
                * This was a hand-rolled copy: the same track and pill treatment, reproduced by hand,
                * with `text-sm font-medium` where the shared one has `text-xs` — so the symbols read
                * a size and weight heavier than the identical bar on every sibling block. Total bar
                * height coincided at 38px (text-sm + py-1.5 lands where text-xs + py-2 does), which
                * is why the difference showed as the symbols looking wrong rather than the row.
                *
                * The copy was also LESS ACCESSIBLE, which is the part worth not repeating: `role=
                * group` with `aria-pressed` buttons is a set of independent toggles, not a choice.
                * SegmentedControl is a `radiogroup` with `aria-checked`, roving tabindex, and Arrow/
                * Home/End navigation, so the condition type is now keyboard-reachable here the way it
                * always was on the other four. */}
              <SegmentedControl fieldId='conditionType'
                options={CONDITION_TYPES.map(c => ({
                  id: c.id, label: c.symbol, ariaLabel: t(`blocks.conditions.${CONDITION_ARIA_KEYS[c.id]}`),
                }))}
                value={fields.conditionType}
                onChange={v => handleConditionTypeChange(v as ConditionType)}
                aria-label={t('blocks.numerical.conditionTypeGroupLabel')}
                stretch
              />


              {/*
                * PRECISION FIRST, then what it constrains.
                *
                * The figure count decides what the fields below it may contain: an answer is
                * held to the figures asked for, and with an error box it is held to the place
                * the uncertainty sits at. Reading top to bottom is then the order the rules
                * actually apply in, and changing the count visibly changes what the answer
                * field will accept — the dependency on screen rather than buried in the grader.
                */}
              {fields.sigFigsRequired && (
                <div className='flex items-center justify-between gap-2'>
                  <span className={NUMERICAL_ROW_LABEL}>{t('blocks.numerical.sigFigsCountLabel')}</span>
                  <div data-field-id='sigFigsCount' className='flex items-center overflow-hidden rounded-lg border border-ws-border bg-ws-btn'>
                    <button
                      type='button'
                      disabled={fields.sigFigsCount <= 1}
                      onClick={() => setField('sigFigsCount', Math.max(1, fields.sigFigsCount - 1))}
                      aria-label={t('blocks.numerical.sigFigsDecrement')}
                      className='flex h-9 w-9 items-center justify-center text-ws-muted transition-colors hover:bg-ws-hover hover:text-ws-text disabled:cursor-not-allowed disabled:opacity-30'
                    >
                      <Minus className='h-4 w-4' />
                    </button>
                    <span aria-live='polite' aria-atomic='true' className='min-w-[2rem] select-none text-center text-sm font-semibold text-ws-text'>
                      {fields.sigFigsCount}
                    </span>
                    <button
                      type='button'
                      disabled={fields.sigFigsCount >= 10}
                      onClick={() => setField('sigFigsCount', Math.min(10, fields.sigFigsCount + 1))}
                      aria-label={t('blocks.numerical.sigFigsIncrement')}
                      className='flex h-9 w-9 items-center justify-center text-ws-muted transition-colors hover:bg-ws-hover hover:text-ws-text disabled:cursor-not-allowed disabled:opacity-30'
                    >
                      <Plus className='h-4 w-4' />
                    </button>
                  </div>
                </div>
              )}
              {/* ── Exact answers ── */}
              {fields.conditionType === 'exact' && (
                <>
                  {fields.answers.map((ans, idx) => {
                    const errs    = answerErrors[idx];
                    const isAsym  = answerSymmetry(ans, fields) === 'asymmetric';
                    const isPct   = !intOnly && answerToleranceMode(ans, fields, 'absolute') === 'percentage';
                    const isArmed = armedAnswerId === ans.id;
                    // The armed-delete highlight wraps BOTH rows. It sat on the answer row alone, so
                    // arming a delete in asymmetric mode outlined the answer and left its own
                    // tolerances outside the box that was about to be removed.
                    return (
                      <div key={ans.id} data-field-id={ans.id} className={`flex flex-col gap-1 ${isArmed ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''}`}>
                        <div className='flex items-center gap-2'>
                          {/*
                            * NAMED ONLY WHEN THERE IS SOMETHING TO CONFUSE IT WITH.
                            *
                            * The two formula fields are visually identical, so with an error box on
                            * screen the answer needs a word as much as the uncertainty does. With
                            * the box off, every block that exists today keeps the unlabelled row it
                            * has always had: a label there would name the only field in the section.
                            */}
                          {uncertaintyAsked(fields) && (
                            <span className={NUMERICAL_ROW_LABEL}>{t('blocks.numerical.answerRowLabel')}</span>
                          )}
                          {/* Correct value */}
                          <FormulaField fieldId={`${ans.id}.value`}
                              hideEval
                              className='min-w-0 flex-1'
                              value={ans.value}
                              onChange={val => updateAnswer(ans.id, { value: val })}
                              variables={variables}
                              rolledValues={scenarioValues}
                              ariaLabel={t('blocks.numerical.correctValueLabel')}
                              hasError={!!errs?.value}
                              onFocus={disconnectSharedToolbar}
                            />

                        </div>
                        {/* The tolerance has its own row in both modes, and `scientific` unless
                            the block forbids decimals — see `ToleranceRow` for both calls. With
                            one answer the block-level controls ride with it.

                            PRESENT WITH AN ERROR BOX TOO. It was hidden here on the reasoning that
                            the uncertainty decides how close is close enough, and that was wrong:
                            the uncertainty is graded against its own key, so it was never this
                            row's grading window. What this absorbs is the method and rounding path
                            a student took — rounding an intermediate before carrying it forward
                            moves the answer by a lattice step and leaves the uncertainty untouched,
                            because the answer is quoted far more finely than its own ±. */}
                        <ToleranceRow
                          idPrefix={ans.id}
                          asymmetric={isAsym}
                          grammar={intOnly ? 'integer' : 'scientific'}
                          percent={isPct}
                          toleranceValue={ans.toleranceValue}
                          toleranceBelow={ans.toleranceBelow}
                          toleranceAbove={ans.toleranceAbove}
                          onChange={patch => updateAnswer(ans.id, patch)}
                          invalid={{ value: !!errs?.eValue, below: !!errs?.eBelow, above: !!errs?.eAbove }}
                          onFocus={disconnectSharedToolbar}
                          trailing={<>
                            {renderTypeControls(ans)}
                            {/* Delete sits here, not on the equation row, so the equation keeps
                                the full panel width on every answer — the room a recall chip
                                needs. See `ToleranceRow`. */}
                            {fields.answers.length > 1 && (
                              <IconButton
                                type='button'
                                variant='destructive'
                                size='md'
                                isArmed={isArmed}
                                aria-label={isArmed ? t('blocks.answers.confirmRemoveAnswer') : t('blocks.answers.removeAnswer')}
                                className='shrink-0'
                                onClick={() => {
                                  if (isArmed) { removeAnswer(ans.id); setArmedAnswerId(null); }
                                  else { setArmedBetweenId(null); setArmedAnswerId(ans.id); }
                                }}
                                onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedAnswerId(null); }}
                                onBlur={() => { if (isArmed) setArmedAnswerId(null); }}
                              >
                                <Trash2 className='h-4 w-4' />
                              </IconButton>
                            )}
                          </>} />
                      </div>
                    );
                  })}


                  {/* Add another answer — disabled when unitMode is withUnits (single canonical
                      answer enforced), and when an uncertainty is asked for: an uncertainty
                      belongs to ONE reported result, and a list of accepted values has no single
                      precision for it to set. */}
                  {fields.answers.length < 5 && fields.unitMode !== 'withUnits'
                    && !uncertaintyAsked(fields) && (
                    <AddRowButton
                      ref={addAnswerBtnRef}
                      label={t('blocks.answers.addAnswer')}
                      onClick={addAnswer}
                    />
                  )}
                </>
              )}

              {/*
                * THE ERROR BOX, UNDER THE ANSWER IT QUALIFIES.
                *
                * The answer is the reported result and the uncertainty qualifies it, so the answer
                * reads first. That is the order a student writes the pair in and the order the
                * grader reaches them in: formats settle the uncertainty's own figures, then the
                * place it sets for the answer, and only then are the two numbers judged — together,
                * neither reading the other's verdict.
                *
                * FIGURES RIDE ON THIS ROW rather than taking one of their own. The control is one
                * or two and can never be more, because it counts FIGURES and not places, so it
                * stays a single digit at any magnitude — `0.00012` is two figures exactly as `0.12`
                * is. A row to itself was a row for a stepper.
                *
                * WRAPPED IN THE DATA KEY. `ToleranceRow` tags `uncertainty.toleranceValue` from its own
                * prefix, while the field an edit names is `uncertaintyTolerance`; the reveal ladder
                * collects every `[data-field-id]` in the tree, so the ancestor carries the name
                * undo will look for.
                */}
              {uncertaintyAsked(fields) && (<>
                {/*
                  * ITS OWN ROW, ABOVE THE INPUT IT GOVERNS — the answer's `Significant Figures:`
                  * row, replicated.
                  *
                  * Sharing the input's row bought one line and cost two things worth more. The
                  * field fell to 130px, which is nothing for a formula carrying a recall chip or a
                  * propagation written out, and a long literal has nowhere to go: the authoring
                  * field keeps what was typed, because `countSigFigs` reads the typed string and
                  * that is the only thing separating `450` from `450.`. And a bare `1` beside a
                  * number field names itself to nobody — there was no room for the word.
                  *
                  * CAPPED AT TWO, which is the whole of the convention rather than a round number:
                  * standard guidance is to quote an uncertainty to one significant figure, or two
                  * when the leading digit makes the first too coarse. Nobody reports a third. The
                  * value's own stepper runs to 10 because a VALUE legitimately does.
                  *
                  * Button labels are shared with the answer's stepper and cannot collide: the
                  * sig-figs toggle is disabled whenever an error box is on, so the two are never
                  * on screen together.
                  */}
                <div className='flex items-center justify-between gap-2'>
                  <span className={NUMERICAL_ROW_LABEL}>{t('blocks.numerical.uncertaintySigFigsLabel')}</span>
                  <div data-field-id='uncertaintySigFigs' className='flex items-center overflow-hidden rounded-lg border border-ws-border bg-ws-btn'>
                    <button
                      type='button'
                      disabled={fields.uncertaintySigFigs <= 1}
                      onClick={() => setField('uncertaintySigFigs', Math.max(1, fields.uncertaintySigFigs - 1))}
                      aria-label={t('blocks.numerical.sigFigsDecrement')}
                      className='flex h-9 w-9 items-center justify-center text-ws-muted transition-colors hover:bg-ws-hover hover:text-ws-text disabled:cursor-not-allowed disabled:opacity-30'
                    >
                      <Minus className='h-4 w-4' />
                    </button>
                    <span aria-live='polite' aria-atomic='true' className='min-w-[2rem] select-none text-center text-sm font-semibold text-ws-text'>
                      {fields.uncertaintySigFigs}
                    </span>
                    <button
                      type='button'
                      disabled={fields.uncertaintySigFigs >= 2}
                      onClick={() => setField('uncertaintySigFigs', Math.min(2, fields.uncertaintySigFigs + 1))}
                      aria-label={t('blocks.numerical.sigFigsIncrement')}
                      className='flex h-9 w-9 items-center justify-center text-ws-muted transition-colors hover:bg-ws-hover hover:text-ws-text disabled:cursor-not-allowed disabled:opacity-30'
                    >
                      <Plus className='h-4 w-4' />
                    </button>
                  </div>
                </div>
                <div className='flex flex-col gap-1'>
                  <div className='flex items-center gap-2'>
                    <span className={NUMERICAL_ROW_LABEL}>{t('blocks.numerical.uncertaintyValueLabel')}</span>
                    {/* The tag is the DATA KEY, not a tidier short name. `changedFields` names the
                        key an edit touched and the reveal ladder looks for a control tagged with
                        it, so `uncertainty` here would have left undo flashing the whole block. */}
                    <FormulaField fieldId='uncertaintyValue' angleModeKey='uncertaintyValue'
                      hideEval
                      className='min-w-0 flex-1'
                      value={fields.uncertaintyValue}
                      onChange={(val: string) => setField('uncertaintyValue', val)}
                      variables={variables}
                      rolledValues={scenarioValues}
                      ariaLabel={t('blocks.numerical.uncertaintyValueLabel')}
                      onFocus={disconnectSharedToolbar}/>
                  </div>
                  {/*
                    * ONLY AT TWO FIGURES, and that is a fact about the lattice rather than a
                    * preference.
                    *
                    * At one significant figure the next admissible uncertainty is 20% to 100% away,
                    * so the narrowest band a tolerance could express is already wider than any
                    * disagreement between propagation methods — the rounding convention IS the
                    * band, and its edges come from the convention rather than from a teacher's
                    * guess. At two figures a step is 1% to 10%, which is the size of a real
                    * disagreement, and a teacher may want to accept both quadrature and a linear
                    * bound.
                    *
                    * Blank by default either way: an uncertainty read off an instrument (±3.5 m
                    * from a receiver) is exact and has no procedure to disagree about, and nothing
                    * in the data distinguishes that question from an estimated one. Only the
                    * teacher knows which they asked.
                    */}
                  {fields.uncertaintySigFigs >= 2 && (
                    <div data-field-id='uncertaintyTolerance'>
                      <ToleranceRow
                        idPrefix='uncertainty'
                        /* The answer's row is just above and says "Tolerance (±)" too. */
                        ownerLabel={t('blocks.numerical.uncertaintyOwnerLabel')}
                        asymmetric={uncertaintyAsym}
                        grammar='scientific'
                        percent={uncertaintyPct}
                        toleranceValue={fields.uncertaintyTolerance}
                        toleranceBelow={fields.uncertaintyToleranceBelow}
                        toleranceAbove={fields.uncertaintyToleranceAbove}
                        onChange={patch => setFieldMulti({
                          ...(patch.toleranceValue !== undefined ? { uncertaintyTolerance: patch.toleranceValue } : {}),
                          ...(patch.toleranceAbove !== undefined ? { uncertaintyToleranceAbove: patch.toleranceAbove } : {}),
                          ...(patch.toleranceBelow !== undefined ? { uncertaintyToleranceBelow: patch.toleranceBelow } : {}),
                        })}
                        invalid={{
                          value: !!numberError(fields.uncertaintyTolerance, t, { allowNegative: false }),
                          above: !!numberError(fields.uncertaintyToleranceAbove, t, { allowNegative: false }),
                          below: !!numberError(fields.uncertaintyToleranceBelow, t, { allowNegative: false }),
                        }}
                        onFocus={disconnectSharedToolbar}
                        trailing={renderToleranceTypeControls({
                          idPrefix: 'uncertainty',
                          pct: uncertaintyPct,
                          asym: uncertaintyAsym,
                          onMode: next => setField('uncertaintyToleranceMode', next),
                          /* Carries the numbers across, the way the answer's does: switching must
                             not wipe the `±` already typed and leave two empty boxes that grade
                             exact. */
                          onSymmetry: next => setFieldMulti(next === 'symmetric'
                            ? { uncertaintyToleranceSymmetry: 'symmetric',
                                uncertaintyTolerance: fields.uncertaintyToleranceAbove || fields.uncertaintyToleranceBelow }
                            : { uncertaintyToleranceSymmetry: 'asymmetric',
                                uncertaintyToleranceAbove: fields.uncertaintyTolerance,
                                uncertaintyToleranceBelow: fields.uncertaintyTolerance }),
                        })}
                      />
                    </div>
                  )}
                </div>
              </>)}

              {/* ── Between answers ── */}
              {fields.conditionType === 'between' && (
                <>
                  {fields.betweenAnswers.map((ans, idx) => {
                    const errs    = betweenErrors[idx];
                    const isArmed = armedBetweenId === ans.id;
                    return (
                      <BetweenAnswerRow
                        key={ans.id}
                        fieldId={ans.id}
                        armed={isArmed}
                        grouped={fields.betweenAnswers.length > 1}
                        to={t('blocks.connectors.to')}
                        low={
                          <FormulaField fieldId={`${ans.id}.low`} angleModeKey={ans.id}
                            hideEval
                            value={ans.low}
                            onChange={val => updateBetweenAnswer(ans.id, { low: val })}
                            variables={variables}
                            rolledValues={scenarioValues}
                            ariaLabel={t('blocks.numerical.betweenMin')}
                            placeholder={t('blocks.numerical.betweenMin')}
                            hasError={!!errs?.low}
                            onFocus={disconnectSharedToolbar}
                          />
                        }
                        high={
                          <FormulaField fieldId={`${ans.id}.high`} angleModeKey={ans.id}
                            hideEval
                            value={ans.high}
                            onChange={val => updateBetweenAnswer(ans.id, { high: val })}
                            variables={variables}
                            rolledValues={scenarioValues}
                            ariaLabel={t('blocks.numerical.betweenMax')}
                            placeholder={t('blocks.numerical.betweenMax')}
                            hasError={!!errs?.high}
                            onFocus={disconnectSharedToolbar}
                          />
                        }
                        action={fields.betweenAnswers.length > 1 && (
                          <IconButton
                            type='button'
                            variant='destructive'
                            size='md'
                            isArmed={isArmed}
                            aria-label={isArmed ? t('blocks.answers.confirmRemoveRange') : t('blocks.answers.removeRange')}
                            className='shrink-0'
                            onClick={() => {
                              if (isArmed) { removeBetweenAnswer(ans.id); setArmedBetweenId(null); }
                              else { setArmedAnswerId(null); setArmedBetweenId(ans.id); }
                            }}
                            onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedBetweenId(null); }}
                            onBlur={() => { if (isArmed) setArmedBetweenId(null); }}
                          >
                            <Trash2 className='h-4 w-4' />
                          </IconButton>
                        )}
                      />
                    );
                  })}

                  {fields.betweenAnswers.length < 5 && (
                    <AddRowButton
                      ref={addBetweenBtnRef}
                      label={t('blocks.numerical.addRange')}
                      onClick={addBetweenAnswer}
                    />
                  )}
                </>
              )}

              {/* ── One-sided threshold ── */}
              {ONE_SIDED.includes(fields.conditionType) && (
                <div className='flex flex-col gap-1'>
                  <FormulaField
                              hideEval
                      fieldId='threshold'
                      value={fields.threshold}
                      onChange={val => setField('threshold', val)}
                      variables={variables}
                      rolledValues={scenarioValues}
                      ariaLabel={t('blocks.numerical.thresholdLabel')}
                      placeholder={t('blocks.numerical.thresholdPlaceholder')}
                      hasError={!!thresholdError}
                      onFocus={disconnectSharedToolbar}
                    />
                </div>
              )}

              {/* Sig figs count stepper — visible in the main area when enabled.
                  Left-aligned and in Tolerance's label treatment, so the three labelled rows in this
                  column — Tolerance:, Significant Figures:, Unit: — start at the same x and read as one
                  set rather than three unrelated controls. The trailing "significant figures" that used
                  to close the row is gone: with the label naming them, it read "Significant Figures:
                  [3] significant figures". */}

            </>)}

            {/* Unit library (when unitMode === 'withUnits') — above the preview.
                `pt-3` sets it apart from the answer/tolerance/sig-figs rows above: with sig figs and
                units both on, this column runs long enough that one flat gap made four unrelated
                clusters read as a single list. */}
            {/*
              * Graded only. `unitOptions` is read in exactly one place — the
              * `requiresUnit && !isCanonicalUnit` branch of `evaluateNumerical`, which sits below
              * optional's short-circuit — and all it decides there is `role !== 'correct'`. So
              * outside graded a derivative unit library has no effect whatever: every unit the
              * student can type is accepted either way, and marking one wrong changes nothing.
              * The canonical unit is the same story; its other reader is the teacher's own
              * collapsed answer bar.
              *
              * What stays is the unit MODE and its display label, because those decide what the
              * student is ASKED for — a caption beside the box, or a unit field to fill in.
              */}
            {fields.gradingMode !== 'optional' && fields.unitMode === 'withUnits' && (
              <div className='flex flex-col gap-2 pt-3'>
                {/* Canonical unit input. Centred like the temperature-conversion row directly below
                    it: the column beside this label holds one field and nothing under it, so the
                    `pt-2.5` that used to fake a top alignment had nothing to align to. */}
                <div className='flex items-center gap-2'>
                  <span className='shrink-0 text-sm font-medium text-ws-text'>{t('blocks.numerical.unitCanonicalLabel')}</span>
                  <div className='flex flex-1 flex-col gap-0.5'>
                    <UnitField fieldId='canonicalUnit'
                      ariaLabel={t('blocks.numerical.unitCanonicalLabel')}
                      // A canonical unit is PARSED by mathjs and must stay plain ASCII — the
                      // opposite of the unit label above, which is only ever drawn. Releasing the
                      // toolbar here is what stops its variable-insert button pointing at a field
                      // that cannot survive a chip.
                      onFocus={disconnectSharedToolbar}
                      hasError={!!fields.canonicalUnit && !isValidUnit(fields.canonicalUnit)}
                      value={fields.canonicalUnit}
                      onChange={newCanonical => {
                        const updatedOptions = fields.unitOptions.map(opt => {
                          if (!opt.label) return opt;
                          const conv = getConversionFactor(newCanonical, opt.label, fields.tempConvMode);
                          return conv !== null
                            ? { ...opt, conversionFactor: conv.factor, conversionOffset: conv.offset, role: 'neutral' as const }
                            : { ...opt, conversionFactor: 1, conversionOffset: 0, role: 'wrong' as const };
                        });
                        // Auto entries are rebuilt from the NEW canonical, so the old family goes
                        // and the new one arrives in the same edit.
                        setFieldMulti({
                          canonicalUnit: newCanonical,
                          ...unitIdentityFor(newCanonical),
                          unitOptions: withUnitIdentity(updatedOptions),
                        });
                      }} />
                  </div>
                </div>

                {/* The canonical unit's other spellings, as ONE line rather than a row each.
                    `L` alone has six — l, lt, liter, litre, liters, litres — and six locked rows the
                    teacher can neither edit nor delete would dominate a column they cannot act on.
                    The information is "these also count", which is a sentence, not a list of
                    controls.

                    Directly under the canonical field, ahead of "Interpret as": it is a statement
                    ABOUT that unit, so it belongs beside it rather than after a separate control
                    that only some units even show. */}
                {canonicalIsUnderstood && unbrokenSpellings.length > 0 && (
                  <p className='text-xs italic text-ws-muted'>
                    {t('blocks.numerical.unitAlsoAccepted')}{' '}
                    {unbrokenSpellings.map((spelling, i) => (
                      <span key={spelling}>
                        {i > 0 && <span aria-hidden='true'>, </span>}
                        <button
                          type='button'
                          onClick={() => breakOutSpelling(spelling)}
                          title={t('blocks.numerical.unitSpellingBreakOut', { unit: spelling })}
                          aria-label={t('blocks.numerical.unitSpellingBreakOut', { unit: spelling })}
                          className='rounded font-sans not-italic text-ws-text-link underline decoration-dotted underline-offset-2 transition-colors hover:text-ws-text focus-visible:outline focus-visible:outline-1 focus-visible:outline-ws-text-link'
                        >
                          {spelling}
                        </button>
                      </span>
                    ))}
                  </p>
                )}

                {/* Temperature conversion mode toggle — only for offset temperature scales */}
                {fields.canonicalUnit && isValidUnit(fields.canonicalUnit) && isOffsetTempUnit(fields.canonicalUnit) && (
                  <div className='flex items-center gap-2'>
                    <span className='shrink-0 text-sm font-medium text-ws-text'>{t('blocks.numerical.tempConvModeLabel')}</span>
                    <SegmentedControl fieldId='tempConvMode'
                      aria-label={t('blocks.numerical.tempConvModeLabel')}
                      options={[
                        { id: 'absolute', label: t('blocks.numerical.tempConvMode_absolute') },
                        { id: 'interval', label: t('blocks.numerical.tempConvMode_interval') },
                      ]}
                      value={fields.tempConvMode}
                      onChange={mode => {
                        const m = mode as 'absolute' | 'interval';
                        const updatedOptions = fields.unitOptions.map(opt => {
                          if (!opt.label) return opt;
                          const conv = getConversionFactor(fields.canonicalUnit, opt.label, m);
                          /*
                           * THE ROLE SURVIVES, unlike a label change.
                           *
                           * `updateUnitOption` resets a role to neutral when the LABEL changes,
                           * and rightly: a retyped unit may not deserve the old decision. This is
                           * not that. Flipping scale/delta changes the ARITHMETIC, not which unit
                           * names a teacher is willing to accept — K is a valid unit for both a
                           * temperature and a temperature change — so resetting here threw away
                           * the correct/wrong setup of every option for a change that does not
                           * invalidate any of it, with no warning and nothing to undo it by.
                           */
                          return conv !== null
                            ? { ...opt, conversionFactor: conv.factor, conversionOffset: conv.offset }
                            // Not convertible under the new mode: it cannot be accepted, whatever
                            // it was before. That one IS a role change, and the only one here.
                            : { ...opt, conversionFactor: 1, conversionOffset: 0, role: 'wrong' as const };
                        });
                        // Same resync as the canonical field: the temperature mode feeds the
                        // equivalence test, so the auto entries are rebuilt under the new mode.
                        setFieldMulti({
                          tempConvMode: m,
                          unitOptions: withUnitIdentity(updatedOptions),
                        });
                      }}
                    />
                  </div>
                )}

                {/* Derivative unit options */}
                {fields.canonicalUnit && isValidUnit(fields.canonicalUnit) && (
                  <>
                    {fields.unitOptions.length > 0 && (
                      <p className='text-xs text-ws-muted'>{t('blocks.numerical.unitOptionsLabel')}</p>
                    )}
                    {fields.unitOptions.map((opt, idx) => {
                      // See the role indicator below: only the VERDICT half of this field is
                      // mode-dependent, never the acceptance half.
                      const unitRolesGraded = fields.gradingMode === 'graded';
                      const isArmed = armedUnitOptId === opt.id;
                      const isValidOpt = opt.label ? isValidUnit(opt.label) : null;
                      // Every member of the group is marked, not just the later ones. The pair is
                      // the problem, and blaming the second implies the first is fine — it is not,
                      // because which of them decides a grade is currently just list order.
                      const isDuplicate = !!opt.label.trim() && duplicateUnitLabels.has(opt.label.trim());
                      const convResult = (isValidOpt && opt.label)
                        ? getConversionFactor(fields.canonicalUnit, opt.label, fields.tempConvMode)
                        : null;
                      const isCompatible = isValidOpt === true && convResult !== null;

                      // What to show to the right of the input
                      const sideDisplay = (() => {
                        if (!opt.label) return null;
                        if (isDuplicate) return <span className='whitespace-nowrap text-xs text-ws-error-fg'>{t('blocks.numerical.unitDuplicate')}</span>;
                        if (!isValidOpt) return <span className='whitespace-nowrap text-xs text-ws-error-fg'>{t('blocks.numerical.unitCanonicalInvalid')}</span>;
                        if (!isCompatible) return <span className='whitespace-nowrap text-xs text-ws-muted'>{t('blocks.numerical.unitNotConvertible')}</span>;
                        const isFormula = fields.conditionType === 'exact' && hasVariableChip(fields.answers[0]?.value ?? '');
                        if (isFormula) return <span className='whitespace-nowrap text-xs text-ws-muted'>{t('blocks.numerical.unitConvertible')}</span>;
                        const canonVal = fields.conditionType === 'exact' ? parseFloat(fields.answers[0]?.value ?? '') : NaN;
                        if (Number.isFinite(canonVal) && convResult) {
                          const converted = canonVal * convResult.factor + convResult.offset;
                          return <span className='whitespace-nowrap text-xs text-ws-muted'>{fmtLocale(converted, i18n.language, fields.sigFigsRequired ? fields.sigFigsCount : undefined)} {opt.label}</span>;
                        }
                        return <span className='whitespace-nowrap text-xs text-ws-muted'>{t('blocks.numerical.unitConvertible')}</span>;
                      })();

                      return (
                        // `data-field-id` so `unit-option-duplicate` / `-unrecognized` can reach
                        // this row. Both issues have always named these ids, and nothing registered
                        // them — so `IssueRow` drew a button (it draws one whenever `fields` is
                        // non-empty) and the click did nothing, which is the dead control its own
                        // doc says to avoid. `warnUnreachable` was logging it in dev the whole time.
                        <div key={opt.id} data-field-id={opt.id} className={`flex items-center gap-2 ${isArmed ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''}`}>
                          {/* Role indicator — before the input */}
                          <div className='flex shrink-0 items-center justify-center'>
                            {isCompatible && !isDuplicate ? (
                              /*
                               * THREE STATES IN HAS CORRECT, TWO EVERYWHERE ELSE.
                               *
                               * This role is a composite, which is why it is the one role field in
                               * the app that does NOT go through `rolesForMode`: `correct` means
                               * "the question accepts this unit" — a fact about the ask, true in
                               * every mode, and what `resolveSubmittedUnit` converts by — while
                               * `wrong` vs `neutral` differ only in the VERDICT, which
                               * Participation has not got. Neutralising the whole field would
                               * un-accept every alternative, so `40000 cm` against a 400 m answer
                               * would come back a Unit Error.
                               *
                               * So only the verdict half collapses: outside graded the two
                               * not-accepted roles draw as one mark and the cycle is a toggle.
                               *
                               * The accepted mark is HOLLOW outside graded — same hue, no fill —
                               * so it still reads as "yes, this counts" without wearing the solid
                               * grading tick. Deliberately not greyed: `opacity-40` is this app's
                               * disabled treatment in six shared controls, and a greyed control
                               * that still works is the inverse of the bug `CheckboxRow` fixes.
                               */
                              <button
                                type='button'
                                onClick={() => {
                                  if (unitRolesGraded) {
                                    const order: UnitOption['role'][] = ['neutral', 'correct', 'wrong'];
                                    updateUnitOption(opt.id, { role: order[(order.indexOf(opt.role) + 1) % order.length]! });
                                    return;
                                  }
                                  /* Toggling OFF lands on `neutral`, not back on a stored `wrong`:
                                     a teacher doing this here is saying "not accepted" in a mode
                                     with no verdict to keep. An untouched `wrong` is preserved. */
                                  updateUnitOption(opt.id, { role: opt.role === 'correct' ? 'neutral' : 'correct' });
                                }}
                                title={t(
                                  !unitRolesGraded
                                    ? (opt.role === 'correct' ? 'blocks.numerical.unitRoleAcceptedTip'
                                                              : 'blocks.numerical.unitRoleNotAcceptedTip')
                                    : opt.role === 'correct' ? 'blocks.numerical.unitRoleCorrectTip'
                                    : opt.role === 'wrong'   ? 'blocks.numerical.unitRoleWrongTip'
                                    :                          'blocks.numerical.unitRoleNeutralTip'
                                )}
                                className='group/role flex items-center justify-center'
                              >
                                <span className={`pointer-events-none flex h-5 w-5 items-center justify-center rounded-full transition-colors ${
                                  opt.role === 'correct'
                                    ? (unitRolesGraded ? 'border-2 border-ws-primary bg-ws-primary'
                                                       : 'border-2 border-ws-primary dark:border-ws-text-link')
                                    : opt.role === 'wrong' && unitRolesGraded ? 'bg-ws-muted/20'
                                    : 'border-2 border-ws-border group-hover/role:border-ws-muted'
                                }`}>
                                  {opt.role === 'correct' && <Check strokeWidth={3}
                                    className={`h-3.5 w-3.5 ${unitRolesGraded ? 'text-ws-btn-on-primary' : 'text-ws-primary dark:text-ws-text-link'}`} />}
                                  {opt.role === 'wrong' && unitRolesGraded && <X className='h-3.5 w-3.5 text-ws-muted' strokeWidth={3} />}
                                  {(opt.role === 'neutral' || (opt.role === 'wrong' && !unitRolesGraded))
                                    && <Minus className='h-3 w-3 text-ws-muted' strokeWidth={2} />}
                                </span>
                              </button>
                            ) : isValidOpt === true && !isCompatible && !isDuplicate ? (
                              /* Incompatible dimension: a settled role, so it still shows one —
                                 locked wrong where there is a verdict, locked not-accepted where
                                 there is not. It was a ✗ in every mode, which claimed a mark
                                 Participation does not make. */
                              unitRolesGraded ? (
                                <span className='flex h-5 w-5 items-center justify-center rounded-full bg-ws-muted/20'>
                                  <X className='h-3.5 w-3.5 text-ws-muted' strokeWidth={3} />
                                </span>
                              ) : (
                                <span className='flex h-5 w-5 items-center justify-center rounded-full border-2 border-ws-border'>
                                  <Minus className='h-3 w-3 text-ws-muted' strokeWidth={2} />
                                </span>
                              )
                            ) : (
                              /* Unrecognized, or a duplicate — no indicator at all. An incompatible
                                 unit has a settled role (it is wrong), so it shows one; these two
                                 have no role to report until the label itself is dealt with. */
                              <span className='h-5 w-5' />
                            )}
                          </div>

                          {/* Fixed-width input */}
                          {/* w-40, up from w-28: the symbol picker now sits inside this field, so
                              the same width left the input itself about half of what it was. The
                              conversion text beside it still has room — it is short ("Convertible",
                              "= 0.001 kg") and the row has the trash button pinned to its end. */}
                          <div className='flex w-40 shrink-0 flex-col gap-0.5'>
                            <UnitField fieldId={`${opt.id}.label`}
                              ariaLabel={t('blocks.numerical.unitOptionAriaLabel', { number: idx + 1 })}
                              onFocus={disconnectSharedToolbar}
                              hasError={unitFieldsWithIssues.has(opt.id)}
                              value={opt.label}
                              onChange={v => updateUnitOption(opt.id, { label: v })}
                              inputRef={el => {
                                if (el && pendingFocusUnitOptId.current === opt.id) {
                                  el.focus();
                                  pendingFocusUnitOptId.current = null;
                                }
                              }} />
                          </div>

                          {/* Conversion result or status text */}
                          <div className='min-w-0 flex-1'>{sideDisplay}</div>

                          <IconButton
                            type='button'
                            variant='destructive'
                            size='md'
                            isArmed={isArmed}
                            aria-label={isArmed ? t('blocks.numerical.unitOptionRemoveConfirm') : t('blocks.numerical.unitOptionRemove')}
                            className='shrink-0'
                            onClick={() => {
                              if (isArmed) {
                                removeUnitOption(opt.id);
                                setArmedUnitOptId(null);
                                requestAnimationFrame(() => addUnitOptBtnRef.current?.focus());
                              } else { setArmedUnitOptId(opt.id); }
                            }}
                            onPointerLeave={e => { if (e.pointerType !== 'touch' && isArmed) setArmedUnitOptId(null); }}
                            onBlur={() => { if (isArmed) setArmedUnitOptId(null); }}
                          >
                            <Trash2 className='h-4 w-4' />
                          </IconButton>
                        </div>
                      );
                    })}
                    <AddRowButton
                      ref={addUnitOptBtnRef}
                      label={t('blocks.numerical.unitOptionAdd')}
                      onClick={addUnitOption}
                    />
                  </>
                )}
              </div>
            )}

            {/* Unit label input (when unitMode === 'display') — below the preview.

                Centred, not top-aligned: the column beside this holds ONE editor and nothing under
                it, so there is no second line for the label to align to the top of. The `pt-2` that
                used to fake the alignment went with it.

                `pt-3` for the same reason the unit library above carries it — this is the last row
                in a column whose length depends on which options are on, so it sets itself apart
                from whatever happens to precede it: the sig-figs stepper, the Add Answer button, or
                a threshold field. */}
            {fields.unitMode === 'display' && (
              <div className='flex items-center gap-2 pt-3'>
                <span className='shrink-0 whitespace-nowrap text-sm font-medium text-ws-text'>
                  {t('blocks.numerical.unitInlineLabel')}
                </span>
                {/* Rich, unlike `canonicalUnit` right below it, and the split is not stylistic:
                    a canonical unit is parsed by mathjs and must stay plain ASCII, while this one
                    is only ever DRAWN. That is what lets it carry m³ as a real superscript, or a
                    bolded qualifier, without anything downstream having to understand it. */}
                <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                  {/* `hideToolbar` + the focus hand-off: this drives the ONE pinned toolbar at the
                      top of the panel, the same way the feedback editors do. A second button bar
                      inside a field this small would be larger than the field. */}
                  <RichTextEditor fieldId='unitLabel'
                    content={fields.unitLabel}
                    onChange={(html: string) => setField('unitLabel', html)}
                    editorClassName='min-h-[2.5rem]'
                    hideToolbar
                    onEditorReady={(e) => { unitLabelEditorRef.current = e; }}
                    onEditorFocus={() => {
                      if (unitLabelEditorRef.current) {
                        activeEditorRef.current = unitLabelEditorRef.current;
                        setActiveEditor(unitLabelEditorRef.current);
                      }
                    }} />
                </div>
              </div>
            )}
          </div>
            </ScenarioSetupFrame>
          </DrivenRowFrame>
        </div>
      }
    />
  );
}
